import { ReactNode, useState, useEffect, useRef } from "react";
import styles from "@/pages/index.module.css";
import { Step } from "@/typings/step";
import { Guess } from "@/typings/guess";
import { isSimilar } from "@/utils/scoring";

type PigeonAnimationProps = {
  isFadingOut?: boolean;
  isFadingIn?: boolean;
};

type PigeonAnimationContainerProps = PigeonAnimationProps & {
  children: ReactNode;
};

function PigeonAnimationContainer({
  isFadingOut,
  isFadingIn,
  children,
}: PigeonAnimationContainerProps) {
  return (
    <svg
      className={`${styles.pigeon} ${isFadingOut ? styles.pigeonFadeOut : ""} ${isFadingIn ? styles.pigeonFadeIn : ""}`}
      xmlns="http://www.w3.org/2000/svg"
      shape-rendering="geometricPrecision"
      text-rendering="geometricPrecision"
      viewBox="0 0 500 500"
    >
      {children}
    </svg>
  );
}

function PigeonBlinkAnimation() {
  return (
    <animate
      attributeName="opacity"
      dur="4s"
      repeatCount="indefinite"
      values="1; 0; 1"
      keyTimes="0; 0.97; 1"
      calcMode="discrete"
    />
  );
}

function PigeonBlinkFastAnimation() {
  return (
    <animate
      attributeName="opacity"
      dur="3s"
      repeatCount="indefinite"
      values="1; 0; 1; 0; 1;"
      keyTimes="0; 0.9; 0.93; 0.97; 1"
      calcMode="discrete"
    />
  );
}

function PigeonBlinkFasterAnimation() {
  return (
    <animate
      attributeName="opacity"
      dur="2s"
      repeatCount="indefinite"
      values="1; 0; 1; 0; 1;"
      keyTimes="0; 0.85; 0.90; 0.95; 1"
      calcMode="discrete"
    />
  );
}

function PigeonCloseEyesAnimation() {
  return (
    <animate
      attributeName="opacity"
      dur="4s"
      repeatCount="indefinite"
      values="0; 1; 0;"
      keyTimes="0; 0.95; 1"
      calcMode="discrete"
    />
  );
}

function PigeonHeartAnimation() {
  return (
    <>
      <animate
        attributeName="opacity"
        dur="4s"
        repeatCount="indefinite"
        values="1; 1; 0; 0;"
        keyTimes="0; 0.1; 0.2; 1"
        calcMode="linear"
      />
      <animateMotion
        dur="4s"
        repeatCount="indefinite"
        path="M0,0q41.017792-21.170474,42.340946-70.127193"
      />
    </>
  );
}

function PigeonBreathAnimation() {
  return (
    <>
      <animate
        attributeName="opacity"
        dur="4s"
        repeatCount="indefinite"
        values="1; 1; 0; 0;"
        keyTimes="0; 0.025; 0.05; 1"
        calcMode="linear"
      />
      <animateMotion dur="4s" repeatCount="indefinite" path="M15,0l-374.928498,7.848954" />
    </>
  );
}

function PigeonSweatAnimation() {
  return (
    <>
      <animate
        attributeName="opacity"
        dur="8s"
        repeatCount="indefinite"
        values="1; 1; 0; 0;"
        keyTimes="0; 0.01; 0.1; 1"
        calcMode="linear"
      />
      <animateMotion dur="8s" repeatCount="indefinite" path="M0,0l0,250" />
    </>
  );
}

function PigeonStart({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <ellipse
        opacity=".1"
        rx="166.187"
        ry="25.726"
        transform="matrix(1 0 0 .91774 261.418 476.088)"
      />
      <path
        fill="#d5d5d5"
        d="m228.536 156.704 1.883-86.608-12.238-33.89-24.476-21.652-28.242-8.473-36.714 6.59L105.214 31.5 93.917 68.213l-2.824 70.605 2.138 38.327 3.942 29.959-17.345 23.652-12.614 45.728 2.365 38.631 8.672 15.768 6.693 5.673 11.986 43.17 29.705 34.761 53.179 24.304 76.017 11.877 72.058-1.583 50.974-17.934 22.621-10.497 37.521 24.533 24.052 6.734 16.836-17.798 3.367-18.28-6.694-24.02-22.649-33.703-11.653-18.865-8.178-29.085-16.983-31.354-29.797-37.022-35.98-35.297-38.62-25.994-35.847-16.24-33.26-8.5-15.063.941Z"
      />
      <path
        fill="#ddd"
        d="m332.549 364.847 59.508 2.975 22.977-6.735 14.103-10.392 12.865-21.359-25.386-50.726-29.045-36.283-39.34-40.824-39.819-23.75q-54.862-26.021-78.94-23.012c-19.982 2.498-54.34 17.498-69.03 30.433-13.512 11.897-35.184 34.298-40.558 51.48-5.587 17.864-5.433 39.407-1.349 60.703 4.638 24.183 12.513 38.95 33.724 51.26 20.822 12.085 48.03 18.198 67.967 12.51 9.57-2.73 24.547-22.316 24.547-22.316l38.68 16.365 49.096 9.67Z"
      />
      <path
        fill="#b7b0ba"
        d="M204.198 201.258q9.355-11.412 26.079-44.133l1.633-15.027-64.979 19.124-71.702 44.822-8.715 37.837s15.468 23.414 25.998 23.591c10.245.173 34.083-14.649 49.058-25.592 13.039-9.528 31.025-26.466 42.628-40.622Z"
      />
      <path
        fill="#a195a6"
        d="M205.703 188.72c10.94-13.08 24.072-40.622 24.072-40.622l.769-45.424-2.409-46.365-14.094-26.733-27.34-18.726-28.088-3.745-23.595 4.12-19.475 10.86-16.479 22.846-5.808 27.636-1.205 42.15-.602 46.364 5.576 44.845-5.728 19.477q13.753 25.505 23.13 25.505c12.03 0 32.599-11.535 47.644-23.07 12.396-9.503 33.529-27.04 43.632-39.118Z"
      />
      <path
        fill="#998d9c"
        d="M154.047 220.817c14.6-9.162 44.122-33.408 55.166-46.64 12.347-14.794 21.565-38.116 21.565-38.116l.747-44.387-4.276-42.022-16.953-24.931-23.436-13.463-27.923-4.488-24.433 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.205 30.107 1.841 31.583q9.815 21.11 18.45 23.59c12.305 3.534 29.276-7.739 40.12-14.544Z"
      />
      <path
        fill="#959195"
        d="M162.771 203.264c12.732-9.111 31.365-21.723 42.629-32.598 14.543-14.043 26.078-41.626 26.078-41.626l-.005-37.366-4.275-42.022-16.954-24.931-23.435-13.463-27.924-4.488-24.432 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.204 30.107.565 19.244q16.667 20.473 26.997 21.886c13.486 1.846 30.557-10.133 41.626-18.054Z"
      />
      <path
        fill="#4a4a4a"
        d="M159.57 90.281s-7.692-6.545-16.729-7.586c-10.139-1.169-17.439 5.122-19.641 7.01-5.012 4.296-.325 15.947-2.39 22.217-.627 1.903 2.49 12.405 4.376 13.079 6.415 2.291 20.172-10.495 20.172-10.495l19.056-13.889"
      />
      <path
        fill="#f1f1f1"
        d="M160.57 80.237c.687-9.157-14.125-5.25-25.312-5.796-2.938-.144-6.766 2.043-7.826 5.224-1.124 3.373.598 6.196 1.972 6.064 1.979-.189 5.104-3.612 12.43-3.319 6.658.267 18.735 6.871 18.735 6.871v-9.044Z"
      />
      <path
        fill="#555"
        d="M139.403 91.07c-5.17 2.003-7.59 8.764-6.274 17.97.563 3.94 7.582-4.907 10.96-7.014 4.023-2.51 10.045-2.627 9.25-5.805-.606-2.424-9.992-6.678-13.936-5.15Z"
      />
      <path
        d="m75.853 246.057 10.785-25.376 11.42-13.957c-.292-1.106 11.323 11.045 11.323 11.045s-4.854 33.914-5.396 48.562c-.55 14.842-2.224 35.602 1.812 49.472 4.206 14.457 14.93 40.04 26.165 49.604 5.93 5.049 33.68 26.322 50.52 32.093 23.007 7.885 58.142 15.09 81.743 16.057 28.823 1.18 66.964.988 93.765-6.649 27.225-7.757 68.767-33.32 82.558-48.173 7.584-8.168-3.9 26.497-10.117 35.747-9.15 13.611-43.088 33.647-43.088 33.647l-54.559 17.763-62.17 3.807-86.853-9.219-45.167-20.279-32.262-27.653-15.887-30.396-5.076-25.376-14.59-12.688-2.538-39.333 7.612-38.698Z"
        opacity=".06"
      />
      <path
        d="M89.196 229.244s-1.215 41.085.665 58.55c2.224 20.667 11.515 49.047 20.74 67.675 7.282 14.707 22.226 28.113 35.63 37.583 13.319 9.41 36.024 17.593 51.736 21.964 22.036 6.128 45.955 13.138 68.82 13.666 24.925.576 55.694.529 80.018-4.938 17.86-4.014 44.111-13.714 59.56-23.834 12.271-8.04 32.8-36.267 32.8-36.267l-7.984 29.275-23.952 25.283-61.211 24.618-75.85 6.653-77.955-6.698-41.45-16.22-30.296-17.668-26.771-36.395-8.492-36.127s-6.244-2.21-7.806-6.319c-10.555-27.76 11.798-100.8 11.798-100.8ZM125.45 83.294c-7.062-.024-13.008 11.799-13.986 17.768-1.536 9.375 4.717 31.532 14.092 33.063 3.073.502 11.25-5.281 14.566-9.312 3.962-4.814 4.013-10.365 4.013-10.365l-17.052 9.362-7.356-17.386 3.108-13.517 5.876-5.372s-1.123-4.234-3.261-4.241Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532"
            opacity=".58"
            transform="matrix(2.29396 .017 -.0079 2.23453 -376.176 -51.707)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.29205 .10072 -.09384 2.23232 -291.061 -101.131)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.27586 .2895 -.2877 2.21576 -372.39 -158.789)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.264 1.261 5.416 1.646M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c1.55 1.092 3.76 1.488 3.76 1.488"
            opacity=".58"
            transform="matrix(2.24514 -.4495 .4708 2.18891 -348.366 79.222)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M316.934 195.941c31.418 19.295 87.057 60.727 108.043 123.684"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M185.604 23.27c27.554 11.513 34.565 45.997 34.565 68.657"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M192.133 41.23c12.5 12.627 18.05 28.421 19.464 43.215"
        />
      </g>
      <path
        fill="#ccc"
        d="m439.135 332.948-.323 39.152-11.803 27.4-13.646 13.169 9.857 7.393 26.49 12.32 24.026 3.081 2.593-11.936-4.636-25.713-16.019-31.616-16.54-33.25Z"
      />
      <path
        fill="#9d9d9d"
        d="M430.047 394.656q-8.36 9.162-28.082 26.48l30.306 20.203 30.305 12.025 15.47-19.4 5.294-12.943-5.653-27.157-15.16-24.582-16.336-20.81q-8.12 33.646-16.144 46.184Z"
      />
      <path
        fill="#ccc"
        d="M354.972 369.345s21.155-3.09 23.485-8.163c2.063-4.488 1.31-11.836-.519-16.425-3.016-7.57-12.514-14.834-17.596-21.204-4.893-6.133-14.95-14.034-14.95-14.034l13.088 1.036s-1.009-10.831-2.39-14.431c-1.567-4.082-10.046-12.026-15.043-16.933-6.423-6.307-24.269-18.18-24.269-18.18l16.728 1.852s-.893-4.77-2.977-10.42c-1.855-5.029-7.34-11.319-11.117-15.122-5.194-5.232-18.633-15.334-18.633-15.334l27.837 6.74 16.427 14.762L351 262.05l-3.037 7.18 10.375 10.895 11.881 13.016 3.358 13.169-4.017 16.159 21.432 13.767 15.423 16.864 2.767 13.579-18.035 2-36.176.667Z"
      />
      <path
        fill="#9d9d9d"
        d="M380.031 370.656s8.515-6.118 9.912-10.135c1.434-4.124.143-10.339-1.522-14.473-2.786-6.915-7.569-12.327-12.989-17.653-5.613-5.516-20.258-17.54-20.258-17.54l10.082 2.31s2.863-7.994 2.3-11.65c-.97-6.305-7.115-13.794-10.698-17.77-8.006-8.881-26.47-20.854-26.47-20.854l9.891 2.51s.222-4.978-.807-10.452c-.948-5.048-5.03-10.757-8.555-14.493-4.63-4.908-19.157-16.382-19.157-16.382l19.272 4.948 17.09 13.27 4.778 15.567-3.513 8.007 21.81 13.322 11.274 10.122 10.253 23.012-6.866 8.79 8.598 8.102 17.327 13.463 6.206 15.52-15.373 7.864-22.585 4.595Z"
      />
      <path
        fill="#ccc"
        d="M340.697 366.875s6.097-5.636 7.941-8.46c2.893-4.431 6.098-12.258 5.375-17.5-.915-6.636-6.78-15.074-11.127-20.17-5.756-6.75-19.711-17.62-19.711-17.62s2.801-7.793 3.298-11.049c.81-5.302-1.505-11.904-4.619-16.271-4.52-6.343-20.579-16.223-20.579-16.223s6.472-6.464 7.442-9.813c1.366-4.712-1.632-9.115-4.518-13.082-3.813-5.244-24.238-16.564-24.238-16.564l-20.416 3.382s11.397 8.187 15.41 11.37c3.555 2.821 10.157 6.997 10.398 11.529.279 5.272-7.827 12.105-7.827 12.105s12.384 11.814 17.405 16.779c2.951 2.918 7.406 9.819 7.724 13.957.382 4.978-6.874 12.923-6.874 12.923s12.815 13.797 17.961 19.954c4.043 4.837 9.433 9.813 11.425 15.145 1.292 3.46 1.81 8.97.844 12.286-1.081 3.715-13.235 14.1-13.235 14.1l27.92 3.222Z"
      />
      <path
        fill="#9d9d9d"
        d="M332.976 364.97s15.434-13.109 16.182-19.869c.798-7.206-4.38-15.855-9.5-20.988-8.618-8.638-22.507-20.716-22.507-20.716s4.612-11.098 4.899-15.375c.289-4.314-3.963-9.452-6.82-12.7-5.419-6.161-21.144-17.045-21.144-17.045s10.397-5.265 10.94-10.197c.618-5.606-6.222-11.952-10.562-15.554-5.38-4.464-22.398-12.555-22.398-12.555l-9.354 1.971s13.74 7.367 18.347 11.148c3.773 3.097 10.179 8.777 9.984 13.654-.22 5.497-8.519 11-8.519 11s14.402 11.488 20.542 18.804c2.168 2.584 4.352 7.34 3.97 10.691-.464 4.091-5.791 13.179-5.791 13.179s11.873 12.507 16.835 18.92c5.163 6.67 14.536 15.368 14.985 23.791.3 5.604-12.577 18.766-12.577 18.766"
      />
      <path
        d="m402.446 420.173 32.164-31.06 11.262-40.54 26.632 39.454 10.542 17.99-.294 11.18-7.188 22.698s-2.392-18.306-13.508-31.49c-3.888-4.61-17.397-12.615-17.397-12.615s-16.843 27.23-32.11 33.523"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="m442.284 342.1-.86 23.312s15.362 15.8 24.9 26.701c12.6 14.4 15.217 27.072 15.217 27.072l-.829-12.852-3.317-12.853-18.156-30.89-16.955-20.49Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m167.256 481.814 62.303-15.447M229.84 466.423l35.874-4.837M162.107 469.971l64.912-5.893"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M442.227 343.264s23.78 32.684 33.925 48.973c9.617 15.443 6.401 29.708 4.617 37.943 0 0-10.349 21.289-18.75 21.25-10.625.625-23.75-6.25-35-12.5-8.176-4.543-25.625-18.125-25.625-18.125"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M96.04 208.275s-11.746 16.64-13.204 19.779c-20.429 44-15.585 78.148-13.287 91.168 1.571 8.905 9.002 13.622 15.38 18.3"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m230.115 450.92-.083 8.123M302.496 475.363l-65.213 8.51M303.2 476.515l39.099-6.45M304.09 476.572l-58.932 18.484"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.839 467.857.641 19.458"
          transform="matrix(.90062 0 0 .93083 89.927 16.416)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="4"
          d="M158.986 88.338c5.244-.611 3.861-6.397 1.912-9.077s-11.591-6.572-16.568-6.82c-4.798-.418-8.605.906-10.604 1.514-2.904.884-7.286 3.931-7.953 5.934-.683 2.047-.096 4.241 1.558 4.918"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M229.653 153.407s17.933 3.587 28.345 6.19c13.717 3.43 39.622 12.655 53.767 20.906 17.216 10.043 35.439 21.452 51.31 36.746 16.388 15.79 35.843 34.968 48.6 54.668 12.288 18.973 22.853 35.281 30.437 70"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M439.577 359.2s1.98 17.64-10.444 34.678c-10.328 14.164-19.851 22.272-34.299 29.682-22.72 11.653-46.83 19.788-78.491 24.405-21.687 3.163-64.922 1.637-86.675-1.035-16.304-2.003-38.284-4.943-53.736-10.515-17.725-6.391-38.816-14.334-52.41-25.286-10.943-8.815-23.515-24.274-28.868-36.885-6.517-15.354-11.3-42.73-12.83-59.338-1.652-17.935-1.18-42.148 2.403-60.469 2.88-14.724 12.454-48.433 12.454-48.433s-4.873-33.646-5.535-49.124c-.827-19.334-1.684-25.431-.845-44.764.791-18.217 1.714-41.912 6.5-59.507 2.41-8.863 8.532-21.444 15.017-27.949 7.732-7.756 20.3-13.43 31.056-15.492 11.275-2.16 27.346-2.784 38.397.324 10.713 3.013 21.664 9.7 29.538 17.563 7.25 7.24 12.933 18.954 15.966 28.74 3.9 12.58 3.74 33.294 3.992 46.302.5 25.814-1.761 58.593-1.761 58.593"
        />
        <path
          stroke="gray"
          stroke-width="4"
          d="M160.11 90.003c-2.688-3.671-11.068-8.644-16.617-8.797s-12.187 1.377-17.106 5.539"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M126.953 85.69c-2.09 1.768-5.076 6.314-5.633 7.595-2.105 4.838-1.698 13.352-.143 18.79 1.118 3.91 2.766 9.815 6.325 11.595 6.093 3.046 14.406-13.352 34.082-22.136 5.06-2.259 1.084-8.627-1.605-12.299"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M350.229 265.111c3.908-6.628 1.474-15-.958-19.548-2.957-5.53-8.298-11.06-14.878-15.063-7.488-4.556-16.576-6.837-23.65-8.482-7.962-1.852-19.863-2.442-28.234-2.188-6.204.189-19.948 2.69-19.948 2.69M384.719 321.195c5.474-2.666 4.694-6.643 4.793-9.944.167-5.587-2.939-12.964-7.937-19.552-6.687-8.815-17.732-16.431-24.478-19.263-10.074-4.23-12.884-10.342-46.513-11.745"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M196.232 272.25s-7.497 38.536 43.318 62.62c18.066 9.722 39.824 18.679 58.035 23.096 16.056 3.895 38.364 8.385 55.1 8.923 18.192.585 49.367 3.71 60.39-5.832 4.644-4.02 1.184-18.103-10.677-27.272-10.34-7.992-21.523-15.406-31.925-19.705-10.188-4.21-35.769-8.406-35.769-8.406"
        />
        <g stroke="#555" stroke-linecap="round" stroke-linejoin="round" stroke-width="5">
          <path d="M167.6 65.453s7.836-.624 11.33 2.22M167.386 65.453s6.224-4.937 11.805-3.65" />
        </g>
        <g stroke="#555" stroke-linecap="round" stroke-linejoin="round" stroke-width="5">
          <path d="M131.406 65.783s-7.835-.624-11.329 2.22M131.62 65.783s-6.223-4.937-11.804-3.649" />
        </g>
      </g>
      <g transform="translate(28 5)">
        <PigeonBlinkAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36055 -.11903 .1137 1.29952 144.406 59.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9962 -.08716 .07107 .81237 144.587 55.607)"
        />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.35246 .19008 -.17559 1.24938 97.406 58.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12746 .90689 96.406 56.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12799 .91066 96.406 56.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99027 .13917 -.1135 .80754 97.587 54.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonAWatch({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 224.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m199.338 151.704 1.883-86.608-12.238-33.89-24.477-21.652-28.241-8.473-36.715 6.59L76.015 26.5 64.72 63.213l-2.824 70.605.941 49.894 2.824 40.48-4.707 35.773-2.824 31.066 4.707 33.89 16.004 38.597 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 26.36-4.707 38.597 19.77 33.89 10.355 27.25 2.117 12.418-9.437-1.987-24.338-16.97-33.298-28.242-27.3-15.063-12.239-11.296-48.952-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-33.89-4.707-15.062.941Z"
      />
      <path
        fill="#b7b0ba"
        d="M163.283 163.977q11.85-4.477 38.968-10.777l-.113-16.102-64.978 19.124-71.703 44.822 2.181 23.554-4.798 22.246s18.351-27.42 30.237-38.376c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.262-13.116 35.92-17.143Z"
      />
      <path
        fill="#a195a6"
        d="M166.957 154.997c12.653-3.265 33.878-4.082 33.878-4.082l.51-53.241-2.408-46.365-14.094-26.733-27.34-18.726-28.089-3.745-23.594 4.12-19.475 10.86-16.48 22.846-5.808 27.636-1.204 42.15-.602 46.364 1.204 30.107 3.907 41.056q14.575-20.07 22.041-27.348c6.861-6.688 21.598-19.643 33.063-26.531 12.63-7.588 32.392-15.246 44.49-18.368Z"
      />
      <path
        fill="#998d9c"
        d="M114.766 159.634c12.2-6.202 32.1-14.743 45.4-17.965 11.337-2.746 39.998-4.255 39.998-4.255l1.59-50.74-4.276-42.022-16.954-24.931L157.09 6.258 129.166 1.77l-24.433 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.204 30.107 1.842 31.583q9.913-15.865 17.446-22.977c8.936-8.438 20.66-18.59 31.615-24.16Z"
      />
      <path
        fill="#959195"
        d="M115.489 148.052c12.2-6.203 32.654-13.373 45.954-16.595 11.337-2.746 38.721-2.979 38.721-2.979l1.59-41.804-4.276-42.022-16.954-24.931L157.09 6.258 129.166 1.77l-24.433 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.204 30.107.565 19.244q8.637-13.738 16.17-20.85c8.936-8.438 23.935-19.96 34.89-25.53Z"
      />
      <path
        fill="#4a4a4a"
        d="M66.2 55.163s-26.271-8.406-30.94-6.227c-5.98 2.793-17.56 1.5-19.626 7.769-.626 1.903-.251 10.775 1.636 11.449 6.415 2.29 16.355 6.95 22.08 9.404 7.081 3.035 23.615 13.688 23.615 13.688"
      />
      <path
        fill="#f1f1f1"
        d="M67.827 47.138c-.604-1.729-7.407-5.247-13.028-2.914-2.717 1.128-5.489 2.059-6.55 5.24-1.123 3.372 5.426 5.841 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="M307.552 379.286s-12.772-14.412-6.714-12.681c10.444 2.984 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.662-26.99q-55.61-6.808-79.689-3.799c-19.981 2.498-50.02 12.148-64.71 25.082-13.512 11.897-25.16 26.761-30.534 43.944-5.587 17.864-2.592 53.301 1.492 74.597 4.637 24.182 39.536 48.488 39.536 48.488l53.71 26.109 75.343 13.427 26.855-2.983 3.73-14.92Z"
      />
      <path
        d="M61.69 154.153s6.183 18.195 14.524 30.865c8.806 13.375 19.794 21.262 19.794 21.262-.291-1.107-10.487 7.67-11.797 17.052-2.145 15.352 7.69 54.284 9.069 59.798 3.828 15.314 26.287 46.176 26.287 46.176l35.807 32.823 41.028 20.141 44.013 10.444q29.273-.149 42.52 5.221c18.445 7.478 38.67 12.227 65.402 18.68 7.64 1.844 22.282 27.49 22.282 27.49l-65.446 10.14-62.681-4.609-72.82-15.67-45.168-20.28-32.262-27.652-20.28-37.793-12.904-44.246.922-41.48 6.452-41.48-4.742-66.882Z"
        opacity=".06"
      />
      <path
        d="M67.478 224.81s5.03 39.276 8.924 58.247c3.427 16.693 10.62 39.02 18.75 53.997 8.31 15.312 21.048 33.4 35.247 43.497 22.812 16.22 56.825 30.012 83.994 36.747 24.193 5.998 58.247 13.297 83.157 14.156 15.707.542 52.262-3.604 52.262-3.604l21.626 15.618-27.032 6.008-57.068 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-3.004-48.657 8.41-45.053Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M326.511 214.103c30.535 22.827 58.278 67.108 75.712 109.881M294.723 211.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M298.255 237.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M156.604 17.27c27.554 11.512 34.565 45.997 34.565 68.657"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M163.133 35.23c12.5 12.627 18.05 28.421 19.464 43.215"
        />
      </g>
      <path
        fill="#ccc"
        d="M419.953 370.41s-.722 22.6-7.878 28.779c-7.652 6.605-20.542 10.627-30.539 12.125-13.495 2.023-40.868 4.491-40.868 4.491l22.226 22.247 38.741 21.854 28.311 10.927 31.29 4.967 17.881-1.987 5.464-16.39-5.464-23.84-14.403-26.325-23.344-23.344-21.417-13.505Z"
      />
      <path
        fill="#9d9d9d"
        d="M424.81 371.196s2.752 23.122-3.143 30.539c-8.96 11.272-24.71 15.727-39.072 18.413-10.465 1.957-32.704 5.808-32.704 5.808l12.715 11.05 38.741 21.854 28.31 10.927 31.292 4.967 17.88-1.987 5.464-16.39-5.464-23.841-14.404-26.324-23.344-23.344-16.27-11.672Z"
      />
      <path
        fill="#ccc"
        d="M227.017 391.5s19.1 1.128 26.808-3.513c8.16-4.913 12.38-10.319 12.65-17.387.393-10.31-10.111-20.731-16.708-31.34-5.077-8.164-16.43-20.221-16.43-20.221l24.013 11.374s3.029-12.097 2.107-17.272c-1.024-5.742-5.486-12.087-8.847-16.852-4.6-6.52-17.273-20.222-17.273-20.222l6.32.843s2.363-8.454 1.263-12.639c-1.035-3.936-4.532-9.22-6.74-12.638-2.919-4.517-12.218-16.43-12.218-16.43l18.115 7.582 14.745 15.588 6.818 16.765-7.947 6.909 15.82 17.549 7.665 15.868-.546 13.58-8.468 14.337 16.604 19.318 9.96 20.569-2.832 13.287h-15.588l-28.226-2.106-21.065-2.949Z"
      />
      <path
        fill="#9d9d9d"
        d="M256.242 394.235s11.406-4.493 16.564-10.588c4.861-5.744 3.403-19.511-.893-26.815-8.939-15.195-24.708-30.937-24.708-30.937l18.349 9.264s3.124-9.584 1.573-20.062c-1.58-10.673-10.507-23.605-19.795-31.558-5.518-4.726-19.664-10.28-19.664-10.28l19.004 6.828s4.022-11.987.393-20.456c-3.54-8.26-19.668-22.815-19.668-22.815l24.405 14.992 11.499 18.836-2.54 15.15 9.036 11.296 11.296 14.12 5.648 21.462-7.907 9.602 15.18 13.566 11.725 19.617-1.427 15.826-15.091 5.907-16.002.945-16.977-3.9Z"
      />
      <path
        fill="#ccc"
        d="M210.75 387.055s17.364-3.387 22.165-8.942c3.995-4.622 4.957-14.945 4.508-21.037-.745-10.137-6.613-20.89-10.973-30.071-3.194-6.726-10.064-15.51-10.064-15.51s6.694-4.104 8.014-7.013c2.111-4.652 1.654-12.196 0-17.03-2.461-7.195-15.527-20.036-15.527-20.036s8.337-4.153 10.017-7.513c2.42-4.839 1.62-12.87 0-18.032-1.26-4.017-8.014-11.52-8.014-11.52l-21.037-5.01s4.025 8.83 7.012 14.026c2.748 4.779 2.374 7.828.501 13.023-1.45 4.02-17.03 11.52-17.03 11.52s15.208 12.933 17.531 18.533c1.792 4.32 4.41 8.06 3.005 12.522-1.355 4.305-13.023 9.517-13.023 9.517s9.13 14.883 12.022 21.538c3.812 8.775 5.877 13.976 6.01 23.542.07 4.975-1.746 13.274-5.009 17.03-4.188 4.823-19.534 8.516-19.534 8.516l29.425 11.947Z"
      />
      <path
        fill="#9d9d9d"
        d="M201.442 383.532s20.043-2.477 25.463-8.424c4.051-4.446 5.043-13.544 4.508-19.535-.82-9.165-7.012-20.036-12.087-28.189-4.28-6.875-11.327-17.047-11.327-17.047s7.746-3.566 9.89-6.356c2.816-3.663 2.83-6.716 1.503-13.023-2.004-9.517-20.942-24.21-20.942-24.21s11.267-2.996 14.43-7.346c3.321-4.566 5.009-8.014 1.503-15.027-2.738-5.476-12.022-16.529-12.022-16.529l-8.892-2.197s3.937 6.988 7.472 12.039c2.906 4.151 5.45 10.356 3.737 14.53-2.253 5.488-17.436 12.869-17.436 12.869s17.713 14.523 19.511 20.757c1.264 4.382 1.037 7.462-.83 11.624-1.777 3.962-12.454 8.302-12.454 8.302s7.166 8.256 9.726 14.518c3.717 9.09 9.131 22 9.142 31.977.005 5.143-1.407 12.526-4.68 16.494-4.066 4.928-19.012 9.6-19.012 9.6l12.797 5.173Z"
      />
      <path
        d="m351.11 426.22 34.627-6.54s18.243 15.483 27.102 20.543c14.005 8 19.505 9.945 28.4 12.82 8.43 2.724 33.937 2.018 33.937 2.018l8.913 8.595-6.367 9.868-14.3 1.886-37.153-6.755-39.687-16.888-35.471-25.546Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="m425.995 374.054-3.753 28.142 61.164 57.412-6.004-30.77-21.764-30.394-29.643-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M423.983 403.493c17.257 14.607 38.835 36.288 58.52 57.894"
      />
      <path
        fill="#555"
        d="M29.708 61.807c3.782.63 9.165 4.216 12.713 3.443 2.067-.45 5.327-1.975 6.091-4.238.91-2.695-3.847-12.076-3.847-12.076l-22.897 2.045s-6.086 4.569-6.626 8.177c-.268 1.79 10.296 1.937 14.566 2.649Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.993 465.89 37.482 6.317M251.085 459.994l63.593 5.896M313.414 454.94l.843 10.108M239.69 477.445l-71.28-11.441M240.135 480.842l35.376 2.526M239.714 480.842l-78.654 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.744 24.697)"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M67.276 47.633s-4.439-4.325-10.503-3.82c-2.896.242-6.897 1.275-7.957 4.456-1.124 3.373.577 6.237 1.89 6.664 2.975.967 3.293 1.234 6.42 1.307 2.67.063 7.056 1.666 7.056 1.666"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M373.209 443.565s-32.91 6.72-48.697 7.983c-19.958 1.597-48.101 1.344-67.857-.798-20.95-2.271-50.444-7.58-70.91-12.602-12.714-3.12-29.457-8.504-41.317-14.048-12.094-5.653-30.538-17.678-40.251-26.837-11.408-10.756-20.567-25.214-27.51-39.272-7.153-14.48-12.868-32.625-15.6-48.543-1.509-8.8-2.254-23.526-1.754-32.44.896-15.997 7.437-52.887 7.437-52.887s-3.748-35.976-4.41-51.455c-.827-19.333-.84-45.17 0-64.503.79-18.218.48-42.959 5.265-60.554 2.411-8.863 8.532-21.444 15.017-27.949 7.732-7.756 20.301-13.43 31.057-15.492 11.274-2.16 27.346-2.784 38.397.324 10.712 3.013 21.663 9.7 29.537 17.563 7.25 7.24 13.227 18.867 15.967 28.74 3.491 12.58 3.907 33.246 3.991 46.302.108 16.604-1.01 55.823-1.01 55.823"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M187.399 154.706s21.788-3.175 33.578-2.212c16.322 1.334 37.904 6.865 52.915 13.412 18.669 8.142 41.455 23.099 57.293 35.903 18.253 14.756 37.65 35.665 50.408 55.366 12.287 18.973 24.767 49.748 31.442 71.343 3.818 12.354 10.058 41.91 10.058 41.91s27.82 21.665 37.005 33.84c9.428 12.495 21.289 31.11 23.346 46.629.804 6.065 1.076 15.896-3.353 20.117-6.231 5.94-19.901 3.79-28.499 3.353-13.892-.706-29.844-7.011-42.729-12.254-11.505-4.682-27.104-13.18-37.739-19.598-8.492-5.126-20.106-15.11-20.106-15.11"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M49.98 48.527s-18.376-.197-23.715 1.227c-5.539 1.477-8.565 1.499-10.63 7.769-.627 1.903-1.515 8.851 2.044 10.63 6.093 3.047 12.086 4.557 18.4 6.952 10.32 3.915 26.13 17.428 26.13 17.428"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M259.39 289.28c5.292-5.586 4.793-14.29 3.441-19.267-1.644-6.051-5.612-12.636-11.13-18.01-6.279-6.117-14.626-10.374-21.152-13.56-12.467-6.088-39.934-13.565-39.934-13.565"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M278.501 342.929c5.823-1.78 6.927-6.949 7.54-10.194 1.035-5.492-.884-13.263-4.795-20.55-5.233-9.748-14.958-18.992-21.18-22.84-9.292-5.746-13.125-11.41-40.173-18.423M105.548 277.483s-.795 26.088 8.675 43.92c5.133 9.664 16.293 21.512 23.127 27.26 11.534 9.7 26.056 19.229 39.919 25.14 11.665 4.973 24.945 9.744 35.967 12.967 13.906 4.066 29.803 6.7 42.363 8.431 10.963 1.51 21.784 1.99 33.847 1.536 4.347-.163 12.187-1.492 14.685-4.378 3.558-4.11 2.441-14.706.304-19.528-2.753-6.214-9.956-13.735-14.881-19.117-7.506-8.202-19.887-14.932-31.46-22.106-14.95-9.266-25.68-12.6-25.68-12.6"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M98.908 52.194s7.835-.624 11.33 2.22M98.694 52.194s6.224-4.937 11.804-3.649"
        />
      </g>
      <g>
        <PigeonBlinkAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.31922 .35348 -.33763 1.26004 104.406 55.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89324 .10968 -.12187 .99255 104.406 52.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.86929 .23292 -.25882 .96593 104.406 52.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.96593 .25882 -.21106 .78768 105.587 50.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonAGuess({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 224.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m199.338 151.704 19.565-47.032 3.286-46.004-14.083-31.922-31.921-21.594-41.31-1.408-24.411 12.205-18.778 29.105-13.84 46.837-19.049-5.911-22.989 5.254-22.477 38.044-11.57 41.319 6.279 42.667 20.166 38.868 29.53 31.268 5.1 41.52 16.004 38.598 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 16.62-2.801 37.307 22.823 39.503 15.362h28.53l8.339-12.29-4.39-28.968-21.068-37.746-30.486-26.573-9.398-57.836-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-33.89-4.707-15.062.941Z"
      />
      <path
        fill="#b7b0ba"
        d="M57.784 254.243s17.74-37.498 34.163-52.317c14.722-13.284 22.42-19.964 32.67-26.264 10.277-6.317 20.203-12.331 29.788-15.374q13.601-4.319 47.113-6.561l.182-16.63-68.114 17.105-31.614 22.833-11.306 6.955-13.944 9.07-15.184 19.048-3.754 42.135Z"
      />
      <path
        fill="#a195a6"
        d="M61.519 225.59c4.846-3.817 14.866-22.297 26.584-33.31 9.34-8.779 21.487-18.208 30.429-24.664 21.68-15.654 36.35-16.529 44.139-18.132 16.51-3.4 41.506-3.083 41.506-3.083l8.325-21.98-82.278 3.944-55.103 49.233c-1.375 4.851-8.8 17.746-8.8 17.746l-3.231 15.63s-.964 12.474-1.571 14.616Z"
      />
      <path
        fill="#998d9c"
        d="M63.452 205.381s18.954-23.828 42.354-40.805c20.954-15.202 47.018-23.15 57.35-24.931 14.082-2.428 44.916-1.315 44.916-1.315l8.427-26.525-82.708 13.585-60.316 42.968s.763 4.25-2.798 13.992c-4.357 11.918-7.225 23.031-7.225 23.031Z"
      />
      <path
        fill="#959195"
        d="M70.677 184.216s20.941-22.897 32.144-30.086c15.867-10.182 48.021-23.114 57.803-24.657 21.629-3.411 49.801-.86 49.801-.86l11.736-42.249-2.347-34.738-9.858-22.533-24.88-19.716-25.82-6.572-27.696.47-21.124 11.266-14.083 19.247-8.45 21.594-12.525 36.51 15.9 12.305s17.348 33.371 10.548 43.466c-6.313 9.373-30.216 1.573-30.216 1.573l-.467 14.925-.466 20.055Z"
      />
      <path
        fill="#4a4a4a"
        d="M88.941 50.31S60.618 38.804 55.913 38.905c-6.6.142-14.473.12-16.538 6.39-.627 1.903-2.477 9.947-.59 10.62 6.415 2.292 15.493 9.159 21.217 11.612 7.082 3.035 17.728 18.778 17.728 18.778"
      />
      <path
        fill="#f1f1f1"
        d="M91.87 37.938c-.604-1.73-7.407-5.247-13.028-2.914-2.717 1.128-5.489 2.058-6.55 5.24-1.123 3.372 5.426 5.841 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="m289.564 405.437-10.284-26.444-13.957-16.895 5.877-8.08s28.307 12.39 29.074 12.587c13.951 3.582 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.661-26.99q-54.368-5.981-77.624-4.237c-22.554 1.692-52.149 8.365-67.662 20.299-14.66 11.277-32.421 34.392-35.523 53.002-2.819 16.915.28 35.644 1.128 67.662.652 24.614 27.12 50.889 27.12 50.889l46.278 34.524 69.048 24.24 37.463 4.408 8.08-14.691Z"
      />
      <path
        d="M83.713 182.512c-.748 12.382-4.592 25.078-8.354 36.363-5.267 15.8-1.379 31.861 3.573 48.084 4.773 15.639 22.336 39.492 22.336 39.492l8.846 28.792 30.598 32.978 55.077 32.298 53.377 16.319 36.378 1.02 4.08-13.26s26.178 12.24 42.837 15.98c7.67 1.722 31.618 24.818 31.618 24.818l-58.552 8.388-62.68-4.609-72.82-15.67-45.168-20.28-32.262-27.653-20.28-37.792-9.465-25.062-4.612-32.286-30.141-38.9-16.051-30.252s1.747-36.346 6.813-27.654c3.345 7.464 16.832 4.81 16.832 4.81l7.615-16.432 10.02 3.206 6.813-2.405s5.796-15.92 2.004-24.046c-2.65-5.68-10.02-22.043-10.02-22.043l21.76 13.855 10.32 3.44q-.003 20.405-.492 28.5Z"
        opacity=".06"
      />
      <path
        d="m42.907 183.206 14.83 2.805 4.808-10.42v-15.63l-7.614-19.237 16.03 6.813-.025 21.707-6.388 37.346s-4.108 23.222-6.388 31.941.884 21.357 7.955 40.36c5.943 15.971 16.7 42.348 24.828 57.325 8.31 15.312 22.931 37.695 37.13 47.79 22.812 16.221 56.046 32.58 83.215 39.315 24.193 5.998 57.648 9.624 82.56 10.483 15.706.542 51.517-4.606 51.517-4.606l15.98 14.96-16.94 5.318-57.067 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-3.414-33.636-11.835-12.856s-9.56-15.195-12.27-22.347c-2.96-7.814-5.332-18.851-6.134-27.168-.61-6.335.632-21.205.632-21.205l9.849-6.079 4.007-12.023Z"
        opacity=".06"
      />
      <path
        d="m57.736 186.011 4.81-10.42v-15.63l-7.615-19.237 16.03 6.813-.025 21.707-13.432 75.051s-9.372-15.379-11.931-22.608c-3.088-8.72-5.976-30.251-5.976-30.251l4.797-7.194 13.342 1.77Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M326.511 214.103c30.535 22.827 58.278 67.108 75.712 109.881M294.723 211.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M298.255 237.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M184.644 20.11c25.332 15.814 29.678 46.274 23.842 72.76"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M187.69 37.784c11.772 12.511 18.244 23.087 16.455 42.658"
        />
      </g>
      <path
        fill="#ccc"
        d="M418.587 375.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M422.83 380.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.644 17.961-1.02 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        fill="#ccc"
        d="M213.02 405.953s15.504-2.829 23.373-7.192c8.33-4.618 12.136-8.657 12.136-15.731 0-9.888-4.496-19.19-8.54-26.07-4.074-6.93-12.136-19.327-12.136-19.327l15.282 7.641s3.754-9.519 2.248-20.675c-.78-5.78-7.149-14.445-10.338-19.327-4.364-6.68-16.63-19.328-16.63-19.328l9.213.472s2.664-8.364 1.715-12.585c-.894-3.971-4.2-9.377-6.285-12.872-2.756-4.617-11.624-16.856-11.624-16.856l17.834 8.225 14.179 16.105 6.215 16.997-9.673 10.403 16.667 14.32 7.094 16.132-1.03 13.552-8.974 14.025 15.904 19.899 9.219 20.91-3.305 13.18-12.652 9.788-32.811-4.494-17.08-7.192Z"
      />
      <path
        fill="#9d9d9d"
        d="M226.365 410.147s21.71-5.108 27.36-10.752c5.323-5.318 5.775-19.172 2.103-26.809-7.64-15.888-15.839-27.761-15.839-27.761l11.188 6.067s2.887-10.472 2.215-21.043c-.684-10.768-10.356-25.961-18.948-34.661-5.105-5.17-17.601-10.196-17.601-10.196l17.23 6.7s5.009-11.609 2.099-20.35c-2.84-8.528-17.697-24.377-17.697-24.377l23.07 16.975 11.847 18.283-3.322 18.275 5.63 10.068 10.983 14.299 8.307 22.705-9.7 15.028 9.641 8.534 11.134 20.187 4.985 19.936-8.861 8.306-28.797-2.215-27.027-7.199Z"
      />
      <path
        fill="#ccc"
        d="M196.15 399.695s14.026.446 21.307-6.834c7.236-7.236 6.318-16.318 6.362-22.426.075-10.164-4.906-21.356-8.512-30.858-2.641-6.962-7.9-14.654-7.9-14.654s6.49-4.845 8.04-7.638c2.48-4.467 2.266-12.346 1.008-17.298-1.873-7.37-14.676-20.892-14.676-20.892s8.506-3.62 10.452-6.835c2.801-4.627 2-12.814.804-18.09-.932-4.105-8.04-14.874-8.04-14.874l-19.698-7.638s5.08 9.052 7.638 14.472c2.353 4.986 4.296 10.25 2.01 15.276-1.77 3.89-18.09 12.864-18.09 12.864s11.68 12.814 13.543 18.583c1.438 4.451 3.746 8.39 1.986 12.724-1.698 4.181-13.749 8.435-13.749 8.435s7.9 15.571 10.245 22.438c3.092 9.054 4.73 14.405 4.092 23.95-.332 4.965-2.81 13.09-6.366 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#9d9d9d"
        d="M185.376 394.651s20.19-.494 26.168-5.879c4.469-4.025 6.351-12.982 6.408-18.996.086-9.201-5.008-20.629-9.256-29.241-3.583-7.263-9.596-18.08-9.596-18.08s8.06-2.786 10.468-5.351c3.163-3.369 3.477-6.406 2.776-12.812-1.058-9.668-19.007-27.207-19.007-27.207s10.092-2.415 13.668-6.432c3.754-4.218 2.8-12.777 0-20.1-2.186-5.72-7.638-12.463-7.638-12.463l-7.236-4.422s3.812 8.697 6.834 14.07c2.483 4.418 2.919 12.498.804 16.483-2.781 5.24-16.874 11.162-16.874 11.162s16.198 16.195 17.375 22.576c.827 4.485.297 7.528-1.97 11.486-2.158 3.768-13.21 7.037-13.21 7.037s6.32 8.92 8.251 15.404c2.805 9.411 6.923 22.791 5.952 32.721-.5 5.119-2.631 12.327-6.279 15.954-4.531 4.504-19.864 7.683-19.864 7.683l12.226 6.407Z"
      />
      <path
        fill="#ccc"
        d="M82.923 94.179s1.86 18.67-4.477 24.375c-8.483 7.634-42.126 5.64-42.126 5.64l18.158 15.46s-.324 13.23-4.275 18.385c-2.48 3.235-7.739 6.909-11.972 7.268-3.112.265-10.26-3.848-10.26-3.848l4.275 8.124s-3.598 6.006-5.986 7.696c-1.986 1.405-5.268 2.737-7.696 2.565-2.86-.204-8.55-4.276-8.55-4.276l8.978 19.24 14.536 5.558 9.406-16.247 14.964 1.71 5.986-18.811-5.13-23.943 27.79 11.116 15.282-6.288 1.493-14.923-6.965-18.904Q83.796 94.345 82.924 94.18Z"
      />
      <path
        fill="#9d9d9d"
        d="M59.303 148.231s-3.957 13.483-6.59 16.616c-2.734 3.255-7.784 4.9-11.503 6.09-8.458 2.707-13.24-9.05-13.24-9.05l5.301 15.266s-2.734 6.617-10.003 8.676c-4.725 1.339-12.827-8.123-12.827-8.123l12.4 19.667 12.826 1.283 6.413-15.392 13.681 2.565 8.978-14.536M101.329 149.653c3.217-2.929 1.492-14.426 1.492-14.426l-4.66-17.38-8.77-15.452s.368 10.376-1.99 17.411c-4.302 12.831-42.284 10.447-42.284 10.447l27.746 19.663q22.15 5.486 28.466-.263Z"
      />
      <path
        d="m344.664 428.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.504 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.46 381.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M417.266 411.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <path
        fill="#555"
        d="M53.591 51.808c3.704.987 8.722 5.07 12.327 4.637 2.101-.252 5.491-1.458 6.468-3.638 1.162-2.596-2.68-12.388-2.68-12.388l-22.989-.144s-6.493 3.969-7.374 7.51c-.437 1.755 10.065 2.908 14.248 4.023Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 465.89 37.482 6.317M250.785 459.994l63.593 5.896M313.115 454.94l.842 10.108M239.39 477.445l-71.28-11.441M239.835 480.842l35.376 2.526M239.414 480.842l-78.653 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.444 24.697)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M362.326 446.214s-22.326 4.071-38.114 5.334c-19.958 1.597-48.101 1.344-67.857-.798-20.95-2.271-50.444-7.58-70.909-12.602-12.715-3.12-29.457-8.504-41.318-14.048-12.094-5.653-30.537-17.678-40.25-26.837-11.408-10.756-20.568-25.214-27.511-39.272-7.153-14.48-12.868-32.625-15.599-48.543-1.51-8.8-3.763-38.332-2.986-54.769.334-30.902 7.754-61.155 7.754-61.155s6.788-19.773 4.83-44.438M75.626 89.927c5.473-17.394 13.267-41.366 22.44-57.126 4.62-7.939 13.785-18.509 21.732-23.116 9.474-5.493 23.082-7.726 34.005-6.936 11.45.827 27.136 4.38 37.008 10.24 9.57 5.68 18.42 14.97 23.993 24.601 5.131 8.868 7.9 21.646 7.994 31.89.12 13.056-4.821 33.127-8.115 45.76-4.189 16.069-13.804 37.231-13.804 37.231"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M187.099 154.706s21.789-3.175 33.579-2.212c16.322 1.334 37.904 6.865 52.914 13.412 18.669 8.142 41.455 23.099 57.294 35.903 18.252 14.756 37.65 35.665 50.408 55.366 12.287 18.973 24.766 49.748 31.441 71.343 3.818 12.354 7.28 47.345 7.28 47.345M420.055 377.578s26.297 22.577 34.835 35.213c8.765 12.97 19.639 32.18 20.885 47.784.486 6.098.246 15.93-4.397 19.914-6.533 5.607-20.072 2.748-28.635 1.863-13.837-1.429-29.44-8.557-42.033-14.465-11.245-5.275-26.38-14.574-36.666-21.538-8.214-5.56-19.291-16.137-19.291-16.137"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M74.16 42.22s-18.091-3.232-23.592-2.71c-5.707.541-8.695.063-11.768 5.905-.933 1.774-2.957 8.48.259 10.823 5.505 4.011 11.167 6.49 16.998 9.896 9.532 5.567 20.67 18.656 20.67 18.656"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M91.404 39.048s-3.663-5-9.727-5.503c-2.896-.24-7.013.117-8.585 3.08-1.666 3.14-.461 6.246.763 6.883 2.774 1.446 3.044 1.762 6.116 2.352 2.623.502 6.683 2.808 6.683 2.808M246.286 302.562c5.881-4.962 6.354-13.668 5.563-18.764-.96-6.197-4.172-13.182-9.058-19.137-5.56-6.777-13.381-11.935-19.512-15.828-11.713-7.437-38.179-17.921-38.179-17.921"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M263.854 361.95c5.984-1.122 7.656-6.135 8.625-9.292 1.64-5.343.597-13.279-2.48-20.955-4.115-10.27-12.751-20.538-18.507-25.054-8.596-6.744-11.775-12.8-37.876-22.776M101.984 280.398s-3.69 25.837 3.738 44.611c4.026 10.176 13.8 23.191 19.952 29.664 10.383 10.922 23.756 22.007 36.876 29.423 11.039 6.24 23.707 12.458 34.301 16.886 13.368 5.588 28.874 9.974 41.163 13.09 10.727 2.72 21.428 4.4 33.466 5.29 4.338.322 12.278-.127 15.081-2.717 3.993-3.688 4.061-14.343 2.474-19.373-2.045-6.482-8.367-14.757-12.663-20.653-6.547-8.987-18.103-17.052-28.807-25.468-13.826-10.871-24.118-15.377-24.118-15.377"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M53.728 138.72c7.6 9.641 13.168 31.036 5.675 44.375-4.376 7.28-15.05 1.026-21.207-5.13-6.441-6.442-10.12-15.739-10.12-15.739"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M39.508 128.252c14.89 11.61 46.51 36.155 61.684 20.395 6.194-6.433-6.634-39.398-15.346-48.955-5.229-5.737-14.224-11.349-22.416-12.92-7.443-1.426-18.13 1.422-24.681 5.232-11.534 6.71-18.195 24.727-24.274 36.605-6.484 12.67-11.239 32.55-11.896 46.767-.697 15.05 2.374 29.085 7.232 43.346 5.08 14.912 14.033 27.882 23.066 40.787 6.918 9.883 24.606 23.622 24.606 23.622"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M42.985 182.41s-3.374 19.914-12.919 17.771c-15.91-3.571-19.089-23.052-19.089-23.052"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M119.014 40.671s7.382 2.7 9.371 6.742M118.819 40.582s7.716-1.892 12.25 1.606"
        />
      </g>
      <g>
        <PigeonBlinkFastAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.2116 .6303 -.60203 1.15725 125.406 44.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.84841 .30018 -.33355 .94273 125.406 41.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.79838 .41534 -.46151 .88714 125.406 41.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.88714 .46151 -.37635 .72343 126.587 39.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonBFail({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 240.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m173.952 122.11-66.957 13.766-3.13 23.78-13.766-8.761 2.503-11.89L75.08 117.73l-21.276-11.264s-24.48-3.01-33.166 1.877c-6.353 3.575-13.767 20.025-13.767 20.025l-5.11 42.23L10 212.847l25.657 35.668 36.92 16.27 5.632 52.565 15.019 48.81 30.037 42.553 40.05 26.908 62.576 18.148 65.704-.819 54.6-4.707 16.62-2.801 32.66 27.726 32.54 20.024 28.786 3.755 5.006-10.638-4.38-33.166-17.522-39.424-19.355-35.669-9.398-57.836-18.805-53.551-25.657-41.927-34.417-37.546-41.927-25.031-48.185-12.516 5.632-22.527-73.84-5.007Z"
      />
      <path
        fill="#b7b0ba"
        d="M75.473 240.828s17.609-24.343 32.26-39.827c10.1-10.674 27.27-25.303 41.44-31.846 11.664-5.386 29.686-12.098 42.351-14.442q14.503-2.683 49.024-3.7l7.104-19.75-51.13 4.228-52.283 18.838-34.6 24.604-22.297 30.37-11.87 31.525Z"
      />
      <path
        fill="#a195a6"
        d="M81.447 219.72s18.569-23.024 28.676-32.26c10.398-9.503 25.72-20.653 38.632-26.286 13.306-5.805 32.678-10.181 47.043-12.271 13.758-2.002 46.327-1.149 46.327-1.149l7.218-27.409-44.979.385-58.05 15.761-43.441 30.371-9.227 21.913-12.199 30.945Z"
      />
      <path
        fill="#998d9c"
        d="M120.632 166.497c13.333-10.554 26.235-15.464 36.67-18.31 13.39-3.652 33.216-7.269 47.71-8.1 11.412-.656 37.736 3.563 37.736 3.563l7.98-24.536-58.435.384-56.512 13.84-29.986 24.22-13.292 33.29s19.47-17.496 28.129-24.35Z"
      />
      <path
        fill="#959195"
        d="M103.16 165.657s18.004-13.1 30.647-19.312c15.196-7.466 39.832-11.703 56.57-14.26 16.01-2.445 54.362 3.599 54.362 3.599l11.16-46.19-2.347-34.739-9.858-22.533-24.88-19.716-25.819-6.572-27.697.47-21.124 11.266-14.083 19.246-8.45 21.595-12.525 36.509-2.463 27.476-1.305 29.868-2.189 13.293Z"
      />
      <path
        fill="#4a4a4a"
        d="M122.71 54.45s-21.446-3.423-25.757-1.537c-6.048 2.646-27.06 11.435-26.58 18.02.145 1.998 1.5 10.14 3.501 10.044 6.804-.326 17.814 2.563 24.041 2.65 7.704.108 14.031 2.732 14.031 2.732"
      />
      <path
        fill="#f1f1f1"
        d="M126.085 46.01c-.604-1.728-7.407-5.246-13.028-2.913-2.717 1.128-5.489 2.059-6.55 5.24-1.123 3.372 5.426 5.841 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="m241.909 426.32 8.688-24.06-4.678-30.077 14.704 1.337s28.042 9.434 40.485 11.646c15.324 2.725 36.221 4.823 51.747 3.726 15.142-1.07 38.946.258 49.458-10.693 11.028-11.49 9.086-52.303 9.086-52.303l-18.443-53.965-18.046-36.09-36.09-42.107L296.045 167q-50.074-7.57-71.456-5.348c-22.233 2.31-53.182 8.58-71.57 20.72-13.459 8.885-27.37 27.157-33.418 42.106-4.61 11.393-13.616 55.778 4.049 84.377 6.368 10.31 31.373 31.248 31.373 31.248l41.438 59.483 29.408 24.729 16.04 2.005Z"
      />
      <path
        d="M107.824 119.37s2.008 21.518 5.215 39.959c2.748 15.8 6.766 28.932 6.766 28.932s-15.43 21.38-18.97 31.95c-3.877 11.573-5.121 28.247-4.493 40.436.836 16.223 4.528 38.236 11.482 52.916 5.388 11.373 16.688 24.482 26.086 32.851 10.63 9.466 40.31 25.02 40.31 25.02l23.57 29.085 34.116 25.126 13.9-3.476 5.56-12.51s76.448 7.177 93.107 10.917c7.67 1.722 21.606 24.82 21.606 24.82l-58.552 8.388-61.721 1.05-64.635-11.814-48.65-27.106-30.58-32.665-15.986-34.75-11.815-39.615v-43.785l-32.665-13.9-27.427-29.92 3.102-21.51 13.9 13.204h15.29v-17.375l21.545 2.78 6.255-14.595-17.375-28.495 17.375 2.78 12.51-6.95 17.68 9.187 3.494-40.935Z"
        opacity=".06"
      />
      <path
        d="m63.332 199.254 10.116-4.817-5.78-21.679L54.66 153.97l21.197 1.445 13.007-4.336 15.898 8.19-13.97 34.204-13.49 39.022-3.162 32.604s10.974 46.785 20.018 65.562c6.052 12.565 19.243 28.479 27.937 39.383 9.1 11.413 22.73 25.776 34.75 34.055 16.443 11.325 41.115 22.885 60.466 27.8 18.228 4.63 43.75 4.69 62.55 4.17 20.578-.568 68.11-8.34 68.11-8.34l14.237 14.7-16.938 5.319-59.849 8.477-54.21-3.475-52.125-11.815-39.616-19.46-26.41-25.716-22.24-34.055-13.205-50.04-4.17-45.87-14.45-5.358-20.714-27.941-7.708-20.234 15.897.964.482-17.343q15.979 4.575 16.38 3.372Z"
        opacity=".06"
      />
      <path
        d="m73.93 184.32-16.38-27.46 16.862-.481 13.97-5.3 14.935 8.19-12.526 35.168-9.153 23.605-6.263 23.124-16.197-13.562L47.93 210.17l-1.125-14.623 22.308 3.707 4.817-14.934Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -291.145 -101.811)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -382.447 -109.689)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.16963 .74612 -.72657 2.11277 -283.83 -286.532)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M330.72 209.128c29.08 24.655 54.057 70.554 68.836 114.315M299.155 204.536c30.176 21.184 85.854 85.776 99.222 148.031"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M301.057 231.193c30.176 21.184 64.272 64.493 82.183 97.608"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M219.324 24.674c25.331 15.813 29.678 46.273 23.842 72.758"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M222.37 42.347c11.772 12.511 18.244 23.087 16.454 42.658"
        />
      </g>
      <path
        fill="#ccc"
        d="M420.118 380.325s.535 16.887-5.766 24.821c-6.126 7.715-15.43 10.892-26.775 13.388-13.327 2.932-42.658 2.042-42.658 2.042l20.075 23.528 29.612-4.556 32.345-15.489 7.29-21.412-14.123-22.322Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.832 385.211s.97 17.409-6.151 23.659c-10.275 9.02-20.97 13.23-35.455 15.138-10.556 1.39-33.295.3-33.295.3l12.78 16.463 30.623 30.186 27.998 17.936 22.311 7.437 15.312-.875 6.125-11.812-5.25-32.81-10.937-23.624-12.25-21.436-11.811-20.562Z"
      />
      <path
        fill="#ccc"
        d="M182.126 377.789s14.672 6.508 23.464 8.422c9.305 2.027 17.628-.053 21.872-5.712 5.19-6.922 4.987-9.465 6.488-17.303 1.513-7.897.865-22.927.865-22.927l4.759 9.95s8.91-1.005 15.14-10.382c3.227-4.858 4.365-16.289 5.19-22.061 1.13-7.899-1.297-18.601-1.297-18.601l6.921 12.544s5.714-3.987 7.786-7.786c1.95-3.573 2.289-9.84 3.028-13.842.977-5.288.866-20.764.866-20.764l9.949 17.736 5.623 19.898-5.623 19.033-14.708 4.759 1.298 12.544v17.736l-9.95 11.68-15.337 2.726 1.65 14.984.601 21.057-8.422 15.04-18.65-6.016-22.26-17.447-19.253-25.268Z"
      />
      <path
        fill="#9d9d9d"
        d="M190.64 390.888s18.653 6.048 31.826 3.792c7.416-1.27 14.835-10.454 16.406-18.781 3.266-17.324-.753-27.521-.753-27.521l6.716 7.975s10.947-2.623 15.53-12.172c5.037-10.494 4.198-23.086 2.938-33.58-.865-7.213 4.617 7.976 4.617 7.976s8.9-4.72 11.333-11.753c3.295-9.523 1.68-31.9 1.68-31.9l7.554 16.79 3.778 28.542-10.074 9.234-11.332-.42 2.938 24.764-7.975 18.888-16.79-1.678 2.1 14.27.985 23.597-8.121 17.537-12.173-.42-21.448-15.527-19.735-19.613Z"
      />
      <path
        fill="#ccc"
        d="M170.826 362.625s17.924 12.115 26.646 11.26c8.148-.8 18.89-7.383 23.268-14.262 3.687-5.794 3.677-15.27 4.128-22.518.364-5.843 0-19.515 0-19.515s10.566 4.877 14.637 3.002c5.493-2.53 8.693-11.686 9.758-17.639 1.183-6.615-3.378-22.143-3.378-22.143s11.113 4.25 15.763 3.003c3.16-.848 6.735-4.233 8.256-7.13 2.421-4.614 1.877-17.265 1.877-17.265l-8.632-11.258s-2.216 12.086-4.88 16.513c-1.323 2.2-3.951 3.206-6.004 3.753-5.662 1.506-19.515-.751-19.515-.751s2.308 12.335 1.876 17.639c-.379 4.655-.323 12.303-4.128 15.012-4.228 3.01-17.264-1.126-17.264-1.126s-3.257 19.185-6.005 27.021c-2.115 6.035-4.235 15.346-9.382 19.14-4.678 3.45-13.339 3.36-19.14 3.003-5.727-.352-18.39-5.254-18.39-5.254l10.509 19.515Z"
      />
      <path
        fill="#9d9d9d"
        d="M168.408 357.277s20.732 12.413 30.174 11.886c5.022-.28 11.869-3.94 15.087-7.315 3.98-4.173 5.592-10.374 6.857-16 2.016-8.966.723-34.94.723-34.94s12.265 7.44 17.107 5.222c4.13-1.892 6.463-8.796 7.315-13.258 1.427-7.47-3.357-26.251-3.357-26.251s14.611 5.763 19.816 2.935c2.857-1.553 4.221-6.451 5.029-9.6 1.15-4.49.3-15.443.3-15.443l-3.043-4.217s-.161 8.877-1.301 12.458c-.965 3.032-2.411 7.713-5.2 9.244-5.512 3.028-20.93-1.129-20.93-1.129s3.33 18.933.914 26.517c-1.099 3.45-3.767 7.474-6.858 8.686-4.135 1.622-14.63-2.286-14.63-2.286s-2.563 20.547-5.028 28.803c-1.415 4.738-4.118 10.857-7.315 14.63-2.937 3.465-8.749 6.77-13.258 7.315-8.886 1.074-28.803-7.773-28.803-7.773l6.4 10.516Z"
      />
      <path
        fill="#ccc"
        d="M63.032 109.724s6.281 12.686 1.715 19.887c-6.113 9.638-28.459 16.115-28.459 16.115l13.715 8.229 8.23 8.228s1.028 14.058-6.172 18.858c-3.514 2.343-16.801-.342-16.801-.342l5.143 8.228s-4.31 5.852-6.858 7.544c-1.544 1.025-3.987 1.835-5.828 2.057-3.08.37-10.287-1.029-10.287-1.029l15.43 15.43 13.029 1.028 1.371-18.172 21.944 4.114 2.4-20.23-16.458-25.029 24.687 1.715 13.715-9.258-2.743-13.715-11.315-11.657q-15.572-12.076-16.458-12Z"
      />
      <path
        fill="#9d9d9d"
        d="M60.658 163.792s1.955 10.883 0 14.904c-1.582 3.255-6.195 7.017-9.484 7.452-4.743.628-14.227-2.032-14.227-2.032l5.759 7.452s-6.345 9.348-10.532 10.046c-5.504.917-15.429-2.4-15.429-2.4l16.137 14.371 12.872-.338 2.032-17.275 23.154 3.108 2.442-13.885M94.53 145.163c1.829-3.948-3.792-13.42-3.792-13.42l-9.08-7.92L69.127 114s1.62 5.815 1.016 13.21c-1.355 16.598-32.518 21.34-32.518 21.34l31.204 8.358q22.113-3.996 25.702-11.745Z"
      />
      <path
        d="m348.45 424.468 26.432.32s16.908 19.381 23.95 24.889c10.479 8.193 28.977 21.487 41.638 25.564 5.328 1.715 15.11-1.339 15.11-1.339l7.633 10.288-5.973 11.947-21.24-1.66-26.654-12.892-26.94-21.687-33.956-35.43Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.52 383.024s-.453 12.512-1.575 17.733c-.555 2.583-2.893 8.317-2.893 8.317l18.352 23.698 26.145 51.418.655-7.984-3.5-21.436-18.373-38.498-18.812-33.248Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.876 407.675c16.246 18.119 36.056 62.865 42.926 79.86"
      />
      <path
        fill="#555"
        d="M87.879 71.358c6.895-2.414 12.572-.573 15.562-2.634 1.742-1.2 4.22-5.756 4.07-8.14-.179-2.839-3.24-6.914-3.24-6.914l-14.714 2.543-17.44 10.536s1.432 4.689 3.79 5.806c3.41 1.616 7.885.234 11.972-1.197Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.28 37.482 6.317M250.785 460.383l63.593 5.896M316.37 454.842l-2.413 10.595M237.39 477.834l-71.28-11.44M237.835 481.231l35.376 2.527M237.414 481.231l-78.653 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m244.322 463.933-4.177 25.359"
          transform="matrix(.90062 0 0 .93083 20.714 25.087)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M362.326 446.604s-22.326 4.07-38.114 5.334c-19.958 1.596-47.967 4.199-68.508 3.045-20.824-1.169-48.893-4.228-68.674-10.843-18.249-6.102-41.817-17.836-56.627-30.12-12.47-10.343-26.079-27.892-33.735-42.17-8.967-16.721-15.96-41.557-19.277-60.24-3.035-17.097-3.024-40.666-2.41-57.831.517-14.441 5.977-32.242 10.166-45.624 4.518-14.43 14.946-27.877 18.773-47.045"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M421.307 383.709s16.848 26.018 22.575 37.967c5.895 12.298 14.009 29.08 15.906 42.586 1.357 9.663 5.376 28.258-3.88 31.344-18.708 6.236-51.662-16.868-60.408-22.986-10.02-7.01-21.72-18.544-30.348-27.209-5.334-5.357-16.743-18.836-16.743-18.836"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M105.742 51.335s-8.05 1.49-14.478 3.611c-7.328 2.419-10.715 4.116-13.046 5.913-4.54 3.5-8.067 6.005-7.552 12.586.156 1.998 3 8.53 7.552 9.44 6.68 1.336 9.577-.562 16.81-.176 11.024.59 19.294 4.706 19.294 4.706"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M124.872 44.403s-4.886-3.812-10.859-2.645c-2.852.558-6.715 2.024-7.42 5.302-.748 3.475 1.257 6.136 2.609 6.416 3.063.635 3.408.866 6.525.596 2.66-.23 7.196.882 7.196.882M270.77 330.084s14.866 4.547 18.88-11.51c2.54-10.164-2.168-27.225-7.025-37.632-7.025-15.053-20.07-32.112-20.07-32.112"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M104.195 160.095c2.22-8.065 1.25-28.305 5.917-58.643 4.22-27.44 16.403-55.008 21.055-63.713 4.33-8.101 13.105-18.997 20.88-23.89 9.267-5.833 22.786-8.558 33.73-8.165 11.473.411 27.278 3.392 37.356 8.89 9.769 5.329 18.95 14.292 24.87 23.715 5.45 8.675 8.679 21.345 9.145 31.579.594 13.043-3.616 33.28-6.449 46.024-3.603 16.21-10.104 32.756-10.104 32.756"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M217.648 149.455c24.351-1.342 48.844 3.345 64.975 10.662 17.349 7.869 40.46 19.56 54.762 32.143 17.967 15.808 37.275 42.127 48.81 63.095 10.618 19.303 19.345 47.756 25 69.048 4.555 17.15 10.112 58.28 10.112 58.28"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M245.97 369.844c4.36 3.033 14.582 5.936 20.497.455 7.628-7.068 5.864-19.93 5.621-28.142-.275-9.289-4.678-20.279-8.046-28.94-3.239-8.328-11.423-22.091-11.423-22.091M158.219 214.042s-16.91 19.882-20.646 39.724c-2.025 10.754-.705 26.977 1.045 35.734 2.953 14.778 9.148 30.888 15.485 44.561 5.76 12.425 12.327 24.494 18.924 33.893 8.324 11.86 17.358 23.283 26.088 32.477 7.62 8.025 15.853 16.343 23.957 20.839 4.69 2.601 13.163 5.146 16.916 4.451 5.345-.989 8.319-9.272 9.793-14.244 2.032-6.855.45-16.754-.619-23.824-1.667-11.038-5.168-25.142-10.065-35.825-3.685-8.042-5.08-12.22-9.353-19.965"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M50.335 152.973c10.553 6.273 24.19 25.712 21.98 40.85-1.472 8.366-14.799 5.359-22.755 1.821-8.323-3.701-13.391-12.197-13.391-12.197"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M35.496 146.224c13.017 8.01 37.943 17.321 56.7 1.874 7.967-6.56-11.782-28.43-29.667-38.314-6.793-3.754-20.541-8.002-28.751-6.528-7.46 1.339-16.414 7.832-21.161 13.739-8.359 10.4-8.533 27.04-9.945 40.31-1.507 14.151 2.547 36.88 7.034 50.386 4.75 14.298 18.012 32.213 30.585 41.427 16.997 12.455 33.09 16.869 33.09 16.869"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M44.882 192.743s8.097 19.566-1.581 20.99c-16.132 2.375-26.09-14.668-26.09-14.668"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M146.565 51.487s7.493 2.372 9.66 6.322M146.05 51.36s7.626-2.23 12.31 1.064"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M189.535 28.706q14.96 12.412 5.093 31.827M88.385 151.094c10.751 6.997 15.818 8.553 14.563 7.046"
        />
      </g>
      <g>
        <PigeonBlinkFasterAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.4091 .38981 -.36098 1.30488 153.535 55.659)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.98245 .17834 -.19017 1.0476 151.535 51.659)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.778"
          ry="11"
          transform="matrix(.86454 .24998 -.27777 .96065 151.835 51.703)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9638 .26663 -.21743 .78595 152.716 49.325)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonBWatch({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(5 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 226.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m307.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m220.008 111.131-74.936 11.906-69.334 23.811-2.1 32.216-8.405 35.717-7.003 42.02-.101 34.23 4.707 33.89 16.004 38.597 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 26.36-4.707 38.597 19.77 33.89 10.355 22.887.246 9.104-9.805-1.4-23.11-14.708-32.917-23.414-26.67-15.063-12.239-11.296-48.952-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-35.285-4.42 7.003-39.919Z"
      />
      <path
        fill="#b7b0ba"
        d="M163.28 161.556q11.85-4.478 48.324-9.805l4.202-19.61-74.936 6.303-70.734 58.829-5.603 25.212-1.693 24.359s18.351-27.42 30.237-38.376c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.26-15.537 35.917-19.564Z"
      />
      <path
        fill="#a195a6"
        d="M169.584 151.05c12.63-3.604 43.421-3.501 43.421-3.501l8.404-51.125-84.741 19.61-61.63 36.417-1.4 30.115-10.506 43.42q16.748-20.982 25.213-28.713c9.215-8.417 22.314-18.371 33.616-24.512 12.325-6.696 32.524-17.401 47.623-21.71Z"
      />
      <path
        fill="#998d9c"
        d="M132.466 153.151c10.939-5 26.052-10.813 37.818-13.306 12.99-2.752 44.122-3.502 44.122-3.502l7.003-51.125-79.138 8.405-67.233 28.013-.7 49.724-7.004 32.916q20.352-23.43 30.815-31.515c9.383-7.25 23.533-14.68 34.317-19.61Z"
      />
      <path
        fill="#959195"
        d="M135.267 139.845c11.588-4.68 27.603-9.787 39.92-11.906 12.232-2.104 41.32-2.1 41.32-2.1l6.303-46.924-3.502-36.417L202.5 17.285 177.988 3.98h-35.017l-22.41 3.502-18.91 13.306-14.007 23.111-6.303 33.617-4.902 47.623-4.202 59.529q15.581-18.399 23.811-24.512c10.637-7.9 26.934-15.348 39.22-20.31Z"
      />
      <path
        fill="#4a4a4a"
        d="M83.008 61.467S56.737 53.06 52.07 55.239c-5.982 2.793-16.154 5.683-18.22 11.953-.626 1.904 1.918 11.838 2.573 11.76 6.765-.8 17.073-.331 22.798 2.122 7.081 3.035 20.346 13.08 20.346 13.08"
      />
      <path
        fill="#f1f1f1"
        d="M86.736 52.04c-.604-1.729-7.407-5.247-13.027-2.914-2.717 1.128-5.49 2.059-6.55 5.24-1.124 3.372 5.426 5.842 6.8 5.71 4.517-.432 11.704 1.48 11.704 1.48"
      />
      <path
        fill="#ddd"
        d="m291.443 403.173-13.306-25.212-14.707-15.408 8.404-6.303s28.021 10.074 29.004 10.355c10.444 2.984 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.662-26.99q-55.61-6.808-79.689-3.799c-19.981 2.498-50.02 12.148-64.71 25.082-13.512 11.897-25.16 26.761-30.534 43.944-5.587 17.864-5.935 74.059-5.935 74.059l21.01 42.72 40.62 33.617 61.63 25.913 60.229 7.703 7.003-15.407Z"
      />
      <path
        d="M75.39 148.897s3.903 24.254 7.628 34.03c2.855 7.492 12.99 23.353 12.99 23.353-.291-1.107-10.487 7.67-11.797 17.052-2.145 15.352 7.69 54.284 9.069 59.798.776 3.104 10.86 34.748 10.86 34.748l17.603 32.857 36.964 29.924 50.46 25.816 58.087 13.495 22.883-4.693V400.02s25.118 9.224 56.914 14.669c7.748 1.327 23.568 29.416 23.568 29.416l-65.446 10.14-62.681-4.61-72.82-15.67-45.168-20.28-32.262-27.652-20.28-37.793-12.904-44.246.922-41.48 6.452-41.48 8.959-72.138Z"
        opacity=".06"
      />
      <path
        d="M66.59 208.158s2.658 52.19 7.05 74.076c3.398 16.926 9.04 39.675 17.074 54.955 8.912 16.949 24.294 38.492 39.483 50.153 21.555 16.55 57.017 28.589 83.232 35.748 24.285 6.632 58.06 10.46 83.233 10.67 16.043.135 57.226-6.595 57.226-6.595l17.55 16.303-27.032 6.008-57.068 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-4.284-49.928 8.801-60.434Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M322.511 207.103c30.535 22.827 58.278 67.108 75.712 109.881M290.723 204.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M294.255 230.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M180.837 16.785c26.842 13.086 31.847 47.919 30.536 70.54"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M185.028 35.026c11.749 13.328 18.636 26.497 19.19 41.348"
        />
      </g>
      <path
        fill="#555"
        d="M46.702 69.034c3.799.403 9 2.462 12.547 1.689 2.067-.45 5.38-1.289 6.515-3.137 1.896-3.084-.724-11.823-.724-11.823l-25.335 3.62s-6.642 5.394-5.068 8.686c2.655 5.55 8.463.583 12.065.965Z"
      />
      <path
        fill="#ccc"
        d="M196.151 399.695s14.026.446 21.306-6.834c7.236-7.236 6.318-16.318 6.363-22.427.074-10.163-4.907-21.355-8.512-30.858-2.642-6.96-7.9-14.654-7.9-14.654s6.489-4.844 8.04-7.638c2.479-4.467 2.266-12.345 1.007-17.297-1.873-7.37-14.676-20.893-14.676-20.893s8.507-3.62 10.452-6.834c2.802-4.627 2.001-12.814.804-18.09-.932-4.106-8.04-14.874-8.04-14.874l-19.698-7.638s5.08 9.052 7.638 14.472c2.353 4.985 4.296 10.25 2.01 15.276-1.769 3.89-18.09 12.864-18.09 12.864s11.68 12.814 13.544 18.583c1.438 4.451 3.745 8.39 1.985 12.724-1.698 4.181-13.748 8.435-13.748 8.435s7.899 15.57 10.244 22.438c3.093 9.053 4.731 14.404 4.093 23.95-.333 4.965-2.812 13.09-6.367 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#ccc"
        d="M213.021 405.953s15.503-2.828 23.372-7.191c8.33-4.618 12.136-8.658 12.136-15.732 0-9.888-4.496-19.189-8.54-26.069-4.074-6.931-12.135-19.327-12.135-19.327l15.282 7.64s3.754-9.518 2.247-20.675c-.78-5.779-7.149-14.445-10.338-19.327-4.363-6.68-16.63-19.327-16.63-19.327l9.214.471s2.664-8.364 1.714-12.585c-.893-3.97-4.2-9.376-6.285-12.871-2.755-4.618-11.623-16.856-11.623-16.856l17.833 8.225 14.18 16.104 6.214 16.998-9.673 10.402 16.667 14.32 7.095 16.133-1.031 13.551-8.974 14.026 15.904 19.898 9.22 20.911-3.306 13.179-12.652 9.789-32.811-4.495-17.08-7.192Z"
      />
      <path
        fill="#9d9d9d"
        d="M226.365 410.147s21.711-5.108 27.36-10.752c5.323-5.318 5.775-19.173 2.103-26.81-7.64-15.887-15.839-27.76-15.839-27.76l11.189 6.067s2.886-10.473 2.215-21.044c-.684-10.767-10.357-25.96-18.949-34.66-5.105-5.17-17.6-10.196-17.6-10.196l17.23 6.7s5.008-11.61 2.098-20.35c-2.839-8.528-17.697-24.378-17.697-24.378l23.07 16.976 11.848 18.283-3.323 18.275 5.63 10.067 10.983 14.3 8.307 22.704-9.7 15.029 9.642 8.533 11.134 20.187 4.984 19.936-8.86 8.307-28.797-2.215-27.028-7.2ZM185.376 394.651s20.19-.493 26.168-5.878c4.468-4.026 6.35-12.983 6.407-18.997.087-9.2-5.007-20.628-9.256-29.241-3.582-7.262-9.595-18.079-9.595-18.079s8.06-2.787 10.467-5.352c3.163-3.368 3.478-6.405 2.777-12.812-1.058-9.668-19.007-27.207-19.007-27.207s10.092-2.415 13.668-6.432c3.754-4.218 2.8-12.777 0-20.1-2.187-5.72-7.638-12.462-7.638-12.462l-7.236-4.422s3.812 8.696 6.834 14.07c2.483 4.417 2.918 12.497.804 16.482-2.782 5.24-16.875 11.163-16.875 11.163s16.2 16.195 17.376 22.575c.827 4.485.297 7.529-1.97 11.486-2.158 3.768-13.21 7.038-13.21 7.038s6.319 8.92 8.25 15.404c2.805 9.41 6.924 22.79 5.953 32.72-.501 5.12-2.632 12.328-6.28 15.954-4.53 4.505-19.864 7.684-19.864 7.684l12.227 6.406Z"
      />
      <path
        fill="#ccc"
        d="M419.587 365.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.83 370.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.645 17.961-1.021 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        d="m345.664 418.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.505 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M423.46 371.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.266 401.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.993 465.89 37.482 6.317M251.085 459.994l63.593 5.896M313.414 454.94l.843 10.108M239.69 477.445l-71.28-11.441M240.135 480.842l35.376 2.526M239.714 480.842l-78.654 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.744 24.697)"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M85.276 51.633s-4.439-4.325-10.503-3.82c-2.896.242-6.897 1.275-7.957 4.456-1.124 3.373.577 6.237 1.89 6.664 2.975.967 3.293 1.234 6.42 1.307 2.67.063 7.056 1.666 7.056 1.666"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M373.209 443.565s-32.91 6.72-48.697 7.983c-19.958 1.597-48.101 1.344-67.857-.798-20.95-2.271-50.444-7.58-70.91-12.602-12.714-3.12-29.457-8.504-41.317-14.048-12.094-5.653-30.538-17.678-40.251-26.837-11.408-10.756-20.567-25.214-27.51-39.272-7.153-14.48-12.868-32.625-15.6-48.543-1.509-8.8-3.654-23.157-3.785-32.255-.232-16.02 4.186-39.638 6.993-55.943 2.333-13.558 7.684-25.967 9.323-39.626 2.515-20.958 1.713-53.461 3.808-74.465 1.548-15.519 3.877-35.354 7.81-50.446 2.762-10.604 6.286-24.73 19.192-37.663 7.34-7.357 20.32-13.274 30.593-14.855 15.106-2.325 39.63-1.776 54.433 5.299 10.05 4.803 20.636 16.738 26.012 26.494 4.873 8.841 6.955 22.478 7.708 32.756.868 11.86-1.56 26.805-3.348 39.886-1.758 12.865-7.127 42.692-7.127 42.692"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M187.399 154.706s21.788-3.175 33.578-2.212c16.322 1.334 37.904 6.865 52.915 13.412 18.669 8.142 41.455 23.099 57.293 35.903 18.253 14.756 37.65 35.665 50.408 55.366 12.287 18.973 24.767 49.748 31.442 71.343 3.818 12.354 10.058 41.91 10.058 41.91"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M66.165 54.51s-16.046 1.385-21.29 3.127c-5.267 1.749-8.686 3.192-11.393 8.82-.868 1.806-.251 9.614 3.308 11.393 6.093 3.047 13.75 1.616 20.064 4.011 10.321 3.915 22.24 11.75 22.24 11.75"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M111.225 48.602s7.607 1.979 9.975 5.81M111.022 48.531s7.499-2.623 12.348.423"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M264.153 361.95c5.985-1.121 7.657-6.135 8.626-9.291 1.64-5.344.597-13.28-2.48-20.956-4.115-10.27-12.752-20.538-18.508-25.054-8.595-6.744-11.775-12.8-37.875-22.776"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M246.586 302.562c5.881-4.962 6.354-13.668 5.563-18.764-.96-6.197-4.172-13.182-9.057-19.137-5.56-6.777-13.382-11.935-19.513-15.828-11.713-7.436-38.179-17.921-38.179-17.921M102.284 280.397s-3.69 25.838 3.737 44.612c4.026 10.175 13.8 23.191 19.953 29.663 10.383 10.923 23.755 22.008 36.875 29.423 11.04 6.24 23.707 12.458 34.301 16.887 13.369 5.588 28.874 9.973 41.163 13.09 10.727 2.72 21.428 4.4 33.466 5.29 4.339.321 12.278-.127 15.081-2.717 3.993-3.689 4.062-14.344 2.474-19.374-2.045-6.481-8.367-14.757-12.663-20.653-6.547-8.986-18.102-17.051-28.807-25.468-13.826-10.87-24.118-15.377-24.118-15.377"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M422.739 370.437s24.912 19.718 33.45 32.354c8.765 12.97 19.64 32.18 20.885 47.783.487 6.1.246 15.93-4.397 19.915-6.533 5.607-20.072 2.748-28.635 1.863-13.836-1.429-29.439-8.557-42.032-14.465-11.245-5.275-24.171-12.282-34.457-19.246-8.214-5.561-21.395-19.248-21.395-19.248"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M138.11 27.59c10.706 9.454 5.871 25.088 5.871 25.088"
        />
      </g>
      <g>
        <PigeonBlinkFastAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.2969 .42815 -.40895 1.23873 117.406 51.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.88553 .16045 -.17828 .98398 117.406 48.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.85459 .28213 -.3135 .9496 117.406 48.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9496 .3135 -.25564 .77436 118.587 46.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonBGuess({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 224.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m207.359 129.665-42.515 6.511-64.077 6.308-14.45-29.478-18.496-12.716H48.747l-21.386 11.56-12.716 24.276-12.138 43.928 6.936 53.755 22.542 35.258L57.736 283.4l5.1 41.52 16.004 38.598 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 16.62-2.801 37.307 22.823 39.503 15.362h28.53l8.339-12.29-4.39-28.968-21.068-37.746-30.486-26.573-9.398-57.836-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-43.948-3.392 3.017-22.413Z"
      />
      <path
        fill="#ddd"
        d="m289.564 405.437-10.284-26.444-13.957-16.895 5.877-8.08s28.307 12.39 29.074 12.587c13.951 3.582 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.661-26.99q-54.368-5.981-77.624-4.237c-22.554 1.692-52.149 8.365-67.662 20.299-14.66 11.277-32.421 34.392-35.523 53.002-2.819 16.915.28 35.644 1.128 67.662.652 24.614 27.12 50.889 27.12 50.889l46.278 34.524 69.048 24.24 37.463 4.408 8.08-14.691Z"
      />
      <path
        fill="#4a4a4a"
        d="M61.606 73.549s-11.267 1.949-17.246 3.58c-6.696 1.826-12.11 7.555-12.11 7.555l-12.37 18.681c-.628 1.904 3.99 10.473 4.646 10.396l18.725-7.129 21.08-9.056"
      />
      <path
        fill="#b7b0ba"
        d="M163.28 161.556q11.85-4.478 41.062-8.616l2.155-21.12-65.627 6.624-74.303 59.913-5.177 20.06-3.236 33.003s23.037-31.997 34.923-42.952c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.26-15.537 35.917-19.564Z"
      />
      <path
        fill="#f1f1f1"
        d="M60.654 64.505s-9.54-1.797-15.16.536c-2.717 1.128-5.019 4.64-6.08 7.821-1.123 3.373 3.587 4.174 4.961 4.043 4.517-.432 15.328-2.88 15.328-2.88"
      />
      <path
        fill="#a195a6"
        d="M174.161 147.726c11.357-2.918 31.474-3.406 31.474-3.406l2.155-39.222-71.122 10.935-38.432 14.75 5.647 30.12-13.805 6.902-23.216-6.274 3.137 20.706-9.412 43.297c11.165-13.989 19.86-24.38 26.982-30.12 11.228-9.048 26.368-21.105 38.904-28.236 12.826-7.297 33.396-15.78 47.688-19.452Z"
      />
      <path
        fill="#998d9c"
        d="M132.747 153.373c10.409-4.894 24.58-11.13 35.767-13.804 12.698-3.037 38.845-3.87 38.845-3.87l.43-42.669-65.518.593-47.173 28.376 8.158 38.277-14.433 6.274-16.785-4.118-7.059 41.767c13.568-15.62 24.619-26.109 33.17-31.438 10.56-6.581 23.831-14.325 34.598-19.388Z"
      />
      <path
        fill="#959195"
        d="M135.267 139.845c11.588-4.68 27.603-9.787 39.92-11.906 12.232-2.104 32.41-2.85 32.41-2.85l-1.74-49.92-9.288-34.829-26.173-28.778L132.747.894 98.863 7.797 73.27 26.754 61.606 55.937l.82 41.767 11.338 4.843 18.824 18.824 9.413 27.61-3.765 17.57-28.865-5.02-.216 23.883c7.305-8.626 15.276-18.864 15.276-18.864l14.432.628s3.358-11.475 5.02-12.55c0 0 19.099-9.821 31.384-14.783Z"
      />
      <path
        d="M83.713 182.512c-.748 12.382-4.592 25.078-8.354 36.363-5.267 15.8-1.379 31.861 3.573 48.084 4.773 15.639 22.336 39.492 22.336 39.492l8.846 28.792 30.598 32.978 55.077 32.298 53.377 16.319 36.378 1.02 4.113-7.011s26.145 5.991 42.804 9.73c7.67 1.723 31.618 24.82 31.618 24.82l-58.552 8.387-62.68-4.609-72.82-15.67-45.168-20.28-32.262-27.653-20.28-37.792-9.465-25.062-4.612-32.286-34.885-30.71-13.694-28.3s-1.825-20.083 5.934-28.299c3.287 2.465 13.237 10.499 13.237 10.499l10.042-5.478 3.651-11.867 15.063 3.651s9.27-14.695 5.477-22.822c-2.65-5.679-7.76-22.366-7.76-22.366L69 159.761l15.063 6.39q.14 8.265-.35 16.36Z"
        opacity=".06"
      />
      <path
        d="m42.069 196.277 14.606 2.282 6.39-13.237-6.846-30.582 11.867 6.847.913 17.801-4.45 27.202s-4.109 23.222-6.389 31.941.884 21.357 7.955 40.36c5.943 15.971 16.7 42.348 24.828 57.325 8.31 15.312 22.931 37.695 37.13 47.79 22.812 16.221 56.046 32.58 83.215 39.315 24.193 5.998 57.648 9.624 82.56 10.483 15.706.542 51.517-4.606 51.517-4.606l15.98 14.96-16.94 5.318-57.067 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-3.414-33.636-11.835-12.856s-11.845-16.413-14.34-23.26c-5.767-15.83-5.933-37.429-5.933-37.429l10.042 1.37 5.477-16.432Z"
        opacity=".06"
      />
      <path
        d="m57.132 199.928 6.39-16.432-5.477-26.93 9.585 5.477.913 25.105-11.411 60.707s-8.94-14.706-11.868-21.453c-2.12-4.886-5.477-16.888-5.477-16.888l3.651-13.694 13.694 4.108Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M326.511 214.103c30.535 22.827 58.278 67.108 75.712 109.881M294.723 211.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M298.255 237.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M154.351 19.212c28.499 8.921 40.378 37.304 41.403 64.405"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M167.38 34.957c14.544 9.142 23.471 17.746 26.67 37.137"
        />
      </g>
      <path
        fill="#ccc"
        d="M418.587 375.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M422.83 380.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.644 17.961-1.02 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        fill="#ccc"
        d="M213.02 405.953s15.504-2.829 23.373-7.192c8.33-4.618 12.136-8.657 12.136-15.731 0-9.888-4.496-19.19-8.54-26.07-4.074-6.93-12.136-19.327-12.136-19.327l15.282 7.641s3.754-9.519 2.248-20.675c-.78-5.78-7.149-14.445-10.338-19.327-4.364-6.68-16.63-19.328-16.63-19.328l9.213.472s2.664-8.364 1.715-12.585c-.894-3.971-4.2-9.377-6.285-12.872-2.756-4.617-11.624-16.856-11.624-16.856l17.834 8.225 14.179 16.105 6.215 16.997-9.673 10.403 16.667 14.32 7.094 16.132-1.03 13.552-8.974 14.025 15.904 19.899 9.219 20.91-3.305 13.18-12.652 9.788-32.811-4.494-17.08-7.192Z"
      />
      <path
        fill="#9d9d9d"
        d="M226.365 410.147s21.71-5.108 27.36-10.752c5.323-5.318 5.775-19.172 2.103-26.809-7.64-15.888-15.839-27.761-15.839-27.761l11.188 6.067s2.887-10.472 2.215-21.043c-.684-10.768-10.356-25.961-18.948-34.661-5.105-5.17-17.601-10.196-17.601-10.196l17.23 6.7s5.009-11.609 2.099-20.35c-2.84-8.528-17.697-24.377-17.697-24.377l23.07 16.975 11.847 18.283-3.322 18.275 5.63 10.068 10.983 14.299 8.307 22.705-9.7 15.028 9.641 8.534 11.134 20.187 4.985 19.936-8.861 8.306-28.797-2.215-27.027-7.199Z"
      />
      <path
        fill="#ccc"
        d="M196.15 399.695s14.026.446 21.307-6.834c7.236-7.236 6.318-16.318 6.362-22.426.075-10.164-4.906-21.356-8.512-30.858-2.641-6.962-7.9-14.654-7.9-14.654s6.49-4.845 8.04-7.638c2.48-4.467 2.266-12.346 1.008-17.298-1.873-7.37-14.676-20.892-14.676-20.892s8.506-3.62 10.452-6.835c2.801-4.627 2-12.814.804-18.09-.932-4.105-8.04-14.874-8.04-14.874l-19.698-7.638s5.08 9.052 7.638 14.472c2.353 4.986 4.296 10.25 2.01 15.276-1.77 3.89-18.09 12.864-18.09 12.864s11.68 12.814 13.543 18.583c1.438 4.451 3.746 8.39 1.986 12.724-1.698 4.181-13.749 8.435-13.749 8.435s7.9 15.571 10.245 22.438c3.092 9.054 4.73 14.405 4.092 23.95-.332 4.965-2.81 13.09-6.366 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#9d9d9d"
        d="M185.376 394.651s20.19-.494 26.168-5.879c4.469-4.025 6.351-12.982 6.408-18.996.086-9.201-5.008-20.629-9.256-29.241-3.583-7.263-9.596-18.08-9.596-18.08s8.06-2.786 10.468-5.351c3.163-3.369 3.477-6.406 2.776-12.812-1.058-9.668-19.007-27.207-19.007-27.207s10.092-2.415 13.668-6.432c3.754-4.218 2.8-12.777 0-20.1-2.186-5.72-7.638-12.463-7.638-12.463l-7.236-4.422s3.812 8.697 6.834 14.07c2.483 4.418 2.919 12.498.804 16.483-2.781 5.24-16.874 11.162-16.874 11.162s16.198 16.195 17.375 22.576c.827 4.485.297 7.528-1.97 11.486-2.158 3.768-13.21 7.037-13.21 7.037s6.32 8.92 8.251 15.404c2.805 9.411 6.923 22.791 5.952 32.721-.5 5.119-2.631 12.327-6.279 15.954-4.531 4.504-19.864 7.683-19.864 7.683l12.226 6.407Z"
      />
      <path
        fill="#ccc"
        d="M83.747 108.599s1.861 18.67-4.477 24.375c-8.483 7.635-42.126 5.64-42.126 5.64l18.158 15.461s-.324 13.23-4.275 18.384c-2.48 3.235-7.739 6.91-11.972 7.269-3.112.264-10.26-3.848-10.26-3.848l4.275 8.123s-3.598 6.006-5.986 7.696c-1.986 1.405-5.268 2.738-7.696 2.565-2.86-.203-8.55-4.275-8.55-4.275l8.978 19.239 14.536 5.558 9.406-16.247 14.964 1.71 5.986-18.811-5.13-23.943 27.79 11.116 15.282-6.288 1.493-14.923-6.965-18.903q-12.558-19.732-13.43-19.898Z"
      />
      <path
        fill="#9d9d9d"
        d="M59.303 161.827s-3.957 13.484-6.59 16.616c-2.734 3.255-7.784 4.9-11.503 6.09-8.458 2.707-13.24-9.05-13.24-9.05l5.301 15.267s-2.734 6.616-10.003 8.676c-4.725 1.338-12.827-8.124-12.827-8.124l12.4 19.667 12.826 1.283 6.413-15.392 13.681 2.566 8.978-14.537M100.505 164.073c3.217-2.928 1.492-14.426 1.492-14.426l-4.66-17.38-8.77-15.451s.368 10.375-1.99 17.41c-4.302 12.831-42.284 10.447-42.284 10.447l27.746 19.663q22.15 5.486 28.466-.263Z"
      />
      <path
        d="m344.664 428.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.504 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.46 381.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M417.266 411.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <path
        fill="#555"
        d="M37.945 94.483c5.149-3.805 10.299-2.134 12.77-4.793 1.441-1.55 1.076-3.474.413-5.768-.79-2.733-7.828-6.593-7.828-6.593l-14.491 13.2s-6.581 8.74-4.965 12.013c.8 1.621 8.41-3.851 14.101-8.059Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.374 37.482 6.317M250.785 460.478l63.593 5.896M313.115 455.424l.842 10.107M239.39 477.928l-71.28-11.44M239.835 481.325l35.376 2.527M239.414 481.325l-78.653 2.129"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.444 25.18)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M362.326 446.698s-22.326 4.07-38.114 5.334c-19.958 1.596-48.101 1.343-67.857-.798-20.95-2.271-50.444-7.58-70.909-12.602-12.715-3.12-29.457-8.504-41.318-14.048-12.094-5.654-30.537-17.679-40.25-26.838-11.408-10.756-20.568-25.214-27.511-39.271-7.153-14.48-12.868-32.625-15.599-48.543-1.51-8.8-3.763-38.333-2.986-54.77.334-30.901 10.013-63.246 10.013-63.246s4.318-13.198 2.505-28.905M65.561 98.807s-3.955-24.375-3.955-28.59c0-10.323.747-22.078 7.14-33.32 4.232-7.44 12.617-18.242 25.015-24.758 14.87-7.815 46.567-14.71 69.946-3.787 11.498 5.372 18.481 12.342 24.155 19.798 7.034 9.245 12.82 24.148 15.673 35.409 3.82 15.075 4.132 36.123 4.123 51.674-.007 11.003-2.963 36.557-2.963 36.557"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M199.725 153.033s9.163-1.019 20.953-.055c16.322 1.334 37.904 6.865 52.914 13.411 18.669 8.143 41.455 23.1 57.294 35.903 18.252 14.756 37.65 35.666 50.408 55.367 12.287 18.972 24.766 49.747 31.441 71.343 3.818 12.354 7.28 47.344 7.28 47.344M420.055 378.062s26.297 22.577 34.835 35.213c8.765 12.97 19.639 32.179 20.885 47.783.486 6.1.246 15.93-4.397 19.915-6.533 5.606-20.072 2.747-28.635 1.863-13.837-1.43-29.44-8.557-42.033-14.465-11.245-5.275-26.38-14.574-36.666-21.538-8.214-5.561-19.291-16.137-19.291-16.137"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M246.286 303.046c5.881-4.962 6.354-13.668 5.563-18.764-.96-6.197-4.172-13.182-9.058-19.137-5.56-6.777-13.381-11.936-19.512-15.828-11.713-7.437-38.179-17.922-38.179-17.922"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M263.854 362.434c5.984-1.122 7.656-6.135 8.625-9.292 1.64-5.343.597-13.28-2.48-20.956-4.115-10.27-12.751-20.537-18.507-25.054-8.596-6.744-11.775-12.799-37.876-22.776M101.984 280.881s-3.69 25.838 3.738 44.612c4.026 10.175 13.8 23.191 19.952 29.663 10.383 10.923 23.756 22.008 36.876 29.423 11.039 6.24 23.707 12.459 34.301 16.887 13.368 5.588 28.874 9.973 41.163 13.09 10.727 2.72 21.428 4.4 33.466 5.29 4.338.321 12.278-.127 15.081-2.717 3.993-3.689 4.061-14.344 2.474-19.374-2.045-6.481-8.367-14.757-12.663-20.653-6.547-8.986-18.103-17.051-28.807-25.468-13.826-10.87-24.118-15.377-24.118-15.377"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M53.728 152.203c7.6 9.642 13.168 31.037 5.675 44.375-4.376 7.28-15.05 1.027-21.207-5.13-6.441-6.441-10.12-15.738-10.12-15.738"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M39.508 141.736c14.89 11.61 46.51 36.154 61.684 20.395 6.194-6.433-6.634-39.398-15.346-48.956-5.229-5.736-13.787-13.806-25.192-12.971M27.68 113.92c-4.762 4.904-8.825 16.224-13.205 28.173-4.898 13.362-11.239 32.55-11.896 46.767-.697 15.05 2.374 29.084 7.232 43.346 5.08 14.911 10.475 23.478 17.625 31.67 7.13 8.17 28.916 21.687 28.916 21.687"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M42.985 195.894s-3.374 19.914-12.919 17.771c-15.91-3.571-19.089-23.052-19.089-23.052"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M91.414 63.087s6.148-5.03 11.747-3.828M91.628 63.084s7.825-.743 11.362 2.046"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M46.557 74.336c-8.126 5.057-12.657 9.405-16.133 13.7-3.74 4.619-8.786 11.365-8.294 17.591.158 1.998 3.82 8.043 7.791 7.79 6.798-.432 7.79-3.769 17.09-8.292 7.84-3.815 18.416-6.302 18.416-6.302"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M59.555 63.006s-5.544-2.77-11.168-.443c-2.685 1.111-6.181 3.313-6.224 6.666-.044 3.555 2.448 5.765 3.828 5.773 3.129.016 3.513.173 6.515-.708 2.561-.753 7.227-.56 7.227-.56"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M115.148 34.52c12.705 6.525 11.87 22.868 11.87 22.868"
        />
      </g>
      <g>
        <PigeonBlinkFasterAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36567 -.01533 .01465 1.3044 98.406 61.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.88973 -.13525 .15028 .98864 96.406 59.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8999 -.0101 .01123 .99994 96.406 59.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99994 -.01123 .00915 .81542 96.587 58.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonCFail({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 224.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m198.603 116.244-52.68 4.79-90.396 20.353-6.585-20.354-4.19-31.728 13.17-6.585 2.993-8.98-8.381-6.585 18.558-6.585 5.388-8.98-4.79-8.38 24.545-5.388 4.789-8.381-10.177-10.177-26.939-7.783-23.347 15.565-22.749 37.715-15.564 56.87 2.993 32.926 20.952 41.906L58.52 219.81l-.784 63.59 5.1 41.52 16.004 38.598 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 16.62-2.801 37.307 22.823 39.503 15.362h28.53l8.339-12.29-4.39-28.968-21.068-37.746-30.486-26.573-9.398-57.836-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-43.948-3.392-5.739-35.834Z"
      />
      <path
        fill="#ddd"
        d="m278.23 424.578-9.293-32.171-12.154-17.158 9.294-22.163s33.43 13.322 34.197 13.519c13.951 3.582 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.661-26.99q-54.368-5.981-77.624-4.237c-22.554 1.692-52.149 8.365-67.662 20.299-14.66 11.277-27.647 36.117-30.749 54.727-2.82 16.915-5.852 32.324-5.004 64.342.651 24.614 20.732 55.763 20.732 55.763l46.47 40.036 62.912 27.166 35.746 8.58 12.154-11.44Z"
      />
      <path
        fill="#4a4a4a"
        d="m45.115 95.449-12.55 4.183-11.854 9.762-9.509 16.55s7.808 10.024 8.463 9.946l14.991-8.367 14.991-6.973"
      />
      <path
        fill="#b7b0ba"
        d="M163.28 161.556q11.85-4.478 41.062-8.616l-4.668-20.566-58.804 6.07-74.303 59.913-5.177 20.06-3.236 33.003s23.037-31.997 34.923-42.952c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.26-15.537 35.917-19.564Z"
      />
      <path
        fill="#f1f1f1"
        d="M45.144 88.736s-1.534-1.538-7.154.795c-2.717 1.128-6.889 1.588-7.95 4.77-1.123 3.372.39 4.885 1.763 4.754 4.518-.432 14.136-3.43 14.136-3.43"
      />
      <path
        fill="#a195a6"
        d="M174.161 147.726c11.357-2.918 27.699-3.33 27.699-3.33l-3.825-37.158-61.367 8.795-38.432 14.75-36.81 31.098 2.186 20.765-3.025 42.888c11.165-13.989 19.86-24.38 26.982-30.12 11.228-9.048 26.368-21.105 38.904-28.236 12.826-7.297 33.396-15.78 47.688-19.452Z"
      />
      <path
        fill="#998d9c"
        d="M132.747 153.373c10.409-4.894 24.58-11.13 35.767-13.804 12.698-3.037 33.346-5.01 33.346-5.01l-8.197-44.26-51.392 3.324-47.173 28.376-34.218 36.057 1.64 45.9c13.567-15.62 27.078-25.866 35.63-31.195 10.56-6.581 23.83-14.325 34.597-19.388Z"
      />
      <path
        fill="#959195"
        d="M135.267 139.845c11.588-4.68 27.603-9.787 39.92-11.906 12.232-2.104 25.58-3.215 25.58-3.215l-8.743-48.633L172.9 40.027l-24.59-21.31-27.321-8.744-37.704 2.732 15.3 12.022 1.64 10.928-27.869 7.104 3.825 11.475-10.382 8.197-12.021 4.371 8.743 6.01-3.279 10.383-14.754 4.918 3.825 30.054 10.929 32.786 6.557 34.425c7.305-8.626 25.641-22.953 38.085-30.75 8.82-5.526 19.099-9.821 31.384-14.783Z"
      />
      <path
        d="M79.945 193.832c-3.726 10.647-3.194 17.567-4.586 25.043-3.048 16.374-1.379 31.861 3.573 48.084 4.773 15.639 20.567 38.955 20.567 38.955l6.282 30.885 31.932 40.307 45.018 30.885 47.113 19.892 39.26 7.33 8.9-10.47-3.141-17.799s11.77 3.618 16.228 4.712c12.789 3.14 24.71 5.182 41.37 8.922 7.67 1.722 31.618 24.818 31.618 24.818l-58.552 8.388-62.68-4.609-72.82-15.67-45.168-20.28-32.262-27.653-20.28-37.792-9.465-25.062-4.612-32.286.168-70.271-34.983-28.025s-3.253-16.246-3.886-26.72c-.604-10 6.022-32.847 6.022-32.847l24.088-10.95-4.38-32.299 14.026-7.993s1.117 20.607 2.824 27.677c3 12.418 6.635 30.91 9.602 43.491 2.659 11.27 8.224 41.337 8.224 41.337Z"
        opacity=".06"
      />
      <path
        d="M36.73 202.23s-5.22-17.544-6.114-28.845c-1.04-13.15 0-42.47 0-42.47l17.745-9.482 15.062 52.09-1.255 30.754-3.765 35.145s.641 20.466 7.712 39.47c5.943 15.97 16.7 42.347 24.828 57.324 8.31 15.312 22.931 37.695 37.13 47.79 22.812 16.221 56.046 32.58 83.215 39.315 24.193 5.998 57.648 9.624 82.56 10.483 15.706.542 51.517-4.606 51.517-4.606l15.98 14.96-16.94 5.318-57.067 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-3.414-33.636-.883-66.173s-21.446-15.28-21.045-16.481Z"
        opacity=".06"
      />
      <path
        d="M36.73 154.514c-.495-8.265.382-27.597.382-27.597l12.928-7.394 11.772 41.438 2.826 26.37-5.651 32.49s-14.806-23.698-18.128-34.567c-2.475-8.102-3.572-21.452-4.129-30.74Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M326.511 214.103c30.535 22.827 58.278 67.108 75.712 109.881M294.723 211.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M298.255 237.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M130.716 27.057c29.243 6.052 43.878 33.116 47.586 59.982"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M144.454 41.373c15.378 7.656 25.115 15.332 30.22 34.31"
        />
      </g>
      <path
        fill="#ccc"
        d="M418.587 375.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M422.83 380.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.644 17.961-1.02 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        fill="#ccc"
        d="M51.507 17.207s7.411.482 8.708 8.909c.46 2.987-8.708 14.346-8.708 14.346l-3.316 1.566s4.837 7.356 5.23 10.985c.427 3.935-2.987 12.85-2.987 12.85l-11.136.227s2.865 7.196 3.139 10.462c.317 3.782-2.51 13.275-2.51 13.275l16.633-5.952c-.018 2.433 6.265-11.81 4.708-9.939-1.835 2.204-5.231-7.846-5.231-7.846l12.03-6.277 9.416-3.661-7.323-12.554 27.2-5.754 3.138-10.985-15.692-10.985-20.923-4.184q-11.732 4.905-12.376 5.517Z"
      />
      <path
        fill="#9d9d9d"
        d="M51.595 40.423s5.778 4.483 7.545 9.584-3.605 14.056-3.605 14.056L43.357 66.57s3.238 5.53 3.97 8.426c1.403 5.543-3.97 14.497-3.97 14.497l16.238-7.682 1.817-8.632-9.995-7.27 16.356-3.635 8.178-9.995-9.541-9.087M99.56 33.722c.977-4.24-4.337-12.468-4.337-12.468l-17.27-6.367s-11.276-2.503-16.065-1.108c-9.778 2.849-5.279 1.572-5.279 1.572s6.239.753 8.437 5.124c5.89 11.71-9.995 19.991-9.995 19.991l18.034 2.659q25.128-3.567 26.474-9.403Z"
      />
      <path
        d="m344.664 428.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.504 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.46 381.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M417.266 411.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <path
        fill="#555"
        d="M27.134 119.476c4.77-4.269 10.054-3.086 12.267-5.964 1.29-1.677.747-3.559-.127-5.781-1.042-2.647-8.41-5.833-8.41-5.833L17.67 116.392s-5.738 9.316-3.824 12.423c.949 1.54 8.014-4.619 13.289-9.339Z"
      />
      <path
        fill="#ccc"
        d="M195.151 409.695s14.026.446 21.306-6.834c7.236-7.236 6.318-16.318 6.363-22.427.074-10.163-6.345-21.975-9.95-31.478-2.642-6.96-8.674-15.345-8.674-15.345s9.118-4.076 11.342-7.673c3.132-5.066 3.419-14.324 2.16-19.276-1.873-7.37-16.838-24.425-16.838-24.425s8.73-4.125 10.675-7.339c2.801-4.628 2.531-13.072 1.334-18.348-.932-4.106-9.007-17.68-9.007-17.68l-18.348-9.007s7.728 15.805 8.34 20.682c.59 4.692 1.608 9.551-.465 13.801-2.69 5.518-16.206 13.055-16.206 13.055s10.684 13.106 14.002 18.18c2.95 4.51 6.35 12.207 4.455 16.483-2.453 5.534-16.798 12.206-16.798 12.206s6.693 15.313 9.038 22.18c3.093 9.053 4.731 14.404 4.093 23.95-.333 4.965-2.812 13.09-6.367 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#9d9d9d"
        d="M183.853 405.128s20.19-.493 26.168-5.878c4.468-4.026 6.35-12.983 6.407-18.997.087-9.2-5.007-20.628-9.256-29.241-3.582-7.262-10.842-20.582-10.842-20.582s11.183-3.476 13.591-6.04c3.163-3.37 4.084-9.445 3.398-13.592-1.762-10.65-19.505-30.236-19.505-30.236s10.644-4.255 14.22-8.272c3.754-4.218 2.642-11.704-.756-19.254-2.512-5.584-9.754-18.17-9.754-18.17l-7.006-2.669s7.178 11.965 8.674 17.681c1.245 4.759 3.028 12.387.913 16.371-2.78 5.24-16.734 12.612-16.734 12.612s13.907 14.014 16.734 21.744c1.714 4.685 4.908 11.703.755 16.611-2.759 3.261-15.479 9.061-15.479 9.061s4.505 9.603 6.437 16.087c2.804 9.41 6.923 22.79 5.952 32.72-.501 5.12-2.632 12.328-6.28 15.954-4.53 4.505-19.864 7.684-19.864 7.684l12.227 6.406Z"
      />
      <path
        fill="#ccc"
        d="M202.348 414.942s15.566-2.462 23.536-6.638c8.436-4.42 12.337-8.369 12.504-15.44.233-9.886-4.042-19.29-7.922-26.264-3.91-7.026-11.676-19.609-11.676-19.609l15.476 9.613s3.577-11.214 2.334-22.403c-.644-5.796-4.862-16.046-7.935-21.002-4.204-6.78-12.134-20.536-12.134-20.536l7.934 3.734s2.453-7.262 2.415-10.491c-.061-5.267-2.835-12.216-4.337-17.013-2.188-6.993-9.98-23.163-9.98-23.163l17.036 11.93 7 16.801 5.226 20.857-6.626 9.48 13.035 15.4 6.711 16.296-1.35 13.523-9.303 13.81 15.43 20.269 8.722 21.123-3.615 13.096-12.88 9.488-32.696-5.269-16.905-7.592Z"
      />
      <path
        fill="#9d9d9d"
        d="M215.761 420.395s21.344-3.697 27.16-8.98c5.48-4.978 6.762-18.823 3.67-26.71-6.435-16.41-13.655-28.835-13.655-28.835l10.47 6.807s3.433-10.3 3.426-20.929c-.007-10.827-7.588-27.711-15.382-36.985-4.63-5.51-14.172-11.189-14.172-11.189l14.545 8.578s4.175-11.174 1.888-20.119c-2.232-8.724-13.45-32.095-13.45-32.095l22.751 22.75 4.103 20.14-8.206 13.8 11.19 11.934 9.696 13.427 6.882 28.916-10.312 14.416 8.82 9.176 9.558 20.942 3.615 20.29-9.089 7.74-27.763-4.1-25.745-8.974Z"
      />
      <path
        fill="none"
        stroke="#555"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="10"
        d="m312.694 466.374 37.482 6.317M250.785 460.478l63.593 5.896M313.115 455.424l.842 10.107M239.39 477.928l-71.28-11.44M239.835 481.325l35.376 2.527M239.414 481.325l-78.653 2.129"
      />
      <path
        fill="none"
        stroke="#555"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="10"
        d="m237.874 457.43 2.271 31.862"
        transform="matrix(.90062 0 0 .93083 23.444 25.18)"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M362.326 446.698s-22.326 4.07-38.114 5.334c-19.958 1.596-48.101 1.343-67.857-.798-20.95-2.271-50.444-7.58-70.909-12.602-12.715-3.12-29.457-8.504-41.318-14.048-12.094-5.654-30.537-17.679-40.25-26.838-11.408-10.756-20.568-25.214-27.511-39.271-7.153-14.48-12.868-32.625-15.599-48.543-1.51-8.8-3.763-38.333-2.986-54.77.334-30.901 4.413-43.958 5.429-62.859.24-4.46.623-14.887-1.394-24.973-2.386-11.93-10.189-39.096-13.314-52.448-3.038-12.982-3.409-24.056-3.575-26.297M80.783 14.663c6.273-3.804 27.494-7.633 52.445-1.048 12.27 3.24 22.944 10.215 29.855 16.542 8.568 7.845 17.681 21.436 22.494 32.009 6.442 14.154 10.497 34.81 13.257 50.115 1.952 10.828 3.549 39.616 3.549 39.616"
      />
      <path
        fill="none"
        stroke="#555"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M71.932 73.088s4.127-6.788 9.808-7.515M72.134 73.013s7.133-3.301 11.396-1.845"
      />
      <path
        fill="none"
        stroke="#9e9e9e"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M199.725 153.033s9.163-1.019 20.953-.055c16.322 1.334 37.904 6.865 52.914 13.411 18.669 8.143 41.455 23.1 57.294 35.903 18.252 14.756 37.65 35.666 50.408 55.367 12.287 18.972 24.766 49.747 31.441 71.343 3.818 12.354 7.28 47.344 7.28 47.344"
      />
      <g transform="translate(-.3 -5.516)">
        <PigeonCloseEyesAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.32825 -.31785 .3036 1.26867 79.406 76.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.83757 -.32922 .36581 .93069 77.406 74.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.87524 -.20944 .23273 .97254 77.406 74.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.97254 -.23273 .18978 .79308 77.587 73.607)"
        />
      </g>
      <path
        fill="none"
        stroke="#9e9e9e"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M420.055 378.062s26.297 22.577 34.835 35.213c8.765 12.97 19.639 32.179 20.885 47.783.486 6.1.246 15.93-4.397 19.915-6.533 5.606-20.072 2.747-28.635 1.863-13.837-1.43-29.44-8.557-42.033-14.465-11.245-5.275-26.38-14.574-36.666-21.538-8.214-5.561-19.291-16.137-19.291-16.137"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M42.164 44.908s9.714-7.22 22.059-2.765c15 5.412 15.883 16.883-.882 21.177s-14.442 3.647-14.442 3.647"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M69.635 43.584c7.835-4.148 29.075-.983 30.487-10.82.682-4.752-2.2-14.002-27.191-19.49-14.092-3.094-19.72 3.252-27.561 9.574-8.51 6.861-15.957 18.76-18.83 24.953"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M26.898 47.472c-3.95 5.31-7.833 16.102-12.213 28.051C9.787 88.886 3.447 108.073 2.79 122.29c-.696 15.05 2.375 29.085 7.232 43.346 5.08 14.911 10.476 23.478 17.625 31.67 7.13 8.17 32.167 23.891 32.167 23.891"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M30.252 69.261c6.485-3.838 11.588-4.059 23.383-.882s10.264 14.47-5.736 19.412c-5.917 1.827-19.295 3-19.736 13.147"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-width="5"
        d="M30.124 99.98s-7.975 6.797-11.243 11.252c-3.516 4.792-7.95 11.092-7.161 17.288.253 1.988 4.198 7.852 8.152 7.41 6.77-.754 7.648-4.052 16.676-9.096 5.83-3.257 11.43-4.905 11.43-4.905"
      />
      <path
        fill="none"
        stroke="#878787"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M31.325 100.025c3.077-.57 3.484-.486 6.268-1.914 2.376-1.218 6.995-1.902 6.995-1.902"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M89.847 40.78c3.606 5.601 9.987 10.416 9.987 10.416"
      />
      <path
        fill="none"
        stroke="#878787"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M102.126 279.47s-6.115 25.373-.494 44.765c3.047 10.51 11.547 24.39 17.06 31.416 9.305 11.854 21.57 24.153 33.931 32.775 10.4 7.255 22.424 14.642 32.553 20.052 12.78 6.826 27.802 12.657 39.742 16.92 10.421 3.722 20.916 6.405 32.816 8.43 4.289.729 12.235 1.032 15.27-1.281 4.324-3.295 5.399-13.896 4.294-19.054-1.424-6.645-6.936-15.481-10.655-21.756-5.67-9.565-16.41-18.686-26.272-28.076-12.737-12.129-22.558-17.587-22.558-17.587M254.107 373.148c6.064-.551 8.202-5.384 9.465-8.435 2.138-5.164 1.85-13.163-.488-21.096-3.128-10.613-10.755-21.65-16.059-26.69-7.919-7.526-10.513-13.855-35.554-26.253M239.163 307.667c6.323-4.385 7.616-13.007 7.31-18.155-.37-6.26-2.907-13.517-7.208-19.907-4.895-7.272-12.194-13.146-17.93-17.6-10.958-8.51-36.315-21.45-36.315-21.45"
      />
    </PigeonAnimationContainer>
  );
}

function PigeonCWatch({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(5 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 226.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m307.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m220.008 111.131-74.936 11.906-69.334 23.811-2.1 32.216-8.405 35.717-7.003 42.02-.101 34.23 4.707 33.89 16.004 38.597 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 26.36-4.707 38.597 19.77 33.89 10.355 22.887.246 9.104-9.805-1.4-23.11-14.708-32.917-23.414-26.67-15.063-12.239-11.296-48.952-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-35.285-4.42 7.003-39.919Z"
      />
      <path
        fill="#b7b0ba"
        d="M163.28 161.556q11.85-4.478 48.324-9.805l4.202-19.61-74.936 6.303-70.734 58.829-5.603 25.212-1.693 24.359s18.351-27.42 30.237-38.376c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.26-15.537 35.917-19.564Z"
      />
      <path
        fill="#a195a6"
        d="M169.584 151.05c12.63-3.604 43.421-3.501 43.421-3.501l8.404-51.125-84.741 19.61-61.63 36.417-1.4 30.115-10.506 43.42q16.748-20.982 25.213-28.713c9.215-8.417 22.314-18.371 33.616-24.512 12.325-6.696 32.524-17.401 47.623-21.71Z"
      />
      <path
        fill="#998d9c"
        d="M132.466 153.151c10.939-5 26.052-10.813 37.818-13.306 12.99-2.752 44.122-3.502 44.122-3.502l7.003-51.125-79.138 8.405-67.233 28.013-.7 49.724-7.004 32.916q20.352-23.43 30.815-31.515c9.383-7.25 23.533-14.68 34.317-19.61Z"
      />
      <path
        fill="#959195"
        d="M135.267 139.845c11.588-4.68 27.603-9.787 39.92-11.906 12.232-2.104 41.32-2.1 41.32-2.1l6.303-46.924-3.502-36.417L202.5 17.285 177.988 3.98h-35.017l-22.41 3.502-18.91 13.306-14.007 23.111-6.303 33.617-4.902 47.623-4.202 59.529q15.581-18.399 23.811-24.512c10.637-7.9 26.934-15.348 39.22-20.31Z"
      />
      <path
        fill="#4a4a4a"
        d="M83.008 61.467S56.737 53.06 52.07 55.239c-5.982 2.793-16.154 5.683-18.22 11.953-.626 1.904 1.918 11.838 2.573 11.76 6.765-.8 17.073-.331 22.798 2.122 7.081 3.035 20.346 13.08 20.346 13.08"
      />
      <path
        fill="#f1f1f1"
        d="M86.736 52.04c-.604-1.729-7.407-5.247-13.027-2.914-2.717 1.128-5.49 2.059-6.55 5.24-1.124 3.372 5.426 5.842 6.8 5.71 4.517-.432 11.704 1.48 11.704 1.48"
      />
      <path
        fill="#ddd"
        d="m277.492 415.216-1.828-19.496-22.542-32.9 11.576-8.53s35.157 12.034 36.14 12.315c10.444 2.984 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.662-26.99q-55.61-6.808-79.689-3.799c-19.981 2.498-50.02 12.148-64.71 25.082-13.512 11.897-25.16 26.761-30.534 43.944-5.587 17.864-7.906 70.373-7.906 70.373l22.98 46.407 40.62 33.616 61.63 25.913 39.878 12.869 13.404-8.53Z"
      />
      <path
        d="M75.39 149.363s3.903 24.254 7.628 34.03c2.855 7.492 12.99 23.353 12.99 23.353-.291-1.107-10.487 7.67-11.797 17.052-2.145 15.352 7.69 54.284 9.069 59.798.776 3.104 10.86 34.748 10.86 34.748l17.603 32.857 36.964 29.924 50.46 25.817 50.45 15.498 15.38-3.729 2.796-20.04s37.462 11.04 69.258 16.485c7.748 1.327 23.568 29.416 23.568 29.416l-65.446 10.14-62.681-4.61-72.82-15.67-45.168-20.28L92.242 386.5l-20.28-37.793-12.904-44.246.922-41.48 6.452-41.48 8.959-72.138Z"
        opacity=".06"
      />
      <path
        d="M66.59 208.158s2.658 52.19 7.05 74.076c3.398 16.926 9.04 39.675 17.074 54.955 8.912 16.949 24.294 38.492 39.483 50.153 21.555 16.55 57.017 28.589 83.232 35.748 24.285 6.632 58.06 10.46 83.233 10.67 16.043.135 57.226-6.595 57.226-6.595l17.55 16.303-27.032 6.008-57.068 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-4.284-49.928 8.801-60.434Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M322.511 207.103c30.535 22.827 58.278 67.108 75.712 109.881M290.723 204.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M294.255 230.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M180.837 16.785c26.842 13.086 31.847 47.919 30.536 70.54"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M185.028 35.026c11.749 13.328 18.636 26.497 19.19 41.348"
        />
      </g>
      <path
        fill="#555"
        d="M46.702 69.034c3.799.403 9 2.462 12.547 1.689 2.067-.45 5.38-1.289 6.515-3.137 1.896-3.084-.724-11.823-.724-11.823l-25.335 3.62s-6.642 5.394-5.068 8.686c2.655 5.55 8.463.583 12.065.965Z"
      />
      <path
        fill="#ccc"
        d="M195.151 399.695s14.026.446 21.306-6.834c7.236-7.236 6.318-16.318 6.363-22.427.074-10.163-6.345-21.975-9.95-31.478-2.642-6.96-8.674-15.345-8.674-15.345s9.118-4.076 11.342-7.673c3.132-5.066 3.419-14.324 2.16-19.276-1.873-7.37-16.838-24.425-16.838-24.425s8.73-4.125 10.675-7.339c2.801-4.628 2.531-13.072 1.334-18.348-.932-4.106-9.007-17.68-9.007-17.68l-18.348-9.007s7.728 15.805 8.34 20.682c.59 4.692 1.608 9.551-.465 13.801-2.69 5.518-16.206 13.055-16.206 13.055s10.684 13.106 14.002 18.18c2.95 4.51 6.35 12.207 4.455 16.483-2.453 5.534-16.798 12.206-16.798 12.206s6.693 15.313 9.038 22.18c3.093 9.053 4.731 14.404 4.093 23.95-.333 4.965-2.812 13.09-6.367 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#ccc"
        d="M202.348 403.941s15.566-2.462 23.536-6.637c8.436-4.42 12.337-8.369 12.504-15.44.233-9.886-4.042-19.29-7.922-26.264-3.91-7.026-11.676-19.609-11.676-19.609l15.476 9.613s3.577-11.214 2.334-22.403c-.644-5.796-4.861-16.046-7.935-21.002-4.204-6.78-12.134-20.536-12.134-20.536l7.934 3.734s2.453-7.262 2.415-10.491c-.061-5.267-2.835-12.216-4.337-17.014-2.188-6.992-9.979-23.162-9.979-23.162l17.035 11.929 7 16.802 5.226 20.857-6.626 9.48 13.035 15.4 6.711 16.296-1.35 13.523-9.303 13.81 15.43 20.269 8.723 21.122-3.616 13.097-12.88 9.488-32.695-5.269-16.906-7.593Z"
      />
      <path
        fill="#9d9d9d"
        d="M215.761 409.396s21.344-3.697 27.16-8.98c5.48-4.978 6.762-18.823 3.67-26.71-6.435-16.41-13.655-28.835-13.655-28.835l10.47 6.807s3.433-10.3 3.426-20.93c-.007-10.827-7.588-27.71-15.382-36.985-4.63-5.51-14.172-11.188-14.172-11.188l14.545 8.578s4.175-11.175 1.888-20.12c-2.232-8.724-13.45-32.095-13.45-32.095l22.751 22.75 4.103 20.14-8.206 13.8 11.19 11.935 9.696 13.427 6.882 28.915-10.312 14.417 8.82 9.175 9.558 20.943 3.615 20.29-9.089 7.74-27.762-4.1-25.746-8.974Z"
      />
      <path
        fill="#9d9d9d"
        d="M183.853 395.129s20.19-.494 26.168-5.88c4.469-4.024 6.351-12.982 6.408-18.996.086-9.2-5.008-20.628-9.256-29.24-3.583-7.263-10.843-20.583-10.843-20.583s11.183-3.475 13.591-6.04c3.163-3.369 4.084-9.445 3.398-13.591-1.762-10.65-19.505-30.237-19.505-30.237s10.644-4.254 14.22-8.272c3.754-4.218 2.643-11.703-.755-19.254-2.513-5.584-9.755-18.17-9.755-18.17l-7.006-2.668s7.178 11.965 8.674 17.68c1.245 4.76 3.028 12.387.914 16.372-2.782 5.24-16.734 12.611-16.734 12.611s13.906 14.014 16.734 21.744c1.713 4.685 4.908 11.704.755 16.612-2.76 3.26-15.48 9.06-15.48 9.06s4.505 9.603 6.437 16.087c2.805 9.41 6.923 22.79 5.952 32.72-.5 5.12-2.631 12.328-6.279 15.954-4.531 4.505-19.864 7.684-19.864 7.684l12.226 6.407Z"
      />
      <path
        fill="#ccc"
        d="M419.587 365.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.83 370.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.645 17.961-1.021 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        d="m345.664 418.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.505 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M423.46 371.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.266 401.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.993 465.89 37.482 6.317M251.085 459.994l63.593 5.896M313.414 454.94l.843 10.108M239.69 477.445l-71.28-11.441M240.135 480.842l35.376 2.526M239.714 480.842l-78.654 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.744 24.697)"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M85.276 51.633s-4.439-4.325-10.503-3.82c-2.896.242-6.897 1.275-7.957 4.456-1.124 3.373.577 6.237 1.89 6.664 2.975.967 3.293 1.234 6.42 1.307 2.67.063 7.056 1.666 7.056 1.666"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M373.209 443.565s-32.91 6.72-48.697 7.983c-19.958 1.597-48.101 1.344-67.857-.798-20.95-2.271-50.444-7.58-70.91-12.602-12.714-3.12-29.457-8.504-41.317-14.048-12.094-5.653-30.538-17.678-40.251-26.837-11.408-10.756-20.567-25.214-27.51-39.272-7.153-14.48-12.868-32.625-15.6-48.543-1.509-8.8-3.654-23.157-3.785-32.255-.232-16.02 4.186-39.638 6.993-55.943 2.333-13.558 7.684-25.967 9.323-39.626 2.515-20.958 1.713-53.461 3.808-74.465 1.548-15.519 3.877-35.354 7.81-50.446 2.762-10.604 6.286-24.73 19.192-37.663 7.34-7.357 20.32-13.274 30.593-14.855 15.106-2.325 39.63-1.776 54.433 5.299 10.05 4.803 20.636 16.738 26.012 26.494 4.873 8.841 6.955 22.478 7.708 32.756.868 11.86-1.56 26.805-3.348 39.886-1.758 12.865-7.127 42.692-7.127 42.692"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M187.399 154.706s21.788-3.175 33.578-2.212c16.322 1.334 37.904 6.865 52.915 13.412 18.669 8.142 41.455 23.099 57.293 35.903 18.253 14.756 37.65 35.665 50.408 55.366 12.287 18.973 24.767 49.748 31.442 71.343 3.818 12.354 10.058 41.91 10.058 41.91"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M66.165 54.51s-16.046 1.385-21.29 3.127c-5.267 1.749-8.686 3.192-11.393 8.82-.868 1.806-.251 9.614 3.308 11.393 6.093 3.047 13.75 1.616 20.064 4.011 10.321 3.915 22.24 11.75 22.24 11.75"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M111.225 48.602s7.607 1.979 9.975 5.81M111.022 48.531s7.499-2.623 12.348.423"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M254.407 361.665c6.064-.552 8.202-5.384 9.465-8.435 2.137-5.165 1.848-13.164-.489-21.097-3.127-10.613-10.755-21.65-16.058-26.69-7.92-7.526-10.513-13.855-35.554-26.253M239.462 296.183c6.324-4.384 7.617-13.006 7.311-18.154-.371-6.26-2.908-13.517-7.21-19.907-4.894-7.272-12.193-13.147-17.929-17.601-10.958-8.51-36.314-21.448-36.314-21.448M102.426 267.986s-6.116 25.373-.495 44.765c3.047 10.51 11.547 24.391 17.06 31.416 9.306 11.855 21.57 24.153 33.931 32.775 10.4 7.255 22.424 14.643 32.553 20.052 12.78 6.826 27.802 12.657 39.742 16.92 10.422 3.723 20.916 6.405 32.816 8.43 4.289.729 12.235 1.033 15.27-1.28 4.324-3.296 5.399-13.896 4.294-19.054-1.424-6.646-6.936-15.482-10.655-21.757-5.67-9.565-16.41-18.686-26.272-28.076-12.737-12.128-22.558-17.587-22.558-17.587"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M422.739 370.437s24.912 19.718 33.45 32.354c8.765 12.97 19.64 32.18 20.885 47.783.487 6.1.246 15.93-4.397 19.915-6.533 5.607-20.072 2.748-28.635 1.863-13.836-1.429-29.439-8.557-42.032-14.465-11.245-5.275-24.171-12.282-34.457-19.246-8.214-5.561-21.395-19.248-21.395-19.248"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M134.573 30.57c1.605 6.466 6.093 13.082 6.093 13.082"
        />
      </g>
      <g>
        <PigeonBlinkFastAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.2969 .42815 -.40895 1.23873 117.406 51.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.88553 .16045 -.17828 .98398 117.406 48.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.85459 .28213 -.3135 .9496 117.406 48.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9496 .3135 -.25564 .77436 118.587 46.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonCGuess({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 224.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m207.359 129.665-42.515 6.511-64.077 6.308-14.45-29.478-18.496-12.716H48.747l-21.386 11.56-12.716 24.276-12.138 43.928 6.936 53.755 22.542 35.258L57.736 283.4l5.1 41.52 16.004 38.598 28.241 39.539 54.601 29.183 65.898 14.12 64.015 6.59 54.6-4.707 16.62-2.801 37.307 22.823 39.503 15.362h28.53l8.339-12.29-4.39-28.968-21.068-37.746-30.486-26.573-9.398-57.836-20.711-49.894-26.36-36.714-37.655-35.773-41.421-25.418-36.715-16.945-43.948-3.392 3.017-22.413Z"
      />
      <path
        fill="#ddd"
        d="m278.23 424.578-9.293-32.171-12.154-17.158 9.294-22.163s33.43 13.322 34.197 13.519c13.951 3.582 37.349 8.168 46.809 10.682 16.092 4.275 43.103 4.626 55.122-6.485 9.908-9.159 10.035-45.868 10.035-45.868l-19.846-52.394-24.61-36.517-42.867-39.692-43.661-26.99q-54.368-5.981-77.624-4.237c-22.554 1.692-52.149 8.365-67.662 20.299-14.66 11.277-27.647 36.117-30.749 54.727-2.82 16.915-5.852 32.324-5.004 64.342.651 24.614 20.732 55.763 20.732 55.763l46.47 40.036 62.912 27.166 35.746 8.58 12.154-11.44Z"
      />
      <path
        fill="#4a4a4a"
        d="M61.606 73.549s-11.267 1.949-17.246 3.58c-6.696 1.826-12.11 7.555-12.11 7.555l-12.37 18.681c-.628 1.904 3.99 10.473 4.646 10.396l18.725-7.129 21.08-9.056"
      />
      <path
        fill="#b7b0ba"
        d="M163.28 161.556q11.85-4.478 41.062-8.616l2.155-21.12-65.627 6.624-74.303 59.913-5.177 20.06-3.236 33.003s23.037-31.997 34.923-42.952c8.183-7.542 25.096-21.073 34.287-27.348 9.521-6.501 25.26-15.537 35.917-19.564Z"
      />
      <path
        fill="#f1f1f1"
        d="M60.654 64.505s-9.54-1.797-15.16.536c-2.717 1.128-5.019 4.64-6.08 7.821-1.123 3.373 3.587 4.174 4.961 4.043 4.517-.432 15.328-2.88 15.328-2.88"
      />
      <path
        fill="#a195a6"
        d="M174.161 147.726c11.357-2.918 31.474-3.406 31.474-3.406l2.155-39.222-71.122 10.935-38.432 14.75 5.647 30.12-13.805 6.902-23.216-6.274 3.137 20.706-9.412 43.297c11.165-13.989 19.86-24.38 26.982-30.12 11.228-9.048 26.368-21.105 38.904-28.236 12.826-7.297 33.396-15.78 47.688-19.452Z"
      />
      <path
        fill="#998d9c"
        d="M132.747 153.373c10.409-4.894 24.58-11.13 35.767-13.804 12.698-3.037 38.845-3.87 38.845-3.87l.43-42.669-65.518.593-47.173 28.376 8.158 38.277-14.433 6.274-16.785-4.118-7.059 41.767c13.568-15.62 24.619-26.109 33.17-31.438 10.56-6.581 23.831-14.325 34.598-19.388Z"
      />
      <path
        fill="#959195"
        d="M135.267 139.845c11.588-4.68 27.603-9.787 39.92-11.906 12.232-2.104 32.41-2.85 32.41-2.85l-1.74-49.92-9.288-34.829-26.173-28.778L132.747.894 98.863 7.797 73.27 26.754 61.606 55.937l.82 41.767 11.338 4.843 18.824 18.824 9.413 27.61-3.765 17.57-28.865-5.02-.216 23.883c7.305-8.626 15.276-18.864 15.276-18.864l14.432.628s3.358-11.475 5.02-12.55c0 0 19.099-9.821 31.384-14.783Z"
      />
      <path
        d="M83.713 182.512c-.748 12.382-4.592 25.078-8.354 36.363-5.267 15.8-1.379 31.861 3.573 48.084 4.773 15.639 20.567 38.955 20.567 38.955l6.282 30.885 31.932 40.307 45.018 30.885 47.113 19.892 39.26 7.33 8.9-10.47-3.141-17.799s11.77 3.618 16.228 4.712c12.789 3.14 24.71 5.182 41.37 8.922 7.67 1.722 31.618 24.818 31.618 24.818l-58.552 8.388-62.68-4.609-72.82-15.67-45.168-20.28-32.262-27.653-20.28-37.792-9.465-25.062-4.612-32.286-34.885-30.71-13.694-28.3s-1.825-20.083 5.934-28.299c3.287 2.465 13.237 10.499 13.237 10.499l10.042-5.478 3.651-11.867 15.063 3.651s9.27-14.695 5.477-22.822c-2.65-5.679-7.76-22.366-7.76-22.366L69 159.761l15.063 6.39q.14 8.265-.35 16.36Z"
        opacity=".06"
      />
      <path
        d="m42.069 196.277 14.606 2.282 6.39-13.237-6.846-30.582 11.867 6.847.913 17.801-4.45 27.202s-4.109 23.222-6.389 31.941.884 21.357 7.955 40.36c5.943 15.971 16.7 42.348 24.828 57.325 8.31 15.312 22.931 37.695 37.13 47.79 22.812 16.221 56.046 32.58 83.215 39.315 24.193 5.998 57.648 9.624 82.56 10.483 15.706.542 51.517-4.606 51.517-4.606l15.98 14.96-16.94 5.318-57.067 3.604-48.657-4.205-60.071-12.615-41.45-16.22-28.233-18.02-28.834-36.043-18.021-47.457-3.414-33.636-11.835-12.856s-11.845-16.413-14.34-23.26c-5.767-15.83-5.933-37.429-5.933-37.429l10.042 1.37 5.477-16.432Z"
        opacity=".06"
      />
      <path
        d="m57.132 199.928 6.39-16.432-5.477-26.93 9.585 5.477.913 25.105-11.411 60.707s-8.94-14.706-11.868-21.453c-2.12-4.886-5.477-16.888-5.477-16.888l3.651-13.694 13.694 4.108Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -307.235 -109.856)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -374.875 -106.85)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -431.994 -154.198)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M326.511 214.103c30.535 22.827 58.278 67.108 75.712 109.881M294.723 211.454c31.418 19.295 90.95 80.353 108.107 141.672"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M298.255 237.944c31.418 19.295 68.104 60.433 88.01 92.388"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M154.351 19.212c28.499 8.921 40.378 37.304 41.403 64.405"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M167.38 34.957c14.544 9.142 23.471 17.746 26.67 37.137"
        />
      </g>
      <path
        fill="#ccc"
        d="M418.587 375.457s-2.741 25.283-9.898 31.461c-7.652 6.606-23.586 11.935-33.582 13.433-13.495 2.023-38.978 2.264-38.978 2.264l22.226 22.247 38.74 21.854 28.311 10.927 31.291 4.966 17.88-1.986 1.63-16.084-4.949-25.099-13.08-24.391-21.348-24.325-18.243-15.267Z"
      />
      <path
        fill="#9d9d9d"
        d="M422.83 380.76s-.073 21.961-6.36 29.05c-9.553 10.773-24.05 15.703-38.535 17.611-10.555 1.39-34.709.65-34.709.65l12.37 13.778 37.508 23.909 27.68 12.436 30.979 6.644 17.961-1.02 6.339-16.073-4.172-24.1-12.965-27.062-22.053-24.567-14.044-11.256Z"
      />
      <path
        fill="#ccc"
        d="M83.747 108.599s1.861 18.67-4.477 24.375c-8.483 7.635-42.126 5.64-42.126 5.64l18.158 15.461s-.324 13.23-4.275 18.384c-2.48 3.235-7.739 6.91-11.972 7.269-3.112.264-10.26-3.848-10.26-3.848l4.275 8.123s-3.598 6.006-5.986 7.696c-1.986 1.405-5.268 2.738-7.696 2.565-2.86-.203-8.55-4.275-8.55-4.275l8.978 19.239 14.536 5.558 9.406-16.247 14.964 1.71 5.986-18.811-5.13-23.943 27.79 11.116 15.282-6.288 1.493-14.923-6.965-18.903q-12.558-19.732-13.43-19.898Z"
      />
      <path
        fill="#9d9d9d"
        d="M59.303 161.827s-3.957 13.484-6.59 16.616c-2.734 3.255-7.784 4.9-11.503 6.09-8.458 2.707-13.24-9.05-13.24-9.05l5.301 15.267s-2.734 6.616-10.003 8.676c-4.725 1.338-12.827-8.124-12.827-8.124l12.4 19.667 12.826 1.283 6.413-15.392 13.681 2.566 8.978-14.537M100.505 164.073c3.217-2.928 1.492-14.426 1.492-14.426l-4.66-17.38-8.77-15.451s.368 10.375-1.99 17.41c-4.302 12.831-42.284 10.447-42.284 10.447l27.746 19.663q22.15 5.486 28.466-.263Z"
      />
      <path
        d="m344.664 428.934 35.77-1.124s18.244 15.483 27.103 20.544c14.005 8 19.504 9.944 28.399 12.819 8.431 2.725 33.937 2.018 33.937 2.018l8.913 8.595-6.366 9.868-14.3 1.887-37.154-6.755-39.687-16.888-36.615-30.964Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.46 381.124s-.777 11.231-1.399 15.896c-.503 3.778-3.888 12.373-3.888 12.373l57.62 60.802-4.949-31.462-17.74-33.219-29.644-24.39Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M417.266 411.27c17.258 14.607 41.973 40.19 57.174 59.985"
      />
      <path
        fill="#555"
        d="M37.945 94.483c5.149-3.805 10.299-2.134 12.77-4.793 1.441-1.55 1.076-3.474.413-5.768-.79-2.733-7.828-6.593-7.828-6.593l-14.491 13.2s-6.581 8.74-4.965 12.013c.8 1.621 8.41-3.851 14.101-8.059Z"
      />
      <path
        fill="#ccc"
        d="M195.151 409.695s14.026.446 21.306-6.834c7.236-7.236 6.318-16.318 6.363-22.427.074-10.163-6.345-21.975-9.95-31.478-2.642-6.96-8.674-15.345-8.674-15.345s9.118-4.076 11.342-7.673c3.132-5.066 3.419-14.324 2.16-19.276-1.873-7.37-16.838-24.425-16.838-24.425s8.73-4.125 10.675-7.339c2.801-4.628 2.531-13.072 1.334-18.348-.932-4.106-9.007-17.68-9.007-17.68l-18.348-9.007s7.728 15.805 8.34 20.682c.59 4.692 1.608 9.551-.465 13.801-2.69 5.518-16.206 13.055-16.206 13.055s10.684 13.106 14.002 18.18c2.95 4.51 6.35 12.207 4.455 16.483-2.453 5.534-16.798 12.206-16.798 12.206s6.693 15.313 9.038 22.18c3.093 9.053 4.731 14.404 4.093 23.95-.333 4.965-2.812 13.09-6.367 16.57-4.564 4.47-20.158 6.912-20.158 6.912l29.703 15.813Z"
      />
      <path
        fill="#9d9d9d"
        d="M183.853 405.128s20.19-.493 26.168-5.878c4.468-4.026 6.35-12.983 6.407-18.997.087-9.2-5.007-20.628-9.256-29.241-3.582-7.262-10.842-20.582-10.842-20.582s11.183-3.476 13.591-6.04c3.163-3.37 4.084-9.445 3.398-13.592-1.762-10.65-19.505-30.236-19.505-30.236s10.644-4.255 14.22-8.272c3.754-4.218 2.642-11.704-.756-19.254-2.512-5.584-9.754-18.17-9.754-18.17l-7.006-2.669s7.178 11.965 8.674 17.681c1.245 4.759 3.028 12.387.913 16.371-2.78 5.24-16.734 12.612-16.734 12.612s13.907 14.014 16.734 21.744c1.714 4.685 4.908 11.703.755 16.611-2.759 3.261-15.479 9.061-15.479 9.061s4.505 9.603 6.437 16.087c2.804 9.41 6.923 22.79 5.952 32.72-.501 5.12-2.632 12.328-6.28 15.954-4.53 4.505-19.864 7.684-19.864 7.684l12.227 6.406Z"
      />
      <path
        fill="#ccc"
        d="M202.348 414.942s15.566-2.462 23.536-6.638c8.436-4.42 12.337-8.369 12.504-15.44.233-9.886-4.042-19.29-7.922-26.264-3.91-7.026-11.676-19.609-11.676-19.609l15.476 9.613s3.577-11.214 2.334-22.403c-.644-5.796-4.862-16.046-7.935-21.002-4.204-6.78-12.134-20.536-12.134-20.536l7.934 3.734s2.453-7.262 2.415-10.491c-.061-5.267-2.835-12.216-4.337-17.013-2.188-6.993-9.98-23.163-9.98-23.163l17.036 11.93 7 16.801 5.226 20.857-6.626 9.48 13.035 15.4 6.711 16.296-1.35 13.523-9.303 13.81 15.43 20.269 8.722 21.123-3.615 13.096-12.88 9.488-32.696-5.269-16.905-7.592Z"
      />
      <path
        fill="#9d9d9d"
        d="M215.761 420.395s21.344-3.697 27.16-8.98c5.48-4.978 6.762-18.823 3.67-26.71-6.435-16.41-13.655-28.835-13.655-28.835l10.47 6.807s3.433-10.3 3.426-20.929c-.007-10.827-7.588-27.711-15.382-36.985-4.63-5.51-14.172-11.189-14.172-11.189l14.545 8.578s4.175-11.174 1.888-20.119c-2.232-8.724-13.45-32.095-13.45-32.095l22.751 22.75 4.103 20.14-8.206 13.8 11.19 11.934 9.696 13.427 6.882 28.916-10.312 14.416 8.82 9.176 9.558 20.942 3.615 20.29-9.089 7.74-27.763-4.1-25.745-8.974Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.374 37.482 6.317M250.785 460.478l63.593 5.896M313.115 455.424l.842 10.107M239.39 477.928l-71.28-11.44M239.835 481.325l35.376 2.527M239.414 481.325l-78.653 2.129"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.874 457.43 2.271 31.862"
          transform="matrix(.90062 0 0 .93083 23.444 25.18)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M362.326 446.698s-22.326 4.07-38.114 5.334c-19.958 1.596-48.101 1.343-67.857-.798-20.95-2.271-50.444-7.58-70.909-12.602-12.715-3.12-29.457-8.504-41.318-14.048-12.094-5.654-30.537-17.679-40.25-26.838-11.408-10.756-20.568-25.214-27.511-39.271-7.153-14.48-12.868-32.625-15.599-48.543-1.51-8.8-3.763-38.333-2.986-54.77.334-30.901 10.013-63.246 10.013-63.246s4.318-13.198 2.505-28.905M65.561 98.807s-3.955-24.375-3.955-28.59c0-10.323.747-22.078 7.14-33.32 4.232-7.44 12.617-18.242 25.015-24.758 14.87-7.815 46.567-14.71 69.946-3.787 11.498 5.372 18.481 12.342 24.155 19.798 7.034 9.245 12.82 24.148 15.673 35.409 3.82 15.075 4.132 36.123 4.123 51.674-.007 11.003-2.963 36.557-2.963 36.557"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M199.725 153.033s9.163-1.019 20.953-.055c16.322 1.334 37.904 6.865 52.914 13.411 18.669 8.143 41.455 23.1 57.294 35.903 18.252 14.756 37.65 35.666 50.408 55.367 12.287 18.972 24.766 49.747 31.441 71.343 3.818 12.354 7.28 47.344 7.28 47.344M420.055 378.062s26.297 22.577 34.835 35.213c8.765 12.97 19.639 32.179 20.885 47.783.486 6.1.246 15.93-4.397 19.915-6.533 5.606-20.072 2.747-28.635 1.863-13.837-1.43-29.44-8.557-42.033-14.465-11.245-5.275-26.38-14.574-36.666-21.538-8.214-5.561-19.291-16.137-19.291-16.137"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M53.728 152.203c7.6 9.642 13.168 31.037 5.675 44.375-4.376 7.28-15.05 1.027-21.207-5.13-6.441-6.441-10.12-15.738-10.12-15.738"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M39.508 141.736c14.89 11.61 46.51 36.154 61.684 20.395 6.194-6.433-6.634-39.398-15.346-48.956-5.229-5.736-13.787-13.806-25.192-12.971M27.68 113.92c-4.762 4.904-8.825 16.224-13.205 28.173-4.898 13.362-11.239 32.55-11.896 46.767-.697 15.05 2.374 29.084 7.232 43.346 5.08 14.911 10.475 23.478 17.625 31.67 7.13 8.17 28.916 21.687 28.916 21.687"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M42.985 195.894s-3.374 19.914-12.919 17.771c-15.91-3.571-19.089-23.052-19.089-23.052"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M91.414 63.087s6.148-5.03 11.747-3.828M91.628 63.084s7.825-.743 11.362 2.046"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M46.557 74.336c-8.126 5.057-12.657 9.405-16.133 13.7-3.74 4.619-8.786 11.365-8.294 17.591.158 1.998 3.82 8.043 7.791 7.79 6.798-.432 7.79-3.769 17.09-8.292 7.84-3.815 18.416-6.302 18.416-6.302"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M59.555 63.006s-5.544-2.77-11.168-.443c-2.685 1.111-6.181 3.313-6.224 6.666-.044 3.555 2.448 5.765 3.828 5.773 3.129.016 3.513.173 6.515-.708 2.561-.753 7.227-.56 7.227-.56"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M107.692 34.29c3.201 5.842 9.228 11.095 9.228 11.095"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M102.126 279.47s-6.115 25.373-.494 44.765c3.047 10.51 11.547 24.39 17.06 31.416 9.305 11.854 21.57 24.153 33.931 32.775 10.4 7.255 22.424 14.642 32.553 20.052 12.78 6.826 27.802 12.657 39.742 16.92 10.421 3.722 20.916 6.405 32.816 8.43 4.289.729 12.235 1.032 15.27-1.281 4.324-3.295 5.399-13.896 4.294-19.054-1.424-6.645-6.936-15.481-10.655-21.756-5.67-9.565-16.41-18.686-26.272-28.076-12.737-12.129-22.558-17.587-22.558-17.587M254.107 373.148c6.064-.551 8.202-5.384 9.465-8.435 2.138-5.164 1.85-13.163-.488-21.096-3.128-10.613-10.755-21.65-16.059-26.69-7.919-7.526-10.513-13.855-35.554-26.253M239.163 307.667c6.323-4.385 7.616-13.007 7.31-18.155-.37-6.26-2.907-13.517-7.208-19.907-4.895-7.272-12.194-13.146-17.93-17.6-10.958-8.51-36.315-21.45-36.315-21.45"
        />
      </g>
      <g>
        <PigeonBlinkFastAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36567 -.01533 .01465 1.3044 98.406 61.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.88973 -.13525 .15028 .98864 96.406 59.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8999 -.0101 .01123 .99994 96.406 59.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99994 -.01123 .00915 .81542 96.587 58.607)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonDFail({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 240.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m155.242 127.788-44.64 8.117 13.746 24.597-3.607-3.924-8.99-11.486-19.75-.564 13.258-16.915-4.994-27.967-22.428 8.619-21.836 31.54-6.16 54.692 8.01 36.682 12.228 14.758 7.168 12.65-2.452 24.842 3.414 35.321 15.019 48.81 30.037 42.553 40.05 26.908 62.576 18.148 65.704-.819 45.448-4.468 23.488-6.338 34.945 31.023 32.54 20.025 28.785 3.755 5.006-10.638-4.38-33.166-17.522-39.424-19.355-35.669-9.398-57.836-18.805-53.551-25.657-41.927-34.417-37.546-41.927-25.031L263.25 152.5l-20.23-4.425 4.772-19.56-92.551-.727Z"
      />
      <path
        fill="#b7b0ba"
        d="M107.733 201s27.27-25.302 41.44-31.845c11.664-5.386 29.686-12.098 42.351-14.442q14.503-2.683 49.024-3.7l7.104-19.75-51.13 4.228-52.283 18.838-34.6 24.604"
      />
      <path
        fill="#a195a6"
        d="M123.69 163.044s61.396-13.657 74.86-14.938c13.84-1.316 43.394-1.017 43.394-1.017l7.4-26.432-44.98.384-89.616 24.051c2.627 2.89 3.524 7.323 5.493 9.489 0 0 2.706 7.646 3.45 8.463Z"
      />
      <path
        fill="#998d9c"
        d="M120.74 155.58c13.39-3.652 58.852-14.793 86.005-16.962 14.357-1.147 37.911 1.718 37.911 1.718l6.072-21.222-58.435.384-83.86 17.13c1.765 4.708 6.58 8.17 7.813 11.46 0 0 4.112 6.471 4.495 7.492Z"
      />
      <path
        fill="#959195"
        d="M166.076 134.923s14.127-1.802 30.864-4.358c16.01-2.445 51.134.256 51.134.256l14.014-42.388-2.51-37.028-15.69-23.85-25.074-15.05-25.819-6.571L165.437 8.1l-21.263 9.57-14.083 19.246-11.096 20.765-4.577 24.757 24.796 19.837q24.147 30.263 26.862 32.648Z"
      />
      <path
        fill="#4a4a4a"
        d="M116.302 65.212s-14.586-6.17-18.903-4.299c-13.38 5.798-28.36 14.152-27.531 20.701.637 5.039 2.292 10.005 4.293 9.909 6.805-.327 20.106-1.196 20.106-1.196l18.897-1.266"
      />
      <path
        fill="#f1f1f1"
        d="M120.085 56.01c-.604-1.728-7.407-5.246-13.028-2.913-2.717 1.128-5.489 2.059-6.55 5.24-1.123 3.372 5.426 5.841 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="M217.477 345.705c10.056 11.906 25.23 23.553 39.067 29.662 13.03 5.752 32.963 12.393 47.156 13.57 14.855 1.233 34.157 1.619 49.683.522 15.142-1.07 38.945.258 49.457-10.694 11.029-11.489 9.086-52.302 9.086-52.302l-18.442-53.965-18.046-36.09-36.09-42.107-42.775-26.734q-50.075-7.57-71.456-5.348c-11.612 1.206-22.938 4.274-22.938 4.274L167 126.461l-34.573-29.114-24.868-13.344-20.623 18.803 6.672 29.72 24.262 36.393-22.442-13.95-12.886-5.617-10.77 27.452 24.262 42.459-14.548-9.757-15.779 23.1 13.344 30.328 42.07 40.056 43.77 14.92 26.371-3.42s16.152 19.3 26.216 31.215Z"
      />
      <path
        d="m88.58 189.626 2.482 25.623-1.91.18-8.283 8.006-4.021 33.54 25.218 29.803s-.103 18.72 6.851 33.4c5.388 11.373 16.28 24.475 24.866 33.622 9.32 9.928 23.657 21.515 34.721 29.238 12.217 8.528 30.375 17.826 43.858 23.3 17.032 6.914 45.364 13.312 65.33 16.446 20.158 3.165 56.578 2.572 72.829 5.608 7.727 1.443 10.01 16.093 10.01 16.093l-50.33 9.878-63.503.94-63.032-11.288-47.979-23.52-32.653-30.631-15.985-34.75-11.816-39.616 2.014-56.49-7.168-13.493-11.806-14.336s-4.678-18.548-1.142-29.226c3.02-9.121 13.264-20.153 16.592-27.393 2.521-5.487 9.413-16.867 9.413-16.867l3.8-11.216 13.343-4.246 12.791 22.582-18.559-10.775-8.304 17.2 2.373 18.388Z"
        opacity=".06"
      />
      <path
        d="M86.398 181.697c0-7.42 4.205 14.753 4.205 14.753s.632 12.583-1.834 14.214c-2.89 1.912-8.006 6.82-8.006 6.82v22.27l-3.457 25.015s7.808 47.115 16.852 65.892c6.052 12.565 19.243 28.479 27.937 39.383 9.1 11.413 22.73 25.776 34.75 34.055 16.443 11.325 41.115 22.885 60.466 27.8 18.228 4.63 43.75 4.69 62.55 4.17 20.578-.568 73.063-9.397 73.063-9.397l8.548 16.872-16.202 4.204-59.849 8.477-54.21-3.475-52.125-11.815-39.616-19.46-31.536-28.732-17.114-31.039-14.838-50.413 1.265-52.705-7.168-13.492-10.54-10.12s2.636-17.432 6.763-27.978c3.243-8.288 12.284-16.316 20.096-25.3Z"
        opacity=".06"
      />
      <path
        d="m70.08 245.094 6.746 10.119 8.102-43.174-5.151-12.061-11.976 19.435 2.278 25.68Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -291.145 -101.811)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -382.447 -109.689)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.16963 .74612 -.72657 2.11277 -283.83 -286.532)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M330.72 209.128c29.08 24.655 54.057 70.554 68.836 114.315M299.155 204.536c30.176 21.184 85.854 85.776 99.222 148.031"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M301.057 231.193c30.176 21.184 64.272 64.493 82.183 97.608"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M222.324 25.674c25.331 15.813 29.678 46.273 23.842 72.758"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M225.37 43.347c11.772 12.511 18.244 23.087 16.454 42.658"
        />
      </g>
      <path
        fill="#ccc"
        d="M420.118 380.325s.535 16.887-5.766 24.821c-6.126 7.715-15.43 10.892-26.775 13.388-13.327 2.932-42.658 2.042-42.658 2.042l20.075 23.528 29.612-4.556 32.345-15.489 7.29-21.412-14.123-22.322Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.832 385.211s.97 17.409-6.151 23.659c-10.275 9.02-20.97 13.23-35.455 15.138-10.556 1.39-33.295.3-33.295.3l12.78 16.463 30.623 30.186 27.998 17.936 22.311 7.437 15.312-.875 6.125-11.812-5.25-32.81-10.937-23.624-12.25-21.436-11.811-20.562Z"
      />
      <path
        fill="#ccc"
        d="M157.6 118.644s-13.798-1.777-19.619-.872c-5.233.814-12.42 2.865-16.13 6.54-3.624 3.589-5.624 12.54-6.104 18.31-.767 9.235 4.796 30.517 4.796 30.517l-8.72-6.104s-7.76 5.843-9.59 9.591c-3.047 6.24-2.234 18.042-.872 24.85 1.744 8.72 6.975 29.21 6.975 29.21l-10.899-8.284c.912.304-7.653 3.678-9.591 6.54-2.738 4.042-1.974 11.251-2.18 16.13-.331 7.85 1.744 27.03 1.744 27.03l-13.079-19.183-7.411-19.182.436-16.567 6.54-7.847 13.95 3.488-10.899-22.234-6.103-18.746 4.795-21.798 10.9-2.616 23.54 15.695-19.021-35.975-3.78-27.312 9.834-20.39 22.49 8.06 16.159 12.833 21.838 18.316Z"
      />
      <path
        fill="#ccc"
        d="m89.494 146.47-11.665 1.883c-1.706-1.65 1.83-11.544 0-17.198-1.475-4.553-9.228-10.486-9.228-10.486l14.965-13.362 13.174-5.95 4.249 25.922-11.495 19.192Z"
      />
      <path
        fill="#9d9d9d"
        d="M151.882 112.001s-18.91-2.97-30.703 3.316c-6.64 3.539-11.249 14.872-11.678 23.334-.75 14.793 7.554 30.564 7.554 30.564l-9.156-6.558s-7.833 4.617-9.797 7.825c-3.384 5.53-4.772 14.706-4.328 21.174.615 8.951 7.587 32.49 7.587 32.49l-6.976-4.796s-10.027.872-12.642 8.72c-3.477 10.428 1.307 39.672 1.307 39.672l-14.822-30.517-.436-23.106 7.411-5.668 15.259 6.104-16.13-28.337-1.745-31.39 8.72-6.975 12.206 3.488 17.003 10.899-18.746-34.916-6.298-22.849 7.64-20.389 12.718-1.589 22.468 11.94 23.584 17.564Z"
      />
      <path
        fill="#9d9d9d"
        d="m95.582 129.124-7.23 15.808 8.537.542 9.043 7.074 2.936 8.389-19.714-10.067-5.19-4.94s.46-15.645-1.84-21.167c-2.661-6.386-10.052-8.992-10.052-8.992l17.21-16.912 6.3 30.265Z"
      />
      <path
        fill="#ccc"
        d="M181.942 141.731s-22.296-5.684-29.356-2.942c-7.832 3.04-17.306 13.814-19.55 22.139-2.52 9.346 6.576 29.12 6.576 29.12s-11.398 3.566-14.836 6.59c-4.16 3.66-8.041 11.572-8.475 17.13-.622 7.959 7.063 25.879 7.063 25.879s-11.916-.93-15.709 1.874c-3.923 2.9-7.431 9.137-8.866 13.871-2.1 6.925.203 28.295.203 28.295l13.49 8.725s1.983-16.712 4.475-23.435c1.547-4.177 4.113-9.904 7.382-12.865 4.326-3.919 17.552-7.724 17.552-7.724s-5.658-18.109-4.199-25.947c.578-3.103 3.074-6.874 5.272-9.113 3.842-3.916 16.317-13.446 16.317-13.446s-6.993-17.187-4.409-26.041c1.466-5.023 6.448-11.061 10.98-13.456 5.282-2.79 30.2-1.55 30.2-1.55l-14.11-17.104Z"
      />
      <path
        fill="#9d9d9d"
        d="M185.363 147.29s-21.947-4.654-29.166-3.132c-9.077 1.914-16.441 11.244-18.295 18.952-2.369 9.849 8.124 30.358 8.124 30.358s-12.948 4.865-17.19 8.131c-3.661 2.82-6.727 9.193-7.184 13.823-.87 8.832 7.698 27.645 7.698 27.645s-12.98.006-17.702 3.184c-4.205 2.83-7.686 9.568-9.09 14.51-2.047 7.203.202 24.776.202 24.776l6.225 5.106s-.06-14.81 1.625-20.897c1.41-5.094 3.966-12.262 7.85-15.792 4.439-4.035 18.427-6.85 18.427-6.85s-6.645-18.514-5.22-26.644c.593-3.388 3.298-10.127 5.807-12.454 3.787-3.513 15.82-10.262 15.82-10.262s-7.355-15.67-4.892-24.518c1.793-6.442 8.04-14.654 14.054-17.225 6.242-2.668 28.038-1.443 28.038-1.443l-5.13-7.268Z"
      />
      <path
        d="m348.45 424.468 26.432.32s16.908 19.381 23.95 24.889c10.479 8.193 28.977 21.487 41.638 25.564 5.328 1.715 15.11-1.339 15.11-1.339l7.633 10.288-5.973 11.947-21.24-1.66-26.654-12.892-26.94-21.687-33.956-35.43Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.52 383.024s-.453 12.512-1.575 17.733c-.555 2.583-2.893 8.317-2.893 8.317l18.352 23.698 26.145 51.418.655-7.984-3.5-21.436-18.373-38.498-18.812-33.248Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.876 407.675c16.246 18.119 36.056 62.865 42.926 79.86"
      />
      <path
        fill="#555"
        d="M86.879 79.358c6.895-2.414 12.572-.573 15.562-2.634 1.742-1.2 4.22-5.756 4.07-8.14-.179-2.839-3.24-6.914-3.24-6.914l-14.714 2.543-12.113 9.181s-3.895 6.044-1.536 7.161c3.409 1.616 7.884.234 11.97-1.197Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.28 37.482 6.317M250.785 460.383l63.593 5.896M316.37 454.842l-2.413 10.595M237.39 477.834l-71.28-11.44M237.835 481.231l35.376 2.527M237.414 481.231l-78.653 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m244.322 463.933-4.177 25.359"
          transform="matrix(.90062 0 0 .93083 20.714 25.087)"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M421.307 383.709s16.848 26.018 22.575 37.967c5.895 12.298 14.009 29.08 15.906 42.586 1.357 9.663 5.376 28.258-3.88 31.344-18.708 6.236-51.662-16.868-60.408-22.986-10.02-7.01-21.72-18.544-30.348-27.209-5.334-5.357-12.543-21.451-12.543-21.451"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M101.632 61.082S92.41 63.26 88.92 64.928c-2.468 1.181-12.873 4.89-17.07 10.081-3.73 4.612-3.07 9.077-.918 13.44 3.013 6.111 7.02.351 18.245 2.188"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M119.813 54.317s-4.914-3.776-10.878-2.565c-2.848.579-6.7 2.074-7.38 5.357-.723 3.48 1.302 6.126 2.656 6.396 3.068.613 3.415.84 6.53.548 2.658-.25 6.264.908 6.264.908"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M146.565 55.487s7.493 2.372 9.66 6.322M146.05 55.36s7.626-2.23 12.31 1.064"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M166.881 31.476q6.024 8.434 15.663 18.072"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M70.438 245.616c-12.153-8.274-13.56-16.272-17.029-29.663-5.285-20.406-2.62-55.109 6.988-79.398 5.126-12.96 13.227-25.657 25.718-31.836M94.483 133.497c-.882 5.806-2.81 8.497-8.268 14.488"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M115.096 84.514c2.035-22.483 5.778-29.946 9.863-38.931 5.916-13.016 17.924-25.624 28.397-31.356 9.93-5.433 23.524-7.012 35.452-6.731 11.307.265 26.88 2.353 36.944 7.514 10.097 5.176 21.93 15.853 27.754 25.695 7.1 12 7.902 22.66 8.115 32.092.282 12.505-2.556 26.642-7.1 40.23-5.264 15.748-11.335 34.943-11.335 34.943M359.59 444.015s-17.644 6.018-33.431 7.281c-19.958 1.597-46.128 5-66.669 3.846-20.824-1.169-49.75-3.495-69.531-10.11-18.249-6.102-41.817-16.631-56.627-28.916-12.47-10.342-29.693-26.686-37.35-40.963-8.967-16.723-14.755-36.74-18.072-55.422-1.387-7.818-3.861-31.698-1.248-51.227 1.28-9.561 1.117-9.537 1.117-9.537"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M95.285 219.66c-12.212-7.52-28.578-23.585-28.625 8.986-.016 10.558 10.728 36.163 32.795 55.88s27.633 21.951 48.71 28.976c14.457 4.82 27.71 4.82 41.908 1.053M226.477 217.117c-2.273-10.424-4.915-24.485-14.457-37.35-8.459-11.403-17.537-24.057-28.56-36.251-9.459-10.463-23.228-24.368-33.572-32.913-15.14-12.507-35.88-28.882-53.323-29.037-4.363-.038-10.727 10.397-10.079 19.492.63 8.836 1.788 17.33 7.644 31.533 7.868 19.085 17.88 29.967 33.986 46.073"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M229.166 148.006c18.598-.362 37.326 4.794 53.457 12.111 17.349 7.869 40.46 19.56 54.762 32.143 17.967 15.808 37.275 42.127 48.81 63.095 10.618 19.303 19.345 47.756 25 69.048 4.555 17.15 10.112 58.28 10.112 58.28"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M112.869 234.217c-25.67-16.409-50.252-53.5-38.234-81.461 7.258-16.888 36.57 14.89 49.77 22.433"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M69.172 182.59s3.002.601 4.4 2M69.552 182.492l3.32-.35M63.139 210.064c1.53 1.11 6.132 2.326 6.132 2.326M83.015 211.333l3.595-.903M87.941 146.014c6.231-.541 13.6 2.216 13.6 2.216"
        />
      </g>
      <g>
        <PigeonBlinkFasterAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.137"
          ry="10.082"
          transform="matrix(1.38447 .46986 -.43511 1.28207 153.286 56.979)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.1014 .39205 -.39809 1.11835 150.284 55.976)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.778"
          ry="11"
          transform="matrix(.8488 .29908 -.33233 .94316 150.524 55.035)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.94695 .32138 -.26207 .77221 151.465 52.641)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonDWatch({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 240.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m181.972 125.985-43.327-34.423-28.489-10.684-7.122 7.716-2.967 13.057s12.449 36.459 9.496 39.172l-9.982 6.137-10.473-1.208 4.431-12.89-7.25-29.404-20.543 18.126-12.084 38.669-2.417 45.516 10.837 30.61 12.247 19.369 3.88 63.622 15.019 48.81 30.037 42.552 40.05 26.909 62.576 18.147 65.704-.818 54.6-4.707 16.62-2.802 32.66 27.726 32.54 20.025 28.786 3.754 5.006-10.638-4.38-33.166-17.522-39.423-19.355-35.67-9.398-57.836-18.805-53.55-25.657-41.928-34.417-37.546-41.927-25.03-27.095-11.06-20.23-4.425 4.772-19.559-65.821-3.15Z"
      />
      <path
        fill="#b7b0ba"
        d="M107.733 201.62c10.1-10.673 27.27-25.303 41.44-31.846 11.664-5.386 29.686-12.098 42.351-14.441q14.503-2.684 48.756-6.235l7.372-17.216-51.13 4.229-52.283 18.837-34.6 24.604"
      />
      <path
        fill="#a195a6"
        d="M180.193 150.337s6.979-2.436 20.442-3.717c13.84-1.317 40.265.62 40.265.62l8.443-25.964-44.979.385-40.354 8.922"
      />
      <path
        fill="#998d9c"
        d="M119.06 161.003c13.389-3.652 76.782-23.474 86.53-23.675 11.768-.243 37.168 1.858 37.168 1.858l4.336-20.442-54.8 1.374-80.357 11.21c1.325 5.52-7.122 19.585-7.122 19.585s13.916 8.725 14.244 10.09Z"
      />
      <path
        fill="#959195"
        d="M106.595 150.32s82.739-22.18 92.801-22.284c13.957-.144 46.46 2.478 46.46 2.478l6.813-48.318-1.858-35.928-13.628-19.823-22.92-16.106-30.353-6.194-25.398 1.858-22.92 10.531-14.867 16.725-8.053 25.398-1.239 22.92 4.065 29.57q-11.618 36.788-8.903 39.173Z"
      />
      <path
        fill="#4a4a4a"
        d="M112.302 62.831S97.91 57.193 93.4 58.532C73.552 64.425 59.942 70.486 60.97 77.007c.706 4.474 9.114 9.612 11.116 9.516 6.804-.326 17.738 3.12 17.738 3.12l9.649 2.512 10.82-10.098"
      />
      <path
        fill="#f1f1f1"
        d="M114.085 55.63c-.604-1.728-7.407-5.247-13.028-2.914-2.717 1.128-5.489 2.06-6.55 5.24-1.123 3.372 5.426 5.842 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="M257.348 383.571c13.064 4.713 31.63 5.402 45.825 6.58 14.854 1.231 34.156 1.618 49.682.521 15.142-1.07 38.946.258 49.458-10.694 11.028-11.489 9.086-52.302 9.086-52.302l-18.443-53.965-18.046-36.091-34.47-42.787-43.326-27.895q-51.143-5.728-72.525-3.506c-11.611 1.206-21.157 3.186-21.157 3.186l-37.273-41.053-39.433-32.41L107.28 82.35l-15.665 1.62-4.862 14.045 7.023 32.95 18.906 34.572-18.906-13.504-14.585-4.862-7.563 27.009 17.826 39.973-12.964-3.241-9.723 23.228 10.803 25.928 21.607 24.848 37.273 22.147 52.397 7.563s19.05 30.43 31.11 42.236 25.212 22.315 37.391 26.708Z"
      />
      <path
        d="m88.265 189.941 9.805 23.402-9.234 2.4-8.282 8.007 3.228 25.942 17.514 23.908s.352 32.212 7.306 46.893c5.388 11.373 16.28 24.476 24.866 33.622 9.32 9.928 23.657 21.515 34.721 29.238 12.217 8.528 30.375 17.826 43.858 23.3 17.032 6.914 45.363 13.312 65.33 16.446 20.158 3.165 56.578 2.572 72.829 5.608 7.727 1.443 10.489 18.88 10.489 18.88l-52.39 13.126-61.72 1.051-64.636-11.815-48.65-27.105-30.58-32.665-15.986-34.75-11.815-39.616-.153-51.907-2.13-16.943-11.03-10.083-6.616-19.536s1.914-14.224 4.096-19.535c2.17-5.283 6.617-9.768 10.398-15.755 3.225-5.105 7.247-34.345 7.247-34.345s3.398-7.227 1.26-15.44c-2.405-9.241-10.397-10.083-10.397-10.083l12.919-15.124 8.822 34.345-3.442 30.147 2.373 18.387Z"
        opacity=".06"
      />
      <path
        d="M76.846 262.118s6.15-17.424 4.508-44.725c-.155-2.585 5.951-8.86 5.951-8.86l10.607-.442s-6.379-17.716-8.397-18.12c0 0-11.127 6.296-15.936 24.853-3.96 15.284-2.52 42.896 3.267 47.294Z"
        opacity=".06"
      />
      <path
        d="M86.813 184.89c0-7.42 10.416 20.795 10.416 20.795s-6.827 1.65-9.294 3.28c-2.89 1.911-7.803 8.519-7.803 8.519v22.27l-5.386 25.984s9.738 46.146 18.782 64.923c6.052 12.565 19.243 28.479 27.937 39.383 9.1 11.413 22.73 25.776 34.75 34.055 16.443 11.325 41.115 22.885 60.466 27.8 18.228 4.63 43.75 4.69 62.55 4.17 20.578-.568 70.216-7.684 70.216-7.684l14.286 17.347-21.429 4.081-57.513 6.412-54.21-3.475-52.126-11.815-39.615-19.46-26.41-25.716-22.24-34.055-13.205-50.04-2.775-58.714-15.44-19.851s3.994-22.216 8.193-29.619c4.447-7.843 11.343-12.288 19.85-18.59Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -291.145 -101.811)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -382.447 -109.689)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.16963 .74612 -.72657 2.11277 -283.83 -286.532)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M330.72 209.128c29.08 24.655 54.057 70.554 68.836 114.315M299.155 204.536c30.176 21.184 85.854 85.776 99.222 148.031"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M301.057 231.193c30.176 21.184 64.272 64.493 82.183 97.608"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M217.324 24.674c25.331 15.813 29.678 46.273 23.842 72.758"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M220.37 42.347c11.772 12.511 18.244 23.087 16.454 42.658"
        />
      </g>
      <path
        fill="#ccc"
        d="M420.118 380.325s.535 16.887-5.766 24.821c-6.126 7.715-15.43 10.892-26.775 13.388-13.327 2.932-42.658 2.042-42.658 2.042l20.075 23.528 29.612-4.556 32.345-15.489 7.29-21.412-14.123-22.322Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.832 385.211s.97 17.409-6.151 23.659c-10.275 9.02-20.97 13.23-35.455 15.138-10.556 1.39-33.295.3-33.295.3l12.78 16.463 30.623 30.186 27.998 17.936 22.311 7.437 15.312-.875 6.125-11.812-5.25-32.81-10.937-23.624-12.25-21.436-11.811-20.562Z"
      />
      <path
        d="m348.45 424.468 26.432.32s16.908 19.381 23.95 24.889c10.479 8.193 28.977 21.487 41.638 25.564 5.328 1.715 15.11-1.339 15.11-1.339l7.633 10.288-5.973 11.947-21.24-1.66-26.654-12.892-26.94-21.687-33.956-35.43Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.52 383.024s-.453 12.512-1.575 17.733c-.555 2.583-2.893 8.317-2.893 8.317l18.352 23.698 26.145 51.418.655-7.984-3.5-21.436-18.373-38.498-18.812-33.248Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.876 407.675c16.246 18.119 36.056 62.865 42.926 79.86"
      />
      <path
        fill="#555"
        d="M76.879 77.358c6.895-2.414 12.572-.573 15.562-2.634 1.742-1.2 4.22-5.756 4.07-8.14-.179-2.839-3.24-6.914-3.24-6.914l-15.675 3.407-15.727 8.538s.68 5.823 3.039 6.94c3.409 1.616 7.884.234 11.97-1.197Z"
      />
      <path
        fill="#ccc"
        d="M157.599 118.644s-13.797-1.778-19.618-.872c-5.234.814-12.42 2.865-16.13 6.539-3.625 3.59-5.624 12.54-6.104 18.31-.768 9.236 4.795 30.517 4.795 30.517l-8.719-6.103s-7.76 5.843-9.59 9.59c-3.047 6.24-2.234 18.043-.873 24.85 1.744 8.72 6.976 29.21 6.976 29.21l-10.9-8.283c.913.304-7.653 3.678-9.59 6.539-2.739 4.043-1.974 11.252-2.18 16.13-.332 7.85 1.744 27.03 1.744 27.03l-13.08-19.182-7.41-19.183.436-16.566 6.539-7.847 13.95 3.487-10.898-22.233-6.104-18.747 4.796-21.798 10.899-2.615 23.541 15.694-19.022-35.974-3.78-27.313 9.835-20.39 22.49 8.061 16.158 12.832 21.839 18.317Z"
      />
      <path
        fill="#9d9d9d"
        d="M151.882 112.001s-18.909-2.97-30.703 3.316c-6.64 3.539-11.248 14.872-11.678 23.335-.75 14.792 7.554 30.563 7.554 30.563l-9.156-6.558s-7.833 4.617-9.797 7.825c-3.384 5.53-4.772 14.706-4.328 21.174.615 8.952 7.587 32.49 7.587 32.49l-6.976-4.795s-10.027.872-12.642 8.719c-3.477 10.429 1.308 39.672 1.308 39.672l-14.823-30.517-.436-23.106 7.411-5.667 15.259 6.103-16.13-28.337-1.744-31.39 8.719-6.975 12.207 3.488 17.002 10.9-18.746-34.917-6.297-22.848 7.639-20.39 12.718-1.588 22.468 11.94L151.882 112Z"
      />
      <path
        fill="#9d9d9d"
        d="m95.582 129.124-7.23 15.808 8.538.541 7.397 7.277 1.36 8.504-18.369-12.926-3.314-2.398s.46-15.645-1.84-21.167c-2.661-6.386-10.052-8.993-10.052-8.993l17.21-16.912 6.3 30.266Z"
      />
      <path
        fill="#ccc"
        d="M181.943 141.73s-22.297-5.683-29.357-2.942c-7.832 3.041-17.306 13.814-19.55 22.14-2.52 9.346 6.576 29.119 6.576 29.119s-11.397 3.566-14.836 6.59c-4.16 3.66-8.041 11.572-8.475 17.131-.622 7.958 7.063 25.878 7.063 25.878s-11.916-.929-15.708 1.875c-3.924 2.9-7.432 9.137-8.867 13.87-2.1 6.926.203 28.296.203 28.296l13.49 8.725s1.984-16.713 4.475-23.436c1.548-4.177 4.113-9.904 7.382-12.865 4.326-3.919 17.553-7.723 17.553-7.723s-5.659-18.109-4.2-25.947c.578-3.104 3.074-6.874 5.272-9.114 3.843-3.916 16.317-13.446 16.317-13.446s-6.993-17.186-4.409-26.041c1.466-5.023 6.448-11.06 10.98-13.455 5.283-2.791 30.2-1.551 30.2-1.551l-14.11-17.104Z"
      />
      <path
        fill="#9d9d9d"
        d="M185.363 147.29s-21.947-4.653-29.166-3.131c-9.077 1.914-16.441 11.243-18.295 18.951-2.369 9.85 8.124 30.359 8.124 30.359s-12.948 4.865-17.19 8.13c-3.661 2.82-6.727 9.194-7.184 13.824-.87 8.831 7.698 27.645 7.698 27.645s-12.98.005-17.702 3.183c-4.205 2.83-7.686 9.568-9.09 14.51-2.046 7.203.202 24.777.202 24.777l6.225 5.105s-.06-14.81 1.625-20.897c1.41-5.094 3.966-12.262 7.85-15.791 4.439-4.035 18.427-6.851 18.427-6.851s-6.645-18.514-5.22-26.644c.593-3.388 3.298-10.126 5.807-12.454 3.787-3.512 15.82-10.262 15.82-10.262s-7.355-15.67-4.892-24.518c1.793-6.442 8.04-14.654 14.054-17.225 6.242-2.668 28.038-1.442 28.038-1.442l-5.13-7.269Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.28 37.482 6.317M250.785 460.383l63.593 5.896"
        />
        <path
          stroke="#555"
          stroke-linejoin="round"
          stroke-width="10"
          d="m314.882 452.163-1.589 13.274"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.39 477.834-71.28-11.44M237.835 481.231l35.376 2.527M237.414 481.231l-78.653 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m241.972 463.372-1.827 25.92"
          transform="matrix(.90062 0 0 .93083 20.714 25.087)"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M421.307 383.709s16.848 26.018 22.575 37.967c5.895 12.298 14.009 29.08 15.906 42.586 1.357 9.663 5.376 28.258-3.88 31.344-18.708 6.236-51.662-16.868-60.408-22.986-10.02-7.01-21.72-18.544-30.348-27.209-5.334-5.357-16.743-18.836-16.743-18.836"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M93.79 60.22s-8.752 1.024-12.308 2.222c-5.344 1.8-14.517 2.633-18.714 7.825-3.729 4.611-2.344 11.376 1.797 13.93 5.785 3.566 16.891-.05 23.17 3.987"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M112.813 54.317s-4.914-3.776-10.878-2.565c-2.848.579-6.7 2.074-7.38 5.357-.723 3.48 1.302 6.126 2.656 6.396 3.068.613 3.415.84 6.53.548 2.658-.25 6.264.908 6.264.908"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M137.565 53.487s7.493 2.372 9.66 6.322M137.05 53.36s7.626-2.23 12.31 1.064"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M155.881 27.476q6.024 8.434 15.663 18.072"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M110.634 81.7s.63-16.522 2.846-28.258c2.652-14.049 13.679-30.207 23.404-37.131 9.221-6.565 18.464-9.908 30.342-11.034 11.26-1.067 30.615-.187 42.753 4.138 9.837 3.504 21.624 11.308 28.963 19.308 6.692 7.295 13.16 20.311 14.48 29.652 1.753 12.385-1.503 32.58-3.34 46.788-2.045 15.825-7.334 42.266-7.334 42.266M362.326 446.604s-22.326 4.07-38.114 5.334c-19.958 1.596-47.967 4.199-68.508 3.045-20.824-1.169-48.893-4.228-68.674-10.843-18.249-6.102-41.817-17.836-56.627-30.12-12.47-10.343-26.079-27.892-33.735-42.17-8.967-16.721-15.96-41.557-19.277-60.24-2.474-13.936-3.894-32.85-2.748-48.372.992-13.437 1.33-7.023 1.33-7.023"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M229.166 148.006c18.598-.362 37.326 4.794 53.457 12.111 17.349 7.869 40.46 19.56 54.762 32.143 17.967 15.808 37.275 42.127 48.81 63.095 10.618 19.303 19.345 47.756 25 69.048 4.555 17.15 10.112 58.28 10.112 58.28"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="m69.426 182.015 4.365 1.455M62.88 210.022c1.854.873 5.819 2.91 5.819 2.91M94.16 133.912c-.883 7.262-5.82 10.64-9.094 14.277"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M70.44 245.62c-9.384-5.715-14.944-17.046-17.03-29.67-3.437-20.797-2.618-55.11 6.99-79.4 5.126-12.96 13.266-25.83 25.758-32.008"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M95.284 219.66c-12.212-7.519-28.577-23.585-28.625 8.986-.015 10.558 10.729 36.164 32.796 55.88s32.37 24.26 48.705 28.974c12.085 3.487 41.913 1.056 41.913 1.056"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M112.869 234.217c-25.67-16.409-50.252-53.5-38.234-81.461 7.258-16.888 36.57 14.89 49.77 22.433M87.976 146.006c8.834-.615 13.822 1.82 13.822 1.82"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M226.48 217.12c-2.274-10.424-4.917-24.485-14.46-37.35-8.458-11.403-17.537-24.06-28.56-36.254-9.459-10.463-23.228-24.368-33.572-32.913-15.14-12.507-35.88-28.883-53.322-29.037-4.364-.038-10.728 10.397-10.08 19.492.63 8.836 1.788 17.33 7.644 31.533 7.868 19.085 17.88 29.967 33.986 46.073"
        />
      </g>
      <g>
        <PigeonBlinkFasterAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="6.665"
          ry="9.713"
          transform="matrix(1.38447 .46986 -.43511 1.28207 142.793 53.283)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.844 .30043 -.31227 .87726 141.284 52.976)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.134"
          ry="10.08"
          transform="matrix(.87127 .22538 -.25044 .96813 141.283 51.975)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.94695 .32138 -.26207 .77221 141.465 49.641)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonDGuess({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <g transform="translate(0 -6)">
        <ellipse
          opacity=".1"
          rx="166.187"
          ry="25.726"
          transform="matrix(1 0 0 .91774 240.418 482.088)"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.414 467.678-35.376-10.95"
        />
      </g>
      <path
        fill="#d5d5d5"
        d="m191.954 124.809-36.795-36.182-41.088-21.464-11.038 7.973-2.453 18.397s7.246 16.297 4.292 19.01l9.812 22.078-18.71-1.846 8.898-17.778-6.132-28.823-17.784 14.105-17.171 32.502-7.36 48.447 5.52 42.314 13.491 23.917-1.226 22.076 4 49.835 15.018 48.81 30.037 42.552 40.05 26.909 62.576 18.147 65.704-.818 54.6-4.707 16.62-2.802 32.66 27.726 32.54 20.025 28.786 3.754 5.006-10.638-4.38-33.166-17.522-39.423-19.355-35.67-9.398-57.836-18.805-53.55-25.657-41.928-34.417-37.546-41.927-25.03-27.095-11.06-20.23-4.425 4.772-19.559-55.84-4.326Z"
      />
      <path
        fill="#b7b0ba"
        d="M107.733 201.62c10.1-10.673 27.27-25.303 41.44-31.846 11.664-5.386 30.043-10.646 42.351-14.441q14.041-4.33 58.106-5.777l3.155-18.405-56.263 4.96-52.283 18.837-34.6 24.604"
      />
      <path
        fill="#a195a6"
        d="M179.694 151.529s7.652-1.888 20.994-4.097c21.085-3.49 45.382-1.066 48.389 0l6.337-23.118-51.05-2.653-40.354 8.922"
      />
      <path
        fill="#998d9c"
        d="M130.785 153.237c13.39-3.652 64.896-10.83 74.198-13.753 12.44-3.91 46.91.524 46.91.524l3.521-16.745-63.12-3.145-74.13 10.507c1.326 5.521 12.294 21.247 12.621 22.612Z"
      />
      <path
        fill="#959195"
        d="M151.82 129.573s35.687 4.31 45.75 4.207c14.198-5.259 56.115-2.182 56.115-1.197l3.307-42.45-5.784-37.862-14.025-20.826-22.92-16.106-30.353-6.194-25.398 1.858-22.92 10.531-14.867 16.725-8.053 25.398-1.239 22.92 4.065 29.57q33.606 11.041 36.322 13.426Z"
      />
      <path
        fill="#4a4a4a"
        d="M105.486 70.23 95.06 63.484S62.313 72.38 63.785 78.815c.974 4.261 8.423 11.135 10.425 11.039l25.756-1.84"
      />
      <path
        fill="#f1f1f1"
        d="M115.085 59.63c-.604-1.728-7.407-5.247-13.028-2.914-2.717 1.128-5.489 2.06-6.55 5.24-1.123 3.372 5.426 5.842 6.8 5.71 4.518-.432 11.705 1.48 11.705 1.48"
      />
      <path
        fill="#ddd"
        d="M257.348 383.571c13.064 4.713 31.63 5.402 45.825 6.58 14.854 1.231 34.156 1.618 49.682.521 15.142-1.07 38.946.258 49.458-10.694 11.028-11.489 9.086-52.302 9.086-52.302l-18.443-53.965-18.046-36.091-34.47-42.787-43.326-27.895q-68.794-9.953-86.33-1.86c-3.813-4.894-17.537-24.28-17.537-24.28l-24.281-31.027-30.352-26.98-24.281-13.489-16.188-.674-6.07 19.56 11.466 37.77 21.583 30.352-36.422-22.258-10.792 6.745 1.35 24.282 17.536 37.77-14.839-7.419-8.768 8.094 6.07 29.677 23.607 33.724 43.842 39.12 42.492 8.769s15.232 21.588 27.904 36.552 28.065 27.812 40.244 32.205Z"
      />
      <path
        d="m90.565 179.62 8.555 15.073-17.11 2.444-1.14 26.298 9.168 19.455 13.558 19.886s.017 31.249 4.068 47.002c4.067 15.818 17.533 34.875 26.12 44.022 9.32 9.928 23.656 21.515 34.72 29.238 12.217 8.528 30.375 17.826 43.858 23.3 17.032 6.914 45.364 13.312 65.33 16.446 20.158 3.165 56.578 2.572 72.829 5.608 7.727 1.443 10.489 18.88 10.489 18.88l-52.39 13.126-61.72 1.05-64.636-11.814-48.65-27.105-30.58-32.665-15.985-34.75-11.816-39.616-1.465-42.327 1.808-26.213-13.528-22.934s.838-24.964 2.037-30.961c1.57-7.845 4.82-23.313 8.148-30.554 2.521-5.486 11.814-19.554 11.814-19.554l3.584-5.366s.613-17.02-2.08-23.008c-1.624-3.613-8.316-8.733-8.316-8.733l4.678-6.452 11.205 9.71 1.494 19.235-6.536 15.5-2.807 11.743.938 15.655 4.358 8.38Z"
        opacity=".06"
      />
      <path
        d="M77.529 245.616s5.467-.922 3.825-28.223c-.155-2.585 5.951-8.86 5.951-8.86l11-14.655s-5.314-13.04-7.332-13.444c0 0-9.043 7.516-13.852 26.073-3.96 15.284-5.378 34.711.408 39.109Z"
        opacity=".06"
      />
      <path
        d="M86.491 171.064c0-7.42 11.407 24.85 11.407 24.85l-13.036-1.221-4.1 22.79-1.604 22.43-3.782 25.825s9.738 46.146 18.782 64.923c6.052 12.565 19.243 28.479 27.937 39.383 9.1 11.413 22.73 25.776 34.75 34.055 16.443 11.325 41.115 22.885 60.466 27.8 18.228 4.63 43.75 4.69 62.55 4.17 20.578-.568 70.216-7.684 70.216-7.684l14.286 17.347-21.428 4.081-57.514 6.412-54.21-3.475-52.125-11.815-39.616-19.46-26.41-25.716-22.24-34.055-13.205-50.04-2.53-65.641-8.556-15.073s.582-21.85 4.074-32.998c2.765-8.827 8.077-17.904 15.888-26.888Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -291.145 -101.811)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.28721 .18067 -.17594 2.22727 -382.447 -109.689)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.16963 .74612 -.72657 2.11277 -283.83 -286.532)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M330.72 209.128c29.08 24.655 54.057 70.554 68.836 114.315M299.155 204.536c30.176 21.184 85.854 85.776 99.222 148.031"
        />
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M301.057 231.193c30.176 21.184 64.272 64.493 82.183 97.608"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M217.324 31.674c25.331 15.813 29.678 46.273 23.842 72.758"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M220.37 49.347c11.772 12.511 18.244 23.087 16.454 42.658"
        />
      </g>
      <path
        fill="#ccc"
        d="M420.118 380.325s.535 16.887-5.766 24.821c-6.126 7.715-15.43 10.892-26.775 13.388-13.327 2.932-42.658 2.042-42.658 2.042l20.075 23.528 29.612-4.556 32.345-15.489 7.29-21.412-14.123-22.322Z"
      />
      <path
        fill="#9d9d9d"
        d="M423.832 385.211s.97 17.409-6.151 23.659c-10.275 9.02-20.97 13.23-35.455 15.138-10.556 1.39-33.295.3-33.295.3l12.78 16.463 30.623 30.186 27.998 17.936 22.311 7.437 15.312-.875 6.125-11.812-5.25-32.81-10.937-23.624-12.25-21.436-11.811-20.562Z"
      />
      <path
        d="m348.45 424.468 26.432.32s16.908 19.381 23.95 24.889c10.479 8.193 28.977 21.487 41.638 25.564 5.328 1.715 15.11-1.339 15.11-1.339l7.633 10.288-5.973 11.947-21.24-1.66-26.654-12.892-26.94-21.687-33.956-35.43Z"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="M422.52 383.024s-.453 12.512-1.575 17.733c-.555 2.583-2.893 8.317-2.893 8.317l18.352 23.698 26.145 51.418.655-7.984-3.5-21.436-18.373-38.498-18.812-33.248Z"
      />
      <path
        fill="none"
        stroke="#959595"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M418.876 407.675c16.246 18.119 36.056 62.865 42.926 79.86"
      />
      <path
        fill="#555"
        d="M76.879 80.358c6.895-2.414 12.572-.573 15.562-2.634 1.742-1.2 4.22-5.756 4.07-8.14-.179-2.839-3.24-6.914-3.24-6.914l-15.675 3.407-11.21 8.224s-3.837 6.137-1.478 7.254c3.409 1.616 7.884.234 11.97-1.197Z"
      />
      <path
        fill="#ccc"
        d="M162.599 104.644s-13.797-1.778-19.618-.872c-5.234.814-12.42 2.865-16.13 6.539-3.625 3.59-5.624 12.54-6.104 18.31-.768 9.236 4.795 30.517 4.795 30.517l-8.719-6.103s-7.76 5.843-9.59 9.59c-3.047 6.24-2.234 18.043-.873 24.85 1.744 8.72 6.976 29.21 6.976 29.21l-10.9-8.283c.913.304-7.653 3.678-9.59 6.539-2.739 4.043-1.974 11.252-2.18 16.13-.332 7.85 1.744 27.03 1.744 27.03l-13.08-19.182-7.41-19.183.436-16.566 6.539-7.847 13.95 3.487-10.898-22.233-6.104-18.747 4.796-21.798 10.899-2.615 23.541 15.694-19.022-35.974-3.78-27.313 9.835-20.39 22.49 8.061 16.158 12.832 21.839 18.317Z"
      />
      <path
        fill="#9d9d9d"
        d="M156.882 98.001s-18.909-2.97-30.703 3.316c-6.64 3.539-11.248 14.872-11.678 23.335-.75 14.792 7.554 30.563 7.554 30.563l-9.156-6.558s-7.833 4.617-9.797 7.825c-3.384 5.53-4.772 14.706-4.328 21.174.615 8.952 7.587 32.49 7.587 32.49l-6.976-4.795s-10.027.872-12.642 8.719c-3.477 10.429 3.768 38.524 3.768 38.524l-17.283-29.37-.436-23.105 7.411-5.667 15.259 6.103-16.13-28.337-1.744-31.39 8.719-6.975 12.207 3.488 17.002 10.9-18.746-34.917-6.297-22.848 7.639-20.39 12.718-1.588 22.468 11.94L156.882 98Z"
      />
      <path
        fill="#9d9d9d"
        d="m103.582 116.124-7.23 15.808 8.538.541 5.808 5.726 2.07 8.8-16.563-10.352-4.24-3.717s.46-15.645-1.841-21.167c-2.661-6.386-10.052-8.993-10.052-8.993l7.682-7.759 10.848-4.93 4.98 26.043Z"
      />
      <path
        fill="#ccc"
        d="M183.165 127.33s-18.519-5.283-25.579-2.542c-7.832 3.041-17.306 13.814-19.55 22.14-2.52 9.346 6.576 29.119 6.576 29.119s-11.397 3.566-14.836 6.59c-4.16 3.66-8.041 11.572-8.475 17.131-.622 7.958 7.063 25.878 7.063 25.878s-11.916-.929-15.708 1.875c-3.924 2.9-7.432 9.137-8.867 13.87-2.1 6.926.203 28.296.203 28.296l13.49 8.725s1.984-16.713 4.475-23.436c1.548-4.177 4.113-9.904 7.382-12.865 4.326-3.919 17.553-7.723 17.553-7.723s-5.659-18.109-4.2-25.947c.578-3.104 3.074-6.874 5.272-9.114 3.843-3.916 16.317-13.446 16.317-13.446s-6.993-17.186-4.409-26.041c1.466-5.023 6.448-11.06 10.98-13.455 5.283-2.791 27.324-.939 27.324-.939l-15.01-18.117Z"
      />
      <path
        fill="#9d9d9d"
        d="M190.363 133.29s-21.947-4.653-29.166-3.131c-9.077 1.914-16.441 11.243-18.295 18.951-2.369 9.85 8.124 30.359 8.124 30.359s-12.948 4.865-17.19 8.13c-3.661 2.82-6.727 9.194-7.184 13.824-.87 8.831 7.698 27.645 7.698 27.645s-12.98.005-17.702 3.183c-4.205 2.83-7.686 9.568-9.09 14.51-2.046 7.203.202 24.777.202 24.777l6.225 5.105s-.06-14.81 1.625-20.897c1.41-5.094 3.966-12.262 7.85-15.791 4.439-4.035 18.427-6.851 18.427-6.851s-6.645-18.514-5.22-26.644c.593-3.388 3.298-10.126 5.807-12.454 3.787-3.512 15.82-10.262 15.82-10.262s-7.355-15.67-4.892-24.518c1.793-6.442 8.04-14.654 14.054-17.225 6.242-2.668 28.038-1.442 28.038-1.442l-5.13-7.269Z"
      />
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m312.694 466.28 37.482 6.317M250.785 460.383l63.593 5.896"
        />
        <path
          stroke="#555"
          stroke-linejoin="round"
          stroke-width="10"
          d="m313.767 453.382-.474 12.055"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.39 477.834-71.28-11.44M237.835 481.231l35.376 2.527M237.414 481.231l-78.653 2.128"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m240.828 462.864-.683 26.428"
          transform="matrix(.90062 0 0 .93083 20.714 25.087)"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M421.307 383.709s16.848 26.018 22.575 37.967c5.895 12.298 14.009 29.08 15.906 42.586 1.357 9.663 5.376 28.258-3.88 31.344-18.708 6.236-51.662-16.868-60.408-22.986-10.02-7.01-21.72-18.544-30.348-27.209-5.334-5.357-16.743-18.836-16.743-18.836"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M96.79 63.718s-8.752 1.025-12.308 2.222c-5.344 1.8-14.508 4.522-18.705 9.713-3.729 4.612.606 9.966 4.747 12.52 5.785 3.566 13.398 1.13 21.424 2.636"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M114.813 57.818s-4.914-3.775-10.878-2.564c-2.848.579-6.7 2.073-7.38 5.357-.723 3.48 1.302 6.126 2.656 6.396"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M137.565 58.487s7.493 2.372 9.66 6.322M137.05 58.36s7.626-2.23 12.31 1.064"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M155.881 33.476q5.225 9.77 15.663 18.072"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M113.33 65.61c3.184-17.238 13.829-36.375 23.554-43.3 9.221-6.564 18.464-9.907 30.342-11.033 11.26-1.067 30.615-.187 42.753 4.138 9.837 3.504 21.624 11.308 28.963 19.308 6.692 7.295 12.004 20.549 14.48 29.652 4.244 15.598 3.311 28.357 3.37 42.684.067 16.033-4.857 30.801-8.642 41.524M362.326 446.604s-22.326 4.07-38.114 5.334c-19.958 1.596-47.967 4.199-68.508 3.045-20.824-1.169-48.893-4.228-68.674-10.843-18.249-6.102-41.817-17.836-56.627-30.12-12.47-10.343-26.079-27.892-33.735-42.17-8.967-16.721-15.96-41.557-19.277-60.24-2.474-13.936-3.894-32.85-2.748-48.372.992-13.437 5.568-29.004 5.568-29.004"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M229.166 148.006c18.598-.362 37.326 4.794 53.457 12.111 17.349 7.869 40.46 19.56 54.762 32.143 17.967 15.808 37.275 42.127 48.81 63.095 10.618 19.303 19.345 47.756 25 69.048 4.555 17.15 10.112 58.28 10.112 58.28"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M70.158 196.032a67.398 67.398 0 0 0 5.697 1.676M74.85 169.895s1.018.252 4.69.335M101.468 121.483c-.882 5.807-1.794 6.511-7.253 12.502"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M75.855 246.967c-9.384-10.534-13.776-20.444-17.121-37.476-4.063-20.684.054-62.647 9.663-86.936 5.126-12.96 11.06-21.336 23.551-27.515"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M101.284 205.66c-12.212-7.519-28.577-23.585-28.625 8.986-.015 10.558 19.742 41.479 32.796 55.88 9.324 10.288 30.718 29.016 43.48 34.485 16.945 7.262 41.135 9.549 41.135 9.549"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M118.869 220.217c-25.67-16.409-50.252-53.5-38.234-81.461 7.258-16.888 36.57 14.89 49.77 22.433M95.941 132.014s7.057 1.355 11.344 2.691M226.48 217.12c-2.274-10.424-2.719-23.93-9.765-39.197-7.262-15.735-18.155-31.47-26.628-42.363-8.659-11.133-23.855-30.412-34.199-38.957-15.14-12.507-35.88-28.883-53.322-29.037-4.364-.038-10.728 10.397-10.08 19.492.63 8.836 1.788 17.33 7.644 31.533 7.868 19.085 17.88 29.967 33.986 46.073"
        />
      </g>
      <g>
        <PigeonBlinkFasterAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="6.665"
          ry="9.713"
          transform="matrix(1.38447 .46986 -.43511 1.28207 142.793 58.283)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.844 .30043 -.31227 .87726 141.284 57.976)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.134"
          ry="10.08"
          transform="matrix(.87127 .22538 -.25044 .96813 141.283 55.975)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.94695 .32138 -.26207 .77221 141.465 53.641)"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonSuccessA({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <ellipse
        opacity=".1"
        rx="166.187"
        ry="25.726"
        transform="matrix(1 0 0 .91774 261.418 476.088)"
      />
      <path
        fill="#d5d5d5"
        d="m228.536 156.704 1.883-86.608-12.238-33.89-24.476-21.652-28.242-8.473-36.714 6.59L105.214 31.5 93.917 68.213l-2.824 70.605 2.138 38.327 3.942 29.959-17.345 23.652-12.614 45.728 2.365 38.631 8.672 15.768 6.693 5.673 11.986 43.17 29.705 34.761 53.179 24.304 76.017 11.877 72.058-1.583 50.974-17.934 22.621-10.497 37.521 24.533 24.052 6.734 16.836-17.798 3.367-18.28-6.694-24.02-22.649-33.703-11.653-18.865-8.178-29.085-16.983-31.354-29.797-37.022-35.98-35.297-38.62-25.994-35.847-16.24-33.26-8.5-15.063.941Z"
      />
      <path
        fill="#ddd"
        d="m332.549 364.847 59.508 2.975 22.977-6.735 14.103-10.392 12.865-21.359-25.386-50.726-29.045-36.283-39.34-40.824-39.819-23.75q-54.862-26.021-78.94-23.012c-19.982 2.498-54.34 17.498-69.03 30.433-13.512 11.897-35.184 34.298-40.558 51.48-5.587 17.864-5.433 39.407-1.349 60.703 4.638 24.183 12.513 38.95 33.724 51.26 20.822 12.085 48.03 18.198 67.967 12.51 9.57-2.73 24.547-22.316 24.547-22.316l38.68 16.365 49.096 9.67Z"
      />
      <path
        fill="#b7b0ba"
        d="M204.198 201.258q9.355-11.412 26.079-44.133l1.633-15.027-64.979 19.124-71.702 44.822-8.715 37.837s15.468 23.414 25.998 23.591c10.245.173 34.083-14.649 49.058-25.592 13.039-9.528 31.025-26.466 42.628-40.622Z"
      />
      <path
        fill="#a195a6"
        d="M205.703 188.72c10.94-13.08 24.072-40.622 24.072-40.622l.769-45.424-2.409-46.365-14.094-26.733-27.34-18.726-28.088-3.745-23.595 4.12-19.475 10.86-16.479 22.846-5.808 27.636-1.205 42.15-.602 46.364 5.576 44.845-5.728 19.477q13.753 25.505 23.13 25.505c12.03 0 32.599-11.535 47.644-23.07 12.396-9.503 33.529-27.04 43.632-39.118Z"
      />
      <path
        fill="#998d9c"
        d="M154.047 220.817c14.6-9.162 44.122-33.408 55.166-46.64 12.347-14.794 21.565-38.116 21.565-38.116l.747-44.387-4.276-42.022-16.953-24.931-23.436-13.463-27.923-4.488-24.433 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.205 30.107 1.841 31.583q9.815 21.11 18.45 23.59c12.305 3.534 29.276-7.739 40.12-14.544Z"
      />
      <path
        fill="#959195"
        d="M162.771 203.264c12.732-9.111 31.365-21.723 42.629-32.598 14.543-14.043 26.078-41.626 26.078-41.626l-.005-37.366-4.275-42.022-16.954-24.931-23.435-13.463-27.924-4.488-24.432 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.204 30.107.565 19.244q16.667 20.473 26.997 21.886c13.486 1.846 30.557-10.133 41.626-18.054Z"
      />
      <path
        fill="#4a4a4a"
        d="M159.57 90.281s-7.692-6.545-16.729-7.586c-10.139-1.169-15.968 5.384-17.986 6.326-5.982 2.793-1.98 16.631-4.045 22.901-.627 1.903 2.49 12.405 4.376 13.079 6.415 2.291 20.172-10.495 20.172-10.495l19.056-13.889"
      />
      <path
        fill="#f1f1f1"
        d="M160.57 80.237c.687-9.157-14.125-5.25-25.312-5.796-2.938-.144-6.766 2.043-7.826 5.224-1.124 3.373.598 6.196 1.972 6.064 1.979-.189 5.104-3.612 12.43-3.319 6.658.267 18.735 6.871 18.735 6.871v-9.044Z"
      />
      <path
        fill="#555"
        d="M139.403 91.07c-5.17 2.003-7.59 8.764-6.274 17.97.563 3.94 7.582-4.907 10.96-7.014 4.023-2.51 10.045-2.627 9.25-5.805-.606-2.424-9.992-6.678-13.936-5.15Z"
      />
      <path
        d="m75.853 246.057 10.785-25.376 11.42-13.957c-.292-1.106 11.323 11.045 11.323 11.045s-4.854 33.914-5.396 48.562c-.55 14.842-2.224 35.602 1.812 49.472 4.206 14.457 14.93 40.04 26.165 49.604 5.93 5.049 33.68 26.322 50.52 32.093 23.007 7.885 58.142 15.09 81.743 16.057 28.823 1.18 66.964.988 93.765-6.649 27.225-7.757 68.767-33.32 82.558-48.173 7.584-8.168-3.9 26.497-10.117 35.747-9.15 13.611-43.088 33.647-43.088 33.647l-54.559 17.763-62.17 3.807-86.853-9.219-45.167-20.279-32.262-27.653-15.887-30.396-5.076-25.376-14.59-12.688-2.538-39.333 7.612-38.698Z"
        opacity=".06"
      />
      <path
        d="M89.196 229.244s-1.215 41.085.665 58.55c2.224 20.667 11.515 49.047 20.74 67.675 7.282 14.707 22.226 28.113 35.63 37.583 13.319 9.41 36.024 17.593 51.736 21.964 22.036 6.128 45.955 13.138 68.82 13.666 24.925.576 55.694.529 80.018-4.938 17.86-4.014 44.111-13.714 59.56-23.834 12.271-8.04 32.8-36.267 32.8-36.267l-7.984 29.275-23.952 25.283-61.211 24.618-75.85 6.653-77.955-6.698-41.45-16.22-30.296-17.668-26.771-36.395-8.492-36.127s-6.244-2.21-7.806-6.319c-10.555-27.76 11.798-100.8 11.798-100.8ZM125.45 83.294c-7.062-.024-13.008 11.799-13.986 17.768-1.536 9.375 4.717 31.532 14.092 33.063 3.073.502 11.25-5.281 14.566-9.312 3.962-4.814 4.013-10.365 4.013-10.365l-17.052 9.362-7.356-17.386 3.108-13.517 5.876-5.372s-1.123-4.234-3.261-4.241Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532"
            opacity=".58"
            transform="matrix(2.29396 .017 -.0079 2.23453 -376.176 -51.707)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.29205 .10072 -.09384 2.23232 -291.061 -101.131)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.27586 .2895 -.2877 2.21576 -372.39 -158.789)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.264 1.261 5.416 1.646M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c1.55 1.092 3.76 1.488 3.76 1.488"
            opacity=".58"
            transform="matrix(2.24514 -.4495 .4708 2.18891 -348.366 79.222)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M316.934 195.941c31.418 19.295 87.057 60.727 108.043 123.684"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M185.604 23.27c27.554 11.513 34.565 45.997 34.565 68.657"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M192.133 41.23c12.5 12.627 18.05 28.421 19.464 43.215"
        />
      </g>
      <g transform="translate(29 -6)">
        <path
          fill="#ccc"
          d="m410.135 338.948-.323 39.152-11.803 27.4-13.646 13.169 9.857 7.393 26.49 12.32 24.026 3.081 2.593-11.936-4.636-25.713-16.019-31.616-16.54-33.25Z"
        />
        <path
          fill="#9d9d9d"
          d="M401.047 400.656q-8.36 9.162-28.082 26.48l30.306 20.203 30.305 12.025 15.47-19.4 5.294-12.943-5.653-27.157-15.16-24.582-16.336-20.81q-8.12 33.646-16.144 46.184Z"
        />
        <path
          fill="#ccc"
          d="M325.972 375.345s21.155-3.09 23.485-8.163c2.063-4.488 1.31-11.836-.519-16.425-3.016-7.57-12.514-14.834-17.596-21.204-4.893-6.133-14.95-14.034-14.95-14.034l13.088 1.036s-1.009-10.831-2.39-14.431c-1.567-4.082-10.046-12.026-15.043-16.933-6.423-6.307-24.269-18.18-24.269-18.18l16.728 1.852s-.893-4.77-2.977-10.42c-1.855-5.029-7.34-11.319-11.117-15.122-5.194-5.232-18.633-15.334-18.633-15.334l27.837 6.74 16.427 14.762L322 268.05l-3.037 7.18 10.375 10.895 11.881 13.016 3.358 13.169-4.017 16.159 21.432 13.767 15.423 16.864 2.767 13.579-18.035 2-36.176.667Z"
        />
        <path
          fill="#9d9d9d"
          d="M351.031 376.656s8.515-6.118 9.912-10.135c1.434-4.124.143-10.339-1.522-14.473-2.786-6.915-7.569-12.327-12.989-17.653-5.613-5.516-20.258-17.54-20.258-17.54l10.082 2.31s2.863-7.994 2.3-11.65c-.97-6.305-7.115-13.794-10.698-17.77-8.006-8.881-26.47-20.854-26.47-20.854l9.891 2.51s.222-4.978-.807-10.452c-.948-5.048-5.03-10.757-8.555-14.493-4.63-4.908-19.157-16.382-19.157-16.382l19.272 4.948 17.09 13.27 4.778 15.567-3.513 8.007 21.81 13.322 11.274 10.122 10.253 23.012-6.866 8.79 8.598 8.102 17.327 13.463 6.206 15.52-15.373 7.864-22.585 4.595Z"
        />
        <path
          fill="#ccc"
          d="M311.697 372.875s6.097-5.636 7.941-8.46c2.893-4.431 6.098-12.258 5.375-17.5-.915-6.636-6.78-15.074-11.127-20.17-5.756-6.75-19.711-17.62-19.711-17.62s2.801-7.793 3.298-11.049c.81-5.302-1.505-11.904-4.619-16.271-4.52-6.343-20.579-16.223-20.579-16.223s6.472-6.464 7.442-9.813c1.366-4.712-1.632-9.115-4.518-13.082-3.813-5.244-24.238-16.564-24.238-16.564l-20.416 3.382s11.397 8.187 15.41 11.37c3.555 2.821 10.157 6.997 10.398 11.529.279 5.272-7.827 12.105-7.827 12.105s12.384 11.814 17.405 16.779c2.951 2.918 7.406 9.819 7.724 13.957.382 4.978-6.874 12.923-6.874 12.923s12.815 13.797 17.961 19.954c4.043 4.837 9.433 9.813 11.425 15.145 1.292 3.46 1.81 8.97.844 12.286-1.081 3.715-13.235 14.1-13.235 14.1l27.92 3.222Z"
        />
        <path
          fill="#9d9d9d"
          d="M303.976 370.97s15.434-13.109 16.182-19.869c.798-7.206-4.38-15.855-9.5-20.988-8.618-8.638-22.507-20.716-22.507-20.716s4.612-11.098 4.899-15.375c.289-4.314-3.963-9.452-6.82-12.7-5.419-6.161-21.144-17.045-21.144-17.045s10.397-5.265 10.94-10.197c.618-5.606-6.222-11.952-10.562-15.554-5.38-4.464-22.398-12.555-22.398-12.555l-9.354 1.971s13.74 7.367 18.347 11.148c3.773 3.097 10.179 8.777 9.984 13.654-.22 5.497-8.519 11-8.519 11s14.402 11.488 20.542 18.804c2.168 2.584 4.352 7.34 3.97 10.691-.464 4.091-5.791 13.179-5.791 13.179s11.873 12.507 16.835 18.92c5.163 6.67 14.536 15.368 14.985 23.791.3 5.604-12.577 18.766-12.577 18.766"
        />
        <path
          d="m373.446 426.173 32.164-31.06 11.262-40.54 26.632 39.454 10.542 17.99-.294 11.18-7.188 22.698s-2.392-18.306-13.508-31.49c-3.888-4.61-17.397-12.615-17.397-12.615s-16.843 27.23-32.11 33.523"
          opacity=".04"
        />
        <path
          fill="#b6b6b6"
          d="m413.284 348.1-.86 23.312s15.362 15.8 24.9 26.701c12.6 14.4 15.217 27.072 15.217 27.072l-.829-12.852-3.317-12.853-18.156-30.89-16.955-20.49Z"
        />
        <circle r="9.664" fill="#c7a1b6" transform="matrix(1.4279 0 0 .83512 159.18 92.85)" />
        <circle r="9.664" fill="#c7a1b6" transform="matrix(.96087 0 0 .74165 81.095 92.025)" />
      </g>
      <g fill="none">
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m167.256 481.814 62.303-15.447M229.84 466.423l35.874-4.837M162.107 469.971l64.912-5.893"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M442.227 343.264s23.78 32.684 33.925 48.973c9.617 15.443 6.401 29.708 4.617 37.943 0 0-10.349 21.289-18.75 21.25-10.625.625-23.75-6.25-35-12.5-8.176-4.543-25.625-18.125-25.625-18.125"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M96.04 208.275s-11.746 16.64-13.204 19.779c-20.429 44-15.585 78.148-13.287 91.168 1.571 8.905 9.002 13.622 15.38 18.3"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m230.115 450.92-.083 8.123M302.496 475.363l-65.213 8.51M303.2 476.515l39.099-6.45M304.09 476.572l-58.932 18.484"
        />
        <path
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m237.839 467.857.641 19.458"
          transform="matrix(.90062 0 0 .93083 89.927 16.416)"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="4"
          d="M158.986 88.338c5.244-.611 3.861-6.397 1.912-9.077s-11.591-6.572-16.568-6.82c-4.798-.418-8.605.906-10.604 1.514-2.904.884-7.286 3.931-7.953 5.934-.683 2.047-.096 4.241 1.558 4.918"
        />
        <path
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M229.653 153.407s17.933 3.587 28.345 6.19c13.717 3.43 39.622 12.655 53.767 20.906 17.216 10.043 35.439 21.452 51.31 36.746 16.388 15.79 35.843 34.968 48.6 54.668 12.288 18.973 22.853 35.281 30.437 70"
        />
        <path
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M440.093 359.2s1.98 17.64-10.444 34.678c-10.328 14.164-19.85 22.272-34.298 29.682-22.721 11.653-46.832 19.788-78.492 24.405-21.687 3.163-64.922 1.637-86.675-1.035-16.304-2.003-38.283-4.943-53.736-10.515-17.725-6.391-38.816-14.334-52.41-25.286-10.943-8.815-23.515-24.274-28.868-36.885-6.517-15.354-11.3-42.73-12.83-59.338-1.652-17.935-1.18-42.148 2.403-60.469 2.88-14.724 12.455-48.433 12.455-48.433s-4.874-33.646-5.536-49.124c-.827-19.334-.45-24.384.39-43.717.79-18.218.479-42.959 5.265-60.554 2.41-8.863 8.532-21.444 15.017-27.949 7.732-7.756 20.3-13.43 31.056-15.492 11.275-2.16 27.346-2.784 38.397.324 10.713 3.013 21.664 9.7 29.538 17.563 7.25 7.24 13.227 18.867 15.966 28.74 3.492 12.58 2.87 33.294 3.992 46.302 2.218 25.723-1.761 58.593-1.761 58.593"
        />
        <path
          stroke="gray"
          stroke-width="4"
          d="M160.11 90.003c-2.688-3.671-11.068-8.644-16.617-8.797s-12.187 1.377-17.106 5.539"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M126.953 85.69c-2.09 1.768-5.076 6.314-5.633 7.595-2.105 4.838-1.698 13.352-.143 18.79 1.118 3.91 2.766 9.815 6.325 11.595 6.093 3.046 14.406-13.352 34.082-22.136 5.06-2.259 1.084-8.627-1.605-12.299"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M350.229 265.111c3.908-6.628 1.474-15-.958-19.548-2.957-5.53-8.298-11.06-14.878-15.063-7.488-4.556-16.576-6.837-23.65-8.482-7.962-1.852-19.863-2.442-28.234-2.188-6.204.189-19.948 2.69-19.948 2.69M384.719 321.195c5.474-2.666 4.694-6.643 4.793-9.944.167-5.587-2.939-12.964-7.937-19.552-6.687-8.815-17.732-16.431-24.478-19.263-10.074-4.23-12.884-10.342-46.513-11.745"
        />
        <path
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M196.232 272.25s-7.497 38.536 43.318 62.62c18.066 9.722 39.824 18.679 58.035 23.096 16.056 3.895 38.364 8.385 55.1 8.923 18.192.585 49.367 3.71 60.39-5.832 4.644-4.02 1.184-18.103-10.677-27.272-10.34-7.992-21.523-15.406-31.925-19.705-10.188-4.21-35.769-8.406-35.769-8.406"
        />
        <g stroke="#555" stroke-linecap="round" stroke-linejoin="round" stroke-width="5">
          <path d="M131.776 63.388s-8.338-3.849-13.57.26" />
          <path d="M131.776 62.822s-5.006-4.019-10.699-3.392" />
        </g>
        <g stroke="#555" stroke-linecap="round" stroke-linejoin="round" stroke-width="5">
          <path d="M165.666 64.409s8.325-3.878 13.57.214" />
          <path d="M165.664 63.843s4.992-4.036 10.687-3.43" />
        </g>
      </g>
      <g transform="translate(28 5)">
        <PigeonCloseEyesAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36055 -.11903 .1137 1.29952 144.406 59.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9962 -.08716 .07107 .81237 144.587 55.607)"
        />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.35246 .19008 -.17559 1.24938 97.406 58.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12746 .90689 96.406 56.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12799 .91066 96.406 56.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99027 .13917 -.1135 .80754 97.587 54.607)"
        />
      </g>
      <g>
        <PigeonHeartAnimation />
        <path
          fill="#c7a1b6"
          d="M263.244 67.997c-4.626-8.298-8.067-22.168-5.064-28.819 4.225-9.652 19.24-3.85 17.1 5.777 5.955-8.074 19.89-1.527 14.85 10.126-3.062 6.472-15.796 11.89-26.886 12.916Z"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonSuccessB({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <ellipse
        opacity=".1"
        rx="166.187"
        ry="25.726"
        transform="matrix(1.1791 0 0 .91774 271.365 476.088)"
      />
      <path
        fill="#d5d5d5"
        d="m228.536 181.704 1.883-86.608-12.238-33.89-24.476-21.652-28.242-8.473-36.714 6.59L105.214 56.5 93.917 93.213l-2.824 70.605 2.138 38.327 3.942 29.959-17.345 23.652-12.614 45.728 2.365 38.631 8.672 15.768 6.693 5.673 11.986 43.17 29.705 34.761 53.179 24.304 76.017 11.877 72.058-1.583 50.974-17.934 23.63-11.553 36.99 18.946 28.87 5.864 11.54-10.285 7.858-23.999-9.022-24.359-24.812-27.645-11.653-18.865-8.178-29.085-16.983-31.354-29.797-37.022-35.98-35.297-38.62-25.994-35.847-16.24-33.26-8.5-15.063.941Z"
      />
      <path
        fill="#ddd"
        d="m400.237 404.45-1.353-13.534 16.15-4.829 14.103-10.392 12.865-21.359-25.386-50.726-29.045-36.283-39.34-40.824-39.819-23.75q-54.862-26.021-78.94-23.012c-19.982 2.498-54.34 17.498-69.03 30.433-13.512 11.897-35.184 34.298-40.558 51.48-5.587 17.864-8.565 39.857-4.481 61.153 4.637 24.182 9.286 38.168 30.498 50.479 20.822 12.084 43.145 15.853 63.624 12.62 9.99-1.578 31.022-16.642 31.022-16.642l46.463 23.006 49.17 13.533 44.66 5.864 19.397-7.217Z"
      />
      <path
        fill="#b7b0ba"
        d="M204.198 226.258q9.355-11.412 26.079-44.133l1.633-15.027-64.979 19.124-71.702 44.822-8.715 37.837s15.468 23.414 25.998 23.591c10.245.173 34.083-14.649 49.058-25.592 13.039-9.528 31.025-26.466 42.628-40.622Z"
      />
      <path
        fill="#a195a6"
        d="M205.703 213.72c10.94-13.08 24.072-40.622 24.072-40.622l.769-45.424-2.409-46.365-14.094-26.733-27.34-18.726-28.088-3.745-23.595 4.12-19.475 10.86-16.479 22.846-5.808 27.636-1.205 42.15-.602 46.364 5.576 44.845-5.728 19.477q13.753 25.505 23.13 25.505c12.03 0 32.599-11.535 47.644-23.07 12.396-9.503 33.529-27.04 43.632-39.118Z"
      />
      <path
        fill="#998d9c"
        d="M154.047 245.817c14.6-9.162 44.122-33.408 55.166-46.64 12.347-14.794 21.565-38.116 21.565-38.116l.747-44.387-4.276-42.022-16.953-24.931-23.436-13.463-27.923-4.488-24.433 4.986-18.45 10.472-14.46 20.443-6.482 19.447-2.08 41.599-.602 46.364 1.205 30.107 1.841 31.583q9.815 21.11 18.45 23.59c12.305 3.534 29.276-7.739 40.12-14.544Z"
      />
      <path
        fill="#959195"
        d="M162.771 228.436c12.732-9.11 31.365-21.723 42.629-32.598 14.543-14.042 26.078-41.626 26.078-41.626l-.005-37.366-4.275-42.021-16.954-24.932L186.81 36.43l-27.924-4.488-24.432 4.987-18.45 10.47-14.46 20.445-6.482 19.446-2.08 41.599-.602 46.365 1.204 30.107.565 19.243q16.667 20.473 26.997 21.887c13.486 1.845 30.557-10.133 41.626-18.055Z"
      />
      <path
        fill="#4a4a4a"
        d="m158.985 113.198-17.87-6.873-13.609 1.927-8.278 5.608-10.835-.252-4.807 12.016 4.807 18.157 13.084 3.739 5.495-5.354s7.365 1.274 8.645 1.731l22.91-4.123 5.887-14.157"
      />
      <path
        fill="#f1f1f1"
        d="M160.57 105.237c.687-9.157-8.269-7.53-19.455-8.076-2.938-.143-12.623 4.323-13.683 7.504-1.124 3.373.854 1.333 2.228 1.202 1.98-.19 4.848 1.25 12.174 1.543 6.658.267 18.735 6.871 18.735 6.871v-9.044Z"
      />
      <path
        fill="#555"
        d="m130.228 106.201-13.728 8.62s11.965-.761 14.685-.32c5.41.879 18.048 3.176 20.04 1.934 4.023-2.51-.246-8.638-.246-8.638l-20.751-1.596Z"
      />
      <path
        d="m75.853 271.057 10.785-25.376 11.42-13.957c-.292-1.106 11.323 11.045 11.323 11.045s-4.854 33.914-5.396 48.562c-.55 14.842-2.224 35.602 1.812 49.472 4.206 14.457 14.93 40.04 26.165 49.604 5.93 5.049 33.68 26.322 50.52 32.093 23.007 7.885 58.142 15.09 81.743 16.057 28.823 1.18 66.964.988 93.765-6.649 27.225-7.757 68.767-33.32 82.558-48.173 7.584-8.168-3.9 26.497-10.117 35.747-9.15 13.611-43.088 33.647-43.088 33.647l-54.559 17.763-62.17 3.807-86.853-9.219-45.167-20.279-32.262-27.653-15.887-30.396-5.076-25.376-14.59-12.688-2.538-39.333 7.612-38.698Z"
        opacity=".06"
      />
      <path
        d="M89.196 254.244s-1.215 41.085.665 58.55c2.224 20.667 11.515 49.047 20.74 67.675 7.282 14.707 22.226 28.113 35.63 37.583 13.319 9.41 36.024 17.593 51.736 21.964 22.036 6.128 45.955 13.138 68.82 13.666 24.925.576 55.694.529 80.018-4.938 17.86-4.014 44.111-13.714 59.56-23.834 12.271-8.04 32.8-36.267 32.8-36.267l-7.984 29.275-23.952 25.283-61.211 24.618-75.85 6.653-77.955-6.698-41.45-16.22-30.296-17.668-26.771-36.395-8.492-36.127s-6.244-2.21-7.806-6.319c-10.555-27.76 11.798-100.8 11.798-100.8ZM107.246 142.17c-1.536 9.374 8.83 13.246 27.873 10.084 3.072-.51 20.027.615 25.112-4.933 8.533-9.309 1.472-13.247 1.472-13.247l-15.488 9.858-18.955-.462c-3.263-.212-8.361 3.452-8.361 3.452s-10.681-4.69-11.653-4.753Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532"
            opacity=".58"
            transform="matrix(2.29396 .017 -.0079 2.23453 -376.176 -29.707)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.29205 .10072 -.09384 2.23232 -291.061 -79.131)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.27586 .2895 -.2877 2.21576 -372.39 -136.789)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.264 1.261 5.416 1.646M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c1.55 1.092 3.76 1.488 3.76 1.488"
            opacity=".58"
            transform="matrix(2.24514 -.4495 .4708 2.18891 -348.366 101.222)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M313.934 222.941c31.418 19.295 87.057 60.727 108.043 123.684"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M185.604 46.27c27.554 11.512 34.565 45.997 34.565 68.657"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M192.133 64.23c12.5 12.627 18.05 28.421 19.464 43.215"
        />
      </g>
      <path
        fill="#ccc"
        d="m442.021 367.555-1.209 25.545-11.803 27.4-13.646 13.169 9.857 7.393 26.49 12.32 24.026 3.081 2.593-11.936-4.636-25.713-16.019-31.616-15.653-19.643Z"
      />
      <path
        fill="#9d9d9d"
        d="M432.047 415.656q-8.36 9.162-28.082 26.48l30.306 20.203 34.506 7.463 11.268-14.838 5.295-12.943-2.23-24.776-18.633-24.367-20.926-23.52q-3.48 33.76-11.504 46.298Z"
      />
      <path
        fill="#ccc"
        d="M345.59 406.753s10.434-5.027 12.294-8.852c1.91-3.926.184-12.418-.984-16.72-1.953-7.194-7.046-13.252-11.802-19.178-4.926-6.138-16.228-17.704-16.228-17.704l12.786 2.951s2.983-8.737 1.967-12.294c-1.967-6.885-7.728-14.318-10.819-18.687-6.906-9.761-24.096-25.08-24.096-25.08l8.852 1.476s.75-6.486 0-9.344c-1.257-4.786-8.25-14.562-11.31-18.687-4.022-5.418-16.72-16.72-16.72-16.72l21.637 6.393 16.637 9.33 8.442 9.357 4.918 16.72.535 12.67 19.627 12.41 13.278 16.228 4.669 22.592-7.852 7.921 14.001 10.795 13.77 14.753.983 19.67-15.244 4.426-39.341-4.426Z"
      />
      <path
        fill="#9d9d9d"
        d="M354.933 408.72s10.435-4.043 12.294-7.868c1.91-3.926 2.152-10.451.984-14.753-1.954-7.194-6.063-13.744-10.819-19.67-4.926-6.138-15.237-18.688-15.237-18.688l8.352 2.951s2.588-7.613 2.46-11.31c-.223-6.375-5.761-16.777-8.852-21.146-6.906-9.76-22.621-24.588-22.621-24.588l5.409.984s.259-6.978-.492-9.836c-1.257-4.785-4.315-10.627-7.376-14.752-4.021-5.419-16.228-18.687-16.228-18.687l24.997 12.28 8.442 9.357 4.918 16.72.535 12.67 19.627 12.41 13.278 16.228 4.669 22.592-7.852 7.921 14.001 10.795 13.77 14.753.983 19.67-15.244 4.426-29.998-2.459Z"
      />
      <path
        fill="#ccc"
        d="M319.484 402.184s8.697-6.172 10.892-8.734c3.443-4.018 6.465-9.974 6.428-15.266-.047-6.698-5.519-15.84-9.168-21.457-4.833-7.438-15.74-19.922-15.74-19.922s3.504-7.282 4.419-10.446c1.49-5.152-1.497-13.344-4.017-18.078-3.66-6.875-16.07-18.079-16.07-18.079s6.638-4.036 8.035-7.23c1.965-4.496-3.505-14.705-7.137-19.801-4.31-6.047-18.838-14.374-18.838-14.374l-20.681.706s10.238 9.595 13.805 13.272c3.16 3.258 9.164 8.254 8.815 12.78-.407 5.264-10.514 10.63-10.514 10.63s11.931 13.678 16.266 19.252c2.548 3.276 5.248 9.547 5.026 13.69-.266 4.987-7.668 13.073-7.668 13.073s10.918 15.343 15.222 22.115c3.382 5.32 8.081 10.953 9.365 16.498.833 3.6.631 9.13-.756 12.292-1.554 3.543-14.952 12.265-14.952 12.265l27.268 6.814Z"
      />
      <path
        fill="#9d9d9d"
        d="M312.81 400.565s17.012-10.983 18.636-17.587c1.73-7.04-2.275-16.292-6.682-22.048-7.418-9.689-19.613-23.475-19.613-23.475s6.02-10.401 6.861-14.604c.85-4.24-2.696-9.889-5.104-13.48-4.57-6.817-18.741-19.658-18.741-19.658s10.994-3.865 12.176-8.683c1.344-5.478-4.61-12.661-8.443-16.799-4.752-5.128-20.57-15.368-20.57-15.368l-9.531.734s12.662 9.095 16.737 13.446c3.336 3.562 8.947 10.029 8.118 14.838-.935 5.422-9.88 9.795-9.88 9.795s12.78 13.269 17.913 21.323c1.813 2.845 3.358 7.844 2.543 11.118-.995 3.995-7.461 12.31-7.461 12.31s10.14 13.95 14.224 20.954c4.249 7.287 12.407 17.133 11.754 25.543-.434 5.594-14.917 16.965-14.917 16.965"
      />
      <path
        d="m404.446 441.173 32.164-31.06 8.756-41.602 29.138 40.516 10.542 17.99-.294 11.18-7.188 22.698s-2.392-18.306-13.508-31.49c-3.888-4.61-17.397-12.615-17.397-12.615s-16.843 27.23-32.11 33.523"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="m441.387 365.966-.617 18.497s18.016 17.749 27.554 28.65c12.6 14.4 15.217 27.072 15.217 27.072l-.829-12.852-3.317-12.853-17.307-25.902-20.701-22.612Z"
      />
      <g transform="translate(29.712 -1)">
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m62.194 457.949 40.14-13.22"
        />
        <path
          fill="none"
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M411.792 368.02s23.59 26.23 35.364 41.382c11.162 14.366 9.438 28.888 8.515 37.263 0 0-8.093 22.244-16.453 23.074-10.504 1.72-24.27-3.762-36.105-8.815-8.602-3.673-27.361-15.38-27.361-15.38"
        />
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M66.328 233.275s-11.746 16.64-13.203 19.779c-20.43 44-15.586 78.148-13.288 91.168 1.571 8.905 9.003 13.622 15.38 18.3"
        />
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="4"
          d="M129.274 113.338c5.244-.611 3.862-6.397 1.912-9.077s-11.59-6.572-16.567-6.82c-4.799-.418-8.606.906-10.605 1.514-2.903.884-7.286 3.931-7.953 5.934-.683 2.047-.963 3.135.69 3.812"
        />
        <path
          fill="none"
          stroke="#9e9e9e"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M199.94 178.407s17.934 3.587 28.347 6.19c13.716 3.43 39.621 12.655 53.767 20.906 17.216 10.043 35.438 21.452 51.31 36.746 16.387 15.79 35.842 34.968 48.6 54.668 12.287 18.973 23.94 32.568 29.258 69.12"
        />
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M410.38 384.2s1.981 17.64-10.442 34.678c-10.329 14.164-19.852 22.272-34.3 29.682-22.72 11.653-46.83 19.788-78.49 24.405-21.688 3.163-64.923 1.637-86.676-1.035-16.304-2.003-38.283-4.943-53.736-10.515-17.725-6.391-38.816-14.334-52.41-25.286-10.943-8.815-23.514-24.274-28.868-36.885-6.517-15.354-11.3-42.73-12.83-59.338-1.652-17.935-1.179-42.148 2.404-60.469 2.879-14.724 12.454-48.433 12.454-48.433s-4.873-33.646-5.535-49.124c-.827-19.334-.45-24.384.389-43.717.79-18.218.48-42.959 5.265-60.554 2.411-8.863 8.532-21.444 15.017-27.949 7.732-7.756 20.301-13.43 31.057-15.492 11.274-2.16 27.346-2.784 38.397.324 10.712 3.013 21.663 9.7 29.537 17.563 7.25 7.24 13.227 18.867 15.967 28.74 3.491 12.58 2.87 33.294 3.991 46.302 2.219 25.723-1.76 58.593-1.76 58.593"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m54.796 391.678 9.722 64.622M47.505 469.452l18.299-14.511M65.202 455.543l16.329-52.928"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m81.688 490.847 55.322-14.36"
          transform="matrix(.90062 0 0 .93083 60.216 17.416)"
        />
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M91.102 113.455s-4.649-1.07-8.723.426c-4.803 1.763-7.237 7.135-7.504 12.476-.19 3.794 1.168 11.057 2.937 14.419 2.67 5.073 7.286 8.717 12.55 8.278 5.431-.454 8.612-4.536 8.612-4.536"
        />
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M99.308 144.307s9.926 1.658 18.042-.06c8.808-1.862 25.995-9.191 12.688-29.267-3.726-5.62-12.434-7.661-18.442-7.497-6.321.174-10.206.542-15.262 3.516-2.881 1.694-5.658 2.456-5.658 2.456"
        />
        <path
          fill="none"
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M308.999 300.387c4.608-6.162 3.103-14.75 1.182-19.536-2.336-5.82-7.042-11.9-13.146-16.597-6.947-5.346-15.731-8.605-22.583-11.012-7.712-2.71-19.478-4.595-27.827-5.256-6.188-.49-20.122.496-20.122.496"
        />
        <path
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="10"
          d="m116.939 410.424 14.416 60.243M134.986 474.192l-19.852 13.235M149.424 409.822l-14.438 63.768"
        />
        <path
          fill="none"
          stroke="#878787"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M343.61 360.068c5.733-2.052 5.391-6.09 5.85-9.36.776-5.536-1.506-13.208-5.755-20.302-5.686-9.493-15.834-18.268-22.23-21.82-9.552-5.303-11.679-11.686-44.954-16.75M163.637 295.928s-11.659 37.488 36.225 66.974c16.897 11.635 37.548 22.913 55.167 29.291 15.536 5.624 37.22 12.522 53.798 14.884 18.02 2.566 48.666 9.076 60.666.793 5.054-3.488 3.152-17.865-7.638-28.274-9.405-9.073-19.713-17.663-29.584-23.072-9.667-5.297-34.637-12.259-34.637-12.259"
        />
        <g
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
        >
          <path d="M137.89 90.453s7.834-.624 11.328 2.22M137.675 90.453s6.224-4.937 11.804-3.65" />
        </g>
        <g
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
        >
          <path d="M101.695 90.783s-7.836-.624-11.33 2.22M101.91 90.783s-6.225-4.937-11.805-3.649" />
        </g>
        <path
          fill="none"
          stroke="gray"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M161.447 65.785q13.367 19.443 4.86 34.026M78.812 70.795q-9.349 14.903-.842 29.486"
        />
        <ellipse
          fill="#333"
          rx="3.396"
          ry="5.611"
          transform="matrix(1.40869 -.30272 .3014 1.40256 88.054 130.83)"
        />
      </g>
      <g transform="translate(28 26)">
        <PigeonBlinkFastAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36055 -.11903 .1137 1.29952 144.406 59.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 143.406 57.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9962 -.08716 .07107 .81237 144.587 55.607)"
        />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.35246 .19008 -.17559 1.24938 97.406 58.941)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12746 .90689 96.406 56.941)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12799 .91066 96.406 56.941)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99027 .13917 -.1135 .80754 97.587 54.607)"
        />
      </g>
      <g>
        <PigeonBreathAnimation />
        <path
          fill="#f7f7f7"
          d="m67.591 130.12-8.824 1.611q-1.025-10.071-12.18-6.766c-5.512 1.633-11.367 12.18 1.403 12.505-13.04-3.574-9.251 14.56-.204 8.983-5.8 5.848 3.267 10.388 8.576 8.78q7.427-2.25 4.518-10.858l9.043-1.291"
          opacity=".5"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

function PigeonFail({ isFadingOut, isFadingIn }: PigeonAnimationProps) {
  return (
    <PigeonAnimationContainer isFadingOut={isFadingOut} isFadingIn={isFadingIn}>
      <ellipse
        opacity=".1"
        rx="166.187"
        ry="25.726"
        transform="matrix(1.1791 0 0 .91774 282.365 476.088)"
      />
      <path
        fill="#d5d5d5"
        d="m239.536 181.704-9.465-27.767-134.083 52.995 9.531 25.416-14.691 23.408-12.614 45.728 2.365 38.631 8.672 15.768 6.693 5.673 11.986 43.17 29.705 34.761 53.179 24.304 76.017 11.877 72.058-1.583 50.974-17.934 23.63-11.553 36.99 18.946 28.87 5.864 11.54-10.285 7.858-23.999-9.022-24.359-24.812-27.645-11.653-18.865-8.178-29.085-16.983-31.354-29.797-37.022-35.98-35.297-38.62-25.994-35.847-16.24-33.26-8.5-15.063.941Z"
      />
      <path
        fill="#ddd"
        d="m411.237 404.45-1.353-13.534 16.15-4.829 14.103-10.392 12.865-21.359-25.386-50.726-29.045-36.283-39.34-40.824-39.819-23.75q-54.862-26.021-78.94-23.012c-19.982 2.498-54.34 17.498-69.03 30.433-13.512 11.897-35.184 34.298-40.558 51.48-5.587 17.864-8.565 39.857-4.481 61.153 4.637 24.182 9.286 38.168 30.498 50.479 20.822 12.084 43.145 15.853 63.624 12.62 9.99-1.578 31.022-16.642 31.022-16.642l46.463 23.006 49.17 13.533 44.66 5.864 19.397-7.217Z"
      />
      <path
        fill="#b7b0ba"
        d="M215.198 226.258q9.355-11.412 26.079-44.133l-8.39-15.419-54.956 19.516-71.702 44.822-8.715 37.837s15.468 23.414 25.998 23.591c10.245.173 34.083-14.649 49.058-25.592 13.039-9.528 31.025-26.466 42.628-40.622Z"
      />
      <path
        fill="#a195a6"
        d="M216.703 213.72c10.94-13.08 19.581-38.86 19.581-38.86l-10.12-31.18-121.915 85.491-1.952 21.232q13.753 25.505 23.13 25.505c12.03 0 32.599-11.535 47.644-23.07 12.396-9.503 33.529-27.04 43.632-39.118Z"
      />
      <path
        fill="#998d9c"
        d="M165.047 245.817c14.6-9.162 44.122-33.408 55.166-46.64 12.347-14.794 14.713-27.715 14.713-27.715l-16.94-38.237-113.351 71.963-1.022 30.338q12.678 22.355 21.313 24.835c12.305 3.534 29.276-7.739 40.12-14.544Z"
      />
      <path
        fill="#959195"
        d="M173.771 228.436c12.732-9.11 31.365-21.723 42.629-32.598 14.543-14.042 17.578-30.178 17.578-30.178l-17.396-40.398-23.78-38.046-16.306-21.062-27.176-14.268-31.932-3.397-27.177 8.153-21.74 16.985-12.514 24.015.963 30.338 10.99 27.911 27.443 49.135 7.625 22.24q18.837 17.811 29.167 19.225c13.486 1.845 30.557-10.133 41.626-18.055Z"
      />
      <path
        fill="#4a4a4a"
        d="m119.922 174.713 4.876-15.488-11.532-3.357-15.252 4.518-4.766 6.583.549 13.832 6.35 9.28 10.31 6.143 4.343-5.654 5.122-15.857Z"
      />
      <path
        fill="#f1f1f1"
        d="M123.103 151.495c-4.6-4.87-6.545-5.895-19.532-2.92-2.867.658-12.859 7.154-13.92 10.335-1.123 3.372 1.34 8.386 2.715 8.255 1.979-.19 6.964-9.425 14.121-11.015 7.305-1.623 13.686 1.206 13.686 1.206q2.93-4.956 2.93-5.86Z"
      />
      <path
        fill="#555"
        d="M105.496 164.637s-4.05 9.263 2.443 21.98c2.976-11.905 11.427-21.494 7.815-24.423-3.027-2.454-10.258 2.443-10.258 2.443Z"
      />
      <path
        d="m86.853 271.057 10.785-25.376 11.42-13.957c-.292-1.106 11.323 11.045 11.323 11.045s-4.854 33.914-5.396 48.562c-.55 14.842-2.224 35.602 1.812 49.472 4.206 14.457 14.93 40.04 26.165 49.604 5.93 5.049 33.68 26.322 50.52 32.093 23.007 7.885 58.142 15.09 81.743 16.057 28.823 1.18 66.964.988 93.765-6.649 27.225-7.757 68.767-33.32 82.558-48.173 7.584-8.168-3.9 26.497-10.117 35.747-9.15 13.611-43.088 33.647-43.088 33.647l-54.559 17.763-62.17 3.807-86.853-9.219-45.167-20.279-32.262-27.653-15.887-30.396-5.076-25.376-14.59-12.688-2.538-39.333 7.612-38.698Z"
        opacity=".06"
      />
      <path
        d="M100.196 254.244s-1.215 41.085.665 58.55c2.224 20.667 11.515 49.047 20.74 67.675 7.282 14.707 22.226 28.113 35.63 37.583 13.319 9.41 36.024 17.593 51.736 21.964 22.036 6.128 45.955 13.138 68.82 13.666 24.925.576 55.694.529 80.018-4.938 17.86-4.014 44.111-13.714 59.56-23.834 12.271-8.04 32.8-36.267 32.8-36.267l-7.984 29.275-23.952 25.283-61.211 24.618-75.85 6.653-77.955-6.698-41.45-16.22-30.296-17.668-26.771-36.395-8.492-36.127s-6.244-2.21-7.806-6.319c-10.555-27.76 11.798-100.8 11.798-100.8ZM91.404 168.266c-6.323 10.275 5.063 39.122 24.105 35.96 2.048-.34 6.397-11.28 5.137-22.524-.472-4.215-7.162 15.17-7.162 15.17s-10.346-2.621-16.029-10.123c-5.111-6.747-5.59-18.453-6.051-18.483Z"
        opacity=".06"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532"
            opacity=".58"
            transform="matrix(2.29396 .017 -.0079 2.23453 -365.176 -29.707)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.29205 .10072 -.09384 2.23232 -280.061 -79.131)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.743 1.916 5.895 2.301M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c5.422 3.91 8.952 3.533 8.952 3.533"
            opacity=".58"
            transform="matrix(2.27586 .2895 -.2877 2.21576 -361.39 -136.789)"
          />
        </g>
        <g stroke="#b3b3b3" stroke-width="2">
          <path
            d="M267.445 213.283s2.5 1.154 3.94 4.148M263.82 217.536s3.264 1.261 5.416 1.646M267.213 207.55c3.95 2.763 5.088 6.532 5.088 6.532M257.778 218.774c1.55 1.092 3.76 1.488 3.76 1.488"
            opacity=".58"
            transform="matrix(2.24514 -.4495 .4708 2.18891 -337.366 101.222)"
          />
        </g>
        <path
          stroke="#e1e1e1"
          stroke-width="10"
          d="M324.934 222.941c31.418 19.295 87.057 60.727 108.043 123.684"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M142.788 64.309c29.845 1.033 48.588 30.816 56.595 52.014"
        />
        <path
          stroke="#9f9ba3"
          stroke-width="10"
          d="M158.673 76.297c15.819 8.09 26.026 21.36 31.99 34.972"
        />
      </g>
      <path
        fill="#ccc"
        d="m453.021 367.555-1.209 25.545-11.803 27.4-13.646 13.169 9.857 7.393 26.49 12.32 24.026 3.081 2.593-11.936-4.636-25.713-16.019-31.616-15.653-19.643Z"
      />
      <path
        fill="#9d9d9d"
        d="M443.047 415.656q-8.36 9.162-28.082 26.48l30.306 20.203 34.506 7.463 11.268-14.838 5.295-12.943-2.23-24.776-18.633-24.367-20.926-23.52q-3.48 33.76-11.504 46.298Z"
      />
      <path
        fill="#ccc"
        d="M356.59 406.753s10.434-5.027 12.294-8.852c1.91-3.926.184-12.418-.984-16.72-1.953-7.194-7.046-13.252-11.802-19.178-4.926-6.138-16.228-17.704-16.228-17.704l12.786 2.951s2.983-8.737 1.967-12.294c-1.967-6.885-7.728-14.318-10.819-18.687-6.906-9.761-24.096-25.08-24.096-25.08l8.852 1.476s.75-6.486 0-9.344c-1.257-4.786-8.25-14.562-11.31-18.687-4.022-5.418-16.72-16.72-16.72-16.72l21.637 6.393 16.637 9.33 8.442 9.357 4.918 16.72.535 12.67 19.627 12.41 13.278 16.228 4.669 22.592-7.852 7.921 14.001 10.795 13.77 14.753.983 19.67-15.244 4.426-39.341-4.426Z"
      />
      <path
        fill="#9d9d9d"
        d="M365.933 408.72s10.435-4.043 12.294-7.868c1.91-3.926 2.152-10.451.984-14.753-1.954-7.194-6.063-13.744-10.819-19.67-4.926-6.138-15.237-18.688-15.237-18.688l8.352 2.951s2.588-7.613 2.46-11.31c-.223-6.375-5.761-16.777-8.852-21.146-6.906-9.76-22.621-24.588-22.621-24.588l5.409.984s.259-6.978-.492-9.836c-1.257-4.785-4.315-10.627-7.376-14.752-4.021-5.419-16.228-18.687-16.228-18.687l24.997 12.28 8.442 9.357 4.918 16.72.535 12.67 19.627 12.41 13.278 16.228 4.669 22.592-7.852 7.921 14.001 10.795 13.77 14.753.983 19.67-15.244 4.426-29.998-2.459Z"
      />
      <path
        fill="#ccc"
        d="M330.484 402.184s8.697-6.172 10.892-8.734c3.443-4.018 6.465-9.974 6.428-15.266-.047-6.698-5.519-15.84-9.168-21.457-4.833-7.438-15.74-19.922-15.74-19.922s3.504-7.282 4.419-10.446c1.49-5.152-1.497-13.344-4.017-18.078-3.66-6.875-16.07-18.079-16.07-18.079s6.638-4.036 8.035-7.23c1.965-4.496-3.505-14.705-7.137-19.801-4.31-6.047-18.838-14.374-18.838-14.374l-20.681.706s10.238 9.595 13.805 13.272c3.16 3.258 9.164 8.254 8.815 12.78-.407 5.264-10.514 10.63-10.514 10.63s11.931 13.678 16.266 19.252c2.548 3.276 5.248 9.547 5.026 13.69-.266 4.987-7.668 13.073-7.668 13.073s10.918 15.343 15.222 22.115c3.382 5.32 8.081 10.953 9.365 16.498.833 3.6.631 9.13-.756 12.292-1.554 3.543-14.952 12.265-14.952 12.265l27.268 6.814Z"
      />
      <path
        fill="#9d9d9d"
        d="M323.81 400.565s17.012-10.983 18.636-17.587c1.73-7.04-2.275-16.292-6.682-22.048-7.418-9.689-19.613-23.475-19.613-23.475s6.02-10.401 6.861-14.604c.85-4.24-2.696-9.889-5.104-13.48-4.57-6.817-18.741-19.658-18.741-19.658s10.994-3.865 12.176-8.683c1.344-5.478-4.61-12.661-8.443-16.799-4.752-5.128-20.57-15.368-20.57-15.368l-9.531.734s12.662 9.095 16.737 13.446c3.336 3.562 8.947 10.029 8.118 14.838-.935 5.422-9.88 9.795-9.88 9.795s12.78 13.269 17.913 21.323c1.813 2.845 3.358 7.844 2.543 11.118-.995 3.995-7.461 12.31-7.461 12.31s10.14 13.95 14.224 20.954c4.249 7.287 12.407 17.133 11.754 25.543-.434 5.594-14.917 16.965-14.917 16.965"
      />
      <path
        d="m415.446 441.173 32.164-31.06 8.756-41.602 29.138 40.516 10.542 17.99-.294 11.18-7.188 22.698s-2.392-18.306-13.508-31.49c-3.888-4.61-17.397-12.615-17.397-12.615s-16.843 27.23-32.11 33.523"
        opacity=".04"
      />
      <path
        fill="#b6b6b6"
        d="m452.387 365.966-.617 18.497s18.016 17.749 27.554 28.65c12.6 14.4 15.217 27.072 15.217 27.072l-.829-12.852-3.317-12.853-17.307-25.902-20.701-22.612Z"
      />
      <g fill="none" stroke-linecap="round" stroke-linejoin="round">
        <path stroke="#555" stroke-width="10" d="m102.906 456.949 39.062-12.25" />
        <path
          stroke="#9e9e9e"
          stroke-width="5"
          d="M452.504 367.02s23.59 26.23 35.364 41.382c11.162 14.366 9.438 28.888 8.514 37.263 0 0-8.093 22.244-16.453 23.074-10.503 1.72-24.269-3.762-36.104-8.815-8.602-3.673-27.362-15.38-27.362-15.38"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M103.775 234.428s-8.481 14.487-9.939 17.626c-20.429 44-15.585 78.148-13.287 91.168 1.571 8.905 9.002 13.622 15.38 18.3"
        />
        <path
          stroke="gray"
          stroke-width="4"
          d="M119.51 156.207c4.282-3.089 2.601-7.584-.407-8.975s-12.751-.441-17.219 1.766c-4.394 1.973-8.885 5.665-10.334 7.17-2.105 2.186-2.29 3.832-1.898 5.906.401 2.12 2.655 2.956 4.429 2.741"
        />
        <path
          stroke="#9e9e9e"
          stroke-width="5"
          d="M236.964 176.218s21.622 4.776 32.034 7.38c13.717 3.43 39.622 12.654 53.767 20.905 17.216 10.043 35.439 21.452 51.31 36.746 16.388 15.79 35.843 34.968 48.6 54.668 12.288 18.973 23.941 32.568 29.258 69.12"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M451.093 384.466s1.98 17.64-10.444 34.677c-10.328 14.165-19.85 22.272-34.298 29.682-22.721 11.653-46.832 19.788-78.492 24.405-21.687 3.163-64.922 1.637-86.675-1.035-16.304-2.003-38.283-4.943-53.736-10.515-17.725-6.391-38.816-14.334-52.41-25.285-10.943-8.816-23.515-24.275-28.868-36.886-6.517-15.354-11.3-42.729-12.83-59.338-1.652-17.934-1.18-42.148 2.403-60.469 2.88-14.724 9.614-34.016 8.144-48.779-3.223-8.897-5.872-19.717-9.177-25.544-5.06-8.921-10.653-20.686-15.948-29.904-6.527-11.36-16.694-29.332-19.803-42.06-3.265-13.36-4.003-28.773-.68-39.405 4.212-13.477 15.972-27.075 27.177-33.97 15.335-9.44 34.896-12.135 50.276-10.871 10.487.861 22.09 5.812 32.611 12.908 12.095 8.158 19.811 18.563 27.177 28.536 9.477 12.83 21.062 34.65 26.497 47.558 5.551 13.185 18.505 47.784 18.505 47.784"
        />
        <path
          stroke="#555"
          stroke-width="10"
          d="m95.508 390.678 9.721 64.622M88.216 468.452l18.3-14.511M105.914 454.543l16.329-52.928"
        />
        <path
          stroke="#555"
          stroke-width="10"
          d="m81.688 490.847 55.322-14.36"
          transform="matrix(.90062 0 0 .93083 100.927 16.416)"
        />
        <path
          stroke="gray"
          stroke-width="5"
          d="M112.7 155.303c-4.842-.125-13.76 4.559-16.38 7.343-4.11 4.367-4.252 4.544-4.52 9.885-.189 3.794 2.75 10.477 4.52 13.839 2.67 5.073 15.731 14.67 17.228 9.603 3.48-11.777 6.077-20.897 12.398-33.88 2.186-4.491-8.919-6.68-13.245-6.79Z"
        />
        <path
          stroke="#878787"
          stroke-width="5"
          d="M349.71 299.387c4.609-6.162 3.103-14.75 1.182-19.536-2.336-5.82-7.041-11.9-13.145-16.597-6.947-5.346-15.732-8.605-22.584-11.012-7.711-2.71-19.477-4.595-27.826-5.256-6.188-.49-20.123.496-20.123.496"
        />
        <path
          stroke="#555"
          stroke-width="10"
          d="m157.65 409.424 14.416 60.243M175.698 473.192l-19.852 13.235M190.136 408.822l-14.438 63.768"
        />
        <path
          stroke="#878787"
          stroke-width="5"
          d="M384.322 359.068c5.733-2.052 5.39-6.09 5.85-9.36.776-5.536-1.507-13.208-5.756-20.302-5.685-9.493-15.833-18.268-22.23-21.82-9.551-5.303-11.678-11.686-44.953-16.75M204.348 294.928s-11.658 37.488 36.226 66.974c16.897 11.635 37.547 22.913 55.167 29.291 15.536 5.624 37.22 12.522 53.798 14.884 18.02 2.566 48.666 9.076 60.666.793 5.053-3.488 3.152-17.865-7.638-28.274-9.406-9.073-19.714-17.663-29.584-23.072-9.667-5.297-34.638-12.259-34.638-12.259"
        />
      </g>
      <path
        fill="none"
        stroke="#555"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M86.733 136.74s4.774 15.804-8.514 10.305M118.614 124.236s10.912 11.731 13.374-2.283"
      />
      <path
        fill="none"
        stroke="gray"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="5"
        d="M104.417 101.288q18.805 14.39 27.15 2.31M79.001 112.841q-2.273 22.793-13.458 21.899"
      />
      <g transform="rotate(-25.365 280.357 131.084)">
        <PigeonCloseEyesAnimation />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.36055 -.11903 .1137 1.29952 143.978 60.845)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 142.978 58.845)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.89653 -.07844 .08716 .9962 142.978 58.845)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.9962 -.08716 .07107 .81237 144.159 56.51)"
        />
        <ellipse
          fill="#7e7e7e"
          rx="7.136"
          ry="10.081"
          transform="matrix(1.35246 .19008 -.17559 1.24938 96.978 59.845)"
        />
        <ellipse
          fill="#9a5f46"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12746 .90689 95.978 57.845)"
        />
        <ellipse
          fill="none"
          stroke="#555"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          rx="7.136"
          ry="10.081"
          transform="matrix(.8912 .12525 -.12799 .91066 95.978 57.845)"
        />
        <ellipse
          fill="#d6b788"
          rx="2.53"
          ry="4.671"
          transform="matrix(.99027 .13917 -.1135 .80754 97.159 55.51)"
        />
      </g>
      <g>
        <PigeonSweatAnimation />
        <path
          fill="none"
          stroke="#b4ccdc"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M161.481 88.366c-3.856 8.132-5.678 22.42 2.51 21.81 5.82-.432 7.848-5.544 2.895-16.212"
        />
        <path
          fill="none"
          stroke="#fff"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
          d="M162.148 98.137c-.831 3.287-.95 7.42.45 8.106 1.266.62 3.002.15 3.603-1.2"
        />
      </g>
    </PigeonAnimationContainer>
  );
}

const PigeonAnimations = {
  start: PigeonStart,
  aWatch: PigeonAWatch,
  aGuess: PigeonAGuess,
  bFail: PigeonBFail,
  bWatch: PigeonBWatch,
  bGuess: PigeonBGuess,
  cFail: PigeonCFail,
  cWatch: PigeonCWatch,
  cGuess: PigeonCGuess,
  dFail: PigeonDFail,
  dWatch: PigeonDWatch,
  dGuess: PigeonDGuess,
  successA: PigeonSuccessA,
  successB: PigeonSuccessB,
  fail: PigeonFail,
};

type PigeonAnimationKey = keyof typeof PigeonAnimations;

function getAnimationKey(
  step: Step,
  previousGuesses: Array<Guess>
): { key: PigeonAnimationKey; shouldDelay?: true } {
  const isLatestGuessSimilar = previousGuesses.length
    ? isSimilar(previousGuesses[previousGuesses.length - 1].score)
    : false;

  switch (step) {
    case Step.Start:
      return { key: "start" };
    case Step.RoundOne:
      return { key: "aWatch" };
    case Step.RoundOneGuessing:
      return { key: "aGuess" };
    case Step.RoundOneFail:
      return { key: isLatestGuessSimilar ? "bFail" : "cFail", shouldDelay: true };
    case Step.RoundTwo:
      return { key: isLatestGuessSimilar ? "bWatch" : "cWatch" };
    case Step.RoundTwoGuessing:
      return { key: isLatestGuessSimilar ? "bGuess" : "cGuess" };
    case Step.RoundTwoFail:
      return { key: isLatestGuessSimilar ? "cFail" : "dFail", shouldDelay: true };
    case Step.RoundThree:
      return { key: isLatestGuessSimilar ? "cWatch" : "dWatch" };
    case Step.RoundThreeGuessing:
      return { key: isLatestGuessSimilar ? "cGuess" : "dGuess" };
    case Step.RoundThreeFail:
      return { key: "fail", shouldDelay: true };
    case Step.Success: {
      const shouldShowHappyAnimation =
        previousGuesses.length === 1 ||
        (previousGuesses.length === 2 &&
          isSimilar(previousGuesses[previousGuesses.length - 2].score));
      return { key: shouldShowHappyAnimation ? "successA" : "successB", shouldDelay: true };
    }
  }
}

type PigeonProps = {
  step: Step;
  previousGuesses: Array<Guess>;
};

export function Pigeon({ step, previousGuesses }: PigeonProps): ReactNode {
  const [animationKey, setAnimationKey] = useState<PigeonAnimationKey>(
    getAnimationKey(step, previousGuesses).key
  );
  const [nextAnimationKey, setNextAnimationKey] = useState<PigeonAnimationKey | null>(null);
  const [isAnimationFadingOut, setIsAnimationFadingOut] = useState(false);
  const startNextAnimationTimeout = useRef<NodeJS.Timeout | null>(null);
  const updateAnimationTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const { key: nextKey, shouldDelay } = getAnimationKey(step, previousGuesses);

    if (nextKey === animationKey) {
      return;
    }

    if (startNextAnimationTimeout.current) {
      clearTimeout(startNextAnimationTimeout.current);
    }
    startNextAnimationTimeout.current = setTimeout(
      () => {
        setNextAnimationKey(nextKey);
        setIsAnimationFadingOut(true);

        if (updateAnimationTimeout.current) {
          clearTimeout(updateAnimationTimeout.current);
        }
        updateAnimationTimeout.current = setTimeout(() => {
          setAnimationKey(nextKey);
          setNextAnimationKey(null);
          setIsAnimationFadingOut(false);
        }, 200);
      },
      shouldDelay ? 950 : 0
    );
  }, [step]);

  const Animation = PigeonAnimations[animationKey];
  const NextAnimation = nextAnimationKey ? PigeonAnimations[nextAnimationKey] : null;

  return (
    <div className={styles.pigeonContainer}>
      <Animation isFadingOut={isAnimationFadingOut} />
      {NextAnimation !== null && <NextAnimation isFadingIn={true} />}
    </div>
  );
}
